export type Locale = 'en' | 'zh-CN'

export const DEFAULT_LOCALE: Locale = 'zh-CN'

export const SUPPORTED_LOCALES: Locale[] = ['en', 'zh-CN']

export type MessageDictionary = Record<string, string>

export const MESSAGES: Record<Locale, MessageDictionary> = {
  en: {
    'app.crash.title': 'Something went wrong',
    'app.crash.description': 'Please restart the app. The error has been reported.',
    'common.reload': 'Reload',
    'common.cancel': 'Cancel',
    'common.copy': 'Copy',
    'common.copied': 'Copied!',
    'common.delete': 'Delete',
    'common.clear': 'Clear',
    'common.create': 'Create',
    'common.open': 'Open',
    'common.back': 'Back',
    'common.browse': 'Browse',
    'common.workspace': 'Workspace',
    'common.workbench': 'Workbench',
    'common.enable': 'Enable',
    'common.disable': 'Disable',
    'common.edit': 'Edit',
    'common.continue': 'Continue',
    'common.save': 'Save',
    'common.saveAndSend': 'Save & Send',
    'common.reset': 'Reset',
    'common.change': 'Change',
    'common.loading': 'Loading...',
    'common.saving': 'Saving...',
    'common.uploading': 'Uploading...',
    'common.select': 'Select',
    'common.preview': 'Preview',
    'common.file': 'File',
    'common.dataTable': 'Data Table',
    'common.spreadsheet': 'Spreadsheet',
    'common.image': 'Image',
    'common.pdf': 'PDF',
    'common.command': 'Command',
    'common.output': 'Output',
    'common.row': 'row',
    'common.rows': 'rows',
    'common.column': 'column',
    'common.columns': 'columns',
    'common.noRows': 'No rows',
    'common.tableControls': 'Table Controls',
    'common.label': 'label',
    'common.original': 'Original',
    'common.modified': 'Modified',
    'common.copyPath': 'Copy path',
    'common.copyAll': 'Copy all',
    'common.copyCode': 'Copy code',
    'common.loadFailed': 'Load Failed',
    'common.rendering': 'Rendering...',
    'common.openFile': 'Open File',
    'common.revealIn': 'Reveal in {name}',
    'common.viewFullscreen': 'View Fullscreen',
    'common.html': 'HTML',
    'common.loadingData': 'Loading data...',
    'common.sortBy': 'Sort by',
    'common.groupBy': 'Group by',
    'common.clearAll': 'Clear all',
    'common.closeEsc': 'Close (Esc)',
    'common.contextBadge': 'Context badge',
    'common.copyJson': 'Copy JSON',
    'common.exportTable': 'Export table',
    'datatable.yes': 'Yes',
    'datatable.no': 'No',
    'datatable.granularity.hour': 'Hour',
    'datatable.granularity.day': 'Day',
    'datatable.granularity.month': 'Month',
    'datatable.granularity.year': 'Year',
    'common.plainText': 'Plain text',
    'common.searchLanguages': 'Search languages...',
    'common.noLanguagesFound': 'No languages found',
    'common.codeLanguage': 'Code language',
    'common.selectCodeLanguage': 'Select code language',
    'common.default': 'Default',
    'common.duplicate': 'Duplicate',
    'common.include': 'Include',
    'common.exclude': 'Exclude',
    'common.info': 'Info',
    'common.markdown': 'Markdown',
    'common.metadata': 'Metadata',
    'common.slug': 'Slug',
    'common.name': 'Name',
    'common.description': 'Description',
    'common.location': 'Location',
    'common.type': 'Type',
    'common.url': 'URL',
    'common.status': 'Status',
    'common.sources': 'Sources',
    'common.labels': 'Labels',
    'common.documentation': 'Documentation',
    'common.permissions': 'Permissions',
    'common.connection': 'Connection',
    'common.tools': 'Tools',
    'common.followUp': 'Follow up',
    'common.selectedText': 'Selected text',
    'common.branch': 'Branch',
    'common.instructions': 'Instructions',
    'common.settings': 'Settings',
    'common.recentActivity': 'Recent Activity',
    'common.event': 'Event',
    'common.timing': 'Timing',
    'common.timezone': 'Timezone',
    'common.method': 'Method',
    'common.duration': 'Duration',
    'common.error': 'Error',
    'common.response': 'Response',
    'common.never': 'Never',
    'common.active': 'Active',
    'common.disabled': 'Disabled',
    'common.beta': 'Beta',
    'browser.windowOptions': 'Browser window options',
    'browser.hideWindow': 'Hide Window',
    'browser.closeWindowEntirely': 'Close Window Entirely',
    'browser.newTab': 'New Tab',
    'browser.localFile': 'Local File',
    'browser.back': 'Back',
    'browser.forward': 'Forward',
    'browser.reload': 'Reload',
    'browser.stopLoading': 'Stop loading',
    'browser.addressPlaceholder': 'Enter URL or search…',
    'browser.tabActionsAria': '{label} actions',
    'browser.usingHost': 'Using {hostname}',
    'browser.emptyState.title': 'This browser is ready for your Agents - and you ;)',
    'browser.emptyState.description': 'Ask any session to use this browser (or open another one) to complete tasks like research, form filling, QA checks, or data extraction.',
    'browser.emptyState.safetyHint': 'XClaw only controls browser windows when you ask it to.',
    'browser.showWindow': 'Show Browser Window',
    'browser.openSessionUsingWindow': 'Open Session Using this Window',
    'browser.openSessionWhichUsedWindow': 'Open Session Which Used this Window',
    'browser.terminate': 'Terminate Browser',
    'turnCard.responding': 'Responding...',
    'turnCard.stepsCompleted': 'Steps Completed',
    'turnCard.starting': 'Starting...',

    'onboarding.common.continue': 'Continue',
    'onboarding.common.required': 'required',
    'onboarding.common.optional': 'optional',
    'onboarding.welcome.updateTitle': 'Update Settings',
    'onboarding.welcome.title': 'Welcome to XClaw',
    'onboarding.welcome.updateDescription': 'Update your API connection or change your setup.',
    'onboarding.welcome.description': 'Agents with the UX they deserve. Connect anything. Organize your sessions. Everything you need to do the work of your life!',
    'onboarding.welcome.checking': 'Checking...',
    'onboarding.welcome.getStarted': 'Get Started',
    'onboarding.provider.title': 'Welcome to XClaw',
    'onboarding.provider.description': 'How would you like to connect?',
    'onboarding.provider.claudeDescription': 'Use your Anthropic subscription.',
    'onboarding.provider.chatgptDescription': 'Use your ChatGPT Plus or Pro subscription.',
    'onboarding.provider.copilotDescription': 'Use your GitHub Copilot subscription.',
    'onboarding.provider.otherProvider': 'I use other provider',
    'onboarding.provider.otherProviderDescription': 'Anthropic, AWS Bedrock, OpenRouter, Google or any compatible provider.',
    'onboarding.provider.localModel': 'Local model',
    'onboarding.provider.localModelDescription': 'Run models locally with Ollama.',
    'onboarding.provider.setupLater': 'Setup later',
    'onboarding.apiSetup.segment.anthropicDescriptionLine1': 'Use Claude Agent SDK as the main agent.',
    'onboarding.apiSetup.segment.anthropicDescriptionLine2': 'Configure with your Claude subscription or API key.',
    'onboarding.apiSetup.segment.piDescriptionLine1': 'Use XClaw Backend as the main agent.',
    'onboarding.apiSetup.segment.piDescriptionLine2': 'Configure with your API key, OAuth subscription, or custom endpoint.',
    'onboarding.apiSetup.option.claudeOauth': 'Use your Claude subscription for unlimited access.',
    'onboarding.apiSetup.option.anthropicApiKey': 'Pay-as-you-go via Anthropic, OpenRouter, or compatible APIs.',
    'onboarding.apiSetup.option.chatgptOauth': 'Use your ChatGPT subscription with XClaw Backend.',
    'onboarding.apiSetup.option.copilotOauth': 'Use your GitHub Copilot subscription with XClaw Backend.',
    'onboarding.apiSetup.option.apiKeyName': 'API Key',
    'onboarding.apiSetup.option.piApiKey': 'Use provider presets (Anthropic, OpenAI, Google, etc.) via XClaw Backend.',
    'onboarding.apiSetup.title': 'Set up your Agent',
    'onboarding.apiSetup.descriptionLine1': "Select how you'd like to power your AI agents.",
    'onboarding.apiSetup.descriptionLine2': 'You can add more connections later.',
    'onboarding.credentials.connecting': 'Connecting...',
    'onboarding.credentials.validating': 'Validating...',
    'onboarding.credentials.chatgpt.title': 'Connect ChatGPT',
    'onboarding.credentials.chatgpt.description': 'Use your ChatGPT subscription to power XClaw.',
    'onboarding.credentials.chatgpt.signIn': 'Sign in with ChatGPT',
    'onboarding.credentials.chatgpt.help': 'Click the button above to sign in with your OpenAI account. A browser window will open for authentication.',
    'onboarding.credentials.chatgpt.success': 'Connected! Your ChatGPT subscription is ready.',
    'onboarding.credentials.copilot.title': 'Connect GitHub Copilot',
    'onboarding.credentials.copilot.description': 'Use your GitHub Copilot subscription to power XClaw.',
    'onboarding.credentials.copilot.waitingAuthorization': 'Waiting for authorization...',
    'onboarding.credentials.copilot.signIn': 'Sign in with GitHub',
    'onboarding.credentials.copilot.enterCode': 'Enter this code on GitHub to authorize:',
    'onboarding.credentials.copilot.copied': 'Copied to clipboard',
    'onboarding.credentials.copilot.browserHint': 'A browser window should have opened to github.com/login/device',
    'onboarding.credentials.copilot.help': 'Click the button above to sign in with your GitHub account.',
    'onboarding.credentials.copilot.success': 'Connected! Your GitHub Copilot subscription is ready.',
    'onboarding.credentials.claude.codeTitle': 'Enter Authorization Code',
    'onboarding.credentials.claude.codeDescription': 'Copy the code from the browser page and paste it below.',
    'onboarding.credentials.claude.title': 'Connect Claude Account',
    'onboarding.credentials.claude.description': 'Use your Claude subscription to power multi-agent workflows.',
    'onboarding.credentials.claude.signIn': 'Sign in with Claude',
    'onboarding.credentials.apiConfiguration.title': 'API Configuration',
    'onboarding.credentials.apiConfiguration.piDescription': 'Select a provider preset and enter the API key. For arbitrary Anthropic-compatible endpoints, use Anthropic API Key mode.',
    'onboarding.credentials.apiConfiguration.anthropicDescription': 'Enter your API key. Optionally configure a custom endpoint for OpenRouter, Ollama, or compatible APIs.',
    'onboarding.oauth.authorizationCode': 'Authorization Code',
    'onboarding.oauth.authorizationCodePlaceholder': 'Paste your authorization code here',
    'onboarding.localModel.title': 'Local Model',
    'onboarding.localModel.description': 'Connect to Ollama or any OpenAI-compatible local server.',
    'onboarding.localModel.endpointRequired': 'Endpoint URL is required.',
    'onboarding.localModel.modelRequired': 'At least one model name is required.',
    'onboarding.localModel.endpointHint': 'Default Ollama port is 11434. Change if using a custom setup.',
    'onboarding.localModel.model': 'Model',
    'onboarding.localModel.modelHintPrefix': 'Use any model pulled via',
    'onboarding.localModel.modelHintSuffix': 'Comma-separated for multiple models.',
    'onboarding.completion.settingUp': 'Setting up...',
    'onboarding.completion.title': "You're all set!",
    'onboarding.completion.saving': 'Saving your configuration...',
    'onboarding.completion.description': 'Just start a chat and get to work.',
    'onboarding.gitBash.title': 'Git Bash Required',
    'onboarding.gitBash.description': 'XClaw needs Git Bash to run shell commands on Windows. It was not found on your system.',
    'onboarding.gitBash.installTitle': 'Install Git for Windows',
    'onboarding.gitBash.installDescription': "The easiest way to get Git Bash. It's free and includes everything you need.",
    'onboarding.gitBash.download': 'Download Git for Windows',
    'onboarding.gitBash.installedTitle': 'Already have Git installed?',
    'onboarding.gitBash.installedDescription': 'If Git is installed in a non-standard location, you can specify the path to bash.exe.',
    'onboarding.gitBash.usePath': 'Use this path',
    'onboarding.gitBash.recheck': 'Re-check',
    'onboarding.apiKey.preset.custom': 'Custom',
    'onboarding.apiKey.preset.piDirect': 'XClaw Backend (Direct)',
    'onboarding.apiKey.placeholder.pasteKey': 'Paste your key here...',
    'onboarding.apiKey.label': 'API Key',
    'onboarding.apiKey.endpoint': 'Endpoint',
    'onboarding.apiKey.endpointPlaceholder': 'https://your-api-endpoint.com',
    'onboarding.apiKey.protocol': 'Protocol',
    'onboarding.apiKey.protocol.openaiCompatible': 'OpenAI Compatible',
    'onboarding.apiKey.protocol.anthropicCompatible': 'Anthropic Compatible',
    'onboarding.apiKey.protocolHint': 'Most third-party APIs (Ollama, vLLM, DashScope) use OpenAI Compatible.',
    'onboarding.apiKey.authentication': 'Authentication',
    'onboarding.apiKey.authentication.iamCredentials': 'IAM Credentials',
    'onboarding.apiKey.authentication.environment': 'Environment (AWS CLI)',
    'onboarding.apiKey.aws.accessKeyId': 'Access Key ID',
    'onboarding.apiKey.aws.secretAccessKey': 'Secret Access Key',
    'onboarding.apiKey.aws.secretAccessKeyPlaceholder': 'Your secret access key',
    'onboarding.apiKey.aws.sessionToken': 'Session Token',
    'onboarding.apiKey.aws.sessionTokenPlaceholder': 'For temporary credentials (STS)',
    'onboarding.apiKey.aws.environmentHintPrefix': 'Uses your existing AWS credential chain',
    'onboarding.apiKey.aws.environmentHintSuffix': 'and environment variables.',
    'onboarding.apiKey.aws.region': 'AWS Region',
    'onboarding.apiKey.error.selectTierModels': 'Please select a model for each tier.',
    'onboarding.apiKey.error.accessKeyRequired': 'Access Key ID is required for IAM authentication.',
    'onboarding.apiKey.error.secretKeyRequired': 'Secret Access Key is required for IAM authentication.',
    'onboarding.apiKey.error.defaultModelRequired': 'Default model is required for custom endpoints.',
    'onboarding.apiKey.tier.best': 'Best',
    'onboarding.apiKey.tier.bestDescription': 'most capable',
    'onboarding.apiKey.tier.balanced': 'Balanced',
    'onboarding.apiKey.tier.balancedDescription': 'good for everyday use',
    'onboarding.apiKey.tier.fast': 'Fast',
    'onboarding.apiKey.tier.fastDescription': 'summarization & utility',
    'onboarding.apiKey.loadingModels': 'Loading models...',
    'onboarding.apiKey.selectModel': 'Select model...',
    'onboarding.apiKey.searchModels': 'Search models...',
    'onboarding.apiKey.defaultModel': 'Default Model',
    'onboarding.apiKey.defaultModelPlaceholder': 'e.g. claude-opus-4-6, claude-sonnet-4-6, claude-haiku-4-5',
    'onboarding.apiKey.defaultModelHint': 'Comma-separated list. The first model is the default. The last is used for summarization.',
    'onboarding.apiKey.customEndpointHint': 'Required for custom endpoints. Use the provider-specific model ID.',

    'menu.edit': 'Edit',
    'menu.view': 'View',
    'menu.window': 'Window',
    'menu.undo': 'Undo',
    'menu.redo': 'Redo',
    'menu.cut': 'Cut',
    'menu.copy': 'Copy',
    'menu.paste': 'Paste',
    'menu.selectAll': 'Select All',
    'menu.zoomIn': 'Zoom In',
    'menu.zoomOut': 'Zoom Out',
    'menu.resetZoom': 'Reset Zoom',
    'menu.toggleFocusMode': 'Toggle Focus Mode',
    'menu.toggleSidebar': 'Toggle Sidebar',
    'menu.minimize': 'Minimize',
    'menu.maximize': 'Maximize',

    'settings.page.app.label': 'App',
    'settings.page.app.description': 'Power, proxy, updates',
    'settings.page.notifications.label': 'Notifications',
    'settings.page.notifications.description': 'Desktop and in-app alerts',
    'settings.notifications.title': 'Notifications',
    'settings.notifications.desktop.title': 'Desktop',
    'settings.notifications.desktop.label': 'Desktop notifications',
    'settings.notifications.desktop.description': 'Get notified when AI finishes working in a chat.',
    'settings.notifications.completion.title': 'Task completion',
    'settings.notifications.completion.description': 'How to notify you when AI finishes working in a chat.',
    'settings.notifications.sound.title': 'Sound',
    'settings.notifications.sound.label': 'Completion sound',
    'settings.notifications.sound.description': 'Play a sound when AI finishes working.',
    'settings.notifications.sound.none': 'None',
    'settings.notifications.island.title': 'Dynamic Notification',
    'settings.notifications.island.description': 'Show session status in the MacBook notch area.',
    'settings.notifications.island.label': 'Dynamic notification',
    'settings.notifications.island.detail': 'Display a floating status indicator near the notch. Shows processing spinner, completion checkmark, and session list.',
    'settings.page.ai.label': 'AI',
    'settings.page.ai.description': 'Model, thinking, connections',
    'settings.page.appearance.label': 'Appearance',
    'settings.page.appearance.description': 'Theme, font, tool icons',
    'settings.page.input.label': 'Input',
    'settings.page.input.description': 'Send key, spell check',
    'settings.page.memory.label': 'Memory',
    'settings.page.memory.description': 'Tell the AI about yourself, your preferences, and project context',
    'settings.page.workspace.label': 'Workspace',
    'settings.page.workspace.description': 'Name, icon, working directory',
    'settings.page.server.label': 'Server',
    'settings.page.server.description': 'Remote access and TLS',
    'settings.page.permissions.label': 'Permissions',
    'settings.page.permissions.description': 'Explore mode rules',
    'settings.page.labels.label': 'Labels',
    'settings.page.labels.description': 'Manage session labels',
    'settings.page.shortcuts.label': 'Shortcuts',
    'settings.page.shortcuts.description': 'Keyboard shortcuts',
    'settings.page.preferences.label': 'Preferences',
    'settings.page.preferences.description': 'User preferences',
    'settings.openInNewWindow': 'Open in New Window',
    'settings.app.title': 'App',
    'settings.app.saving': 'Saving...',
    'settings.app.notifications.title': 'Notifications',
    'settings.app.notifications.desktop.label': 'Desktop notifications',
    'settings.app.notifications.desktop.description': 'Get notified when AI finishes working in a chat.',
    'settings.app.power.title': 'Power',
    'settings.app.power.keepAwake.label': 'Keep screen awake',
    'settings.app.power.keepAwake.description': 'Prevent the screen from turning off while sessions are running.',
    'settings.app.power.keepAwake.remoteHint': 'Remote mode: keep screen awake currently only tracks local sessions on this desktop client.',
    'settings.app.sidebar.title': 'Sidebar',
    'settings.app.sidebar.channels.label': 'Show Channels entry',
    'settings.app.sidebar.channels.description': 'Hide the Channels entry from the sidebar without deleting any saved channel configuration.',
    'settings.app.sidebar.automationRules.label': 'Show Automations entry',
    'settings.app.sidebar.automationRules.description': 'Hide the Automations entry from the sidebar without deleting any saved automation configuration.',
    'settings.app.network.title': 'Network',
    'settings.app.network.proxy.label': 'HTTP proxy',
    'settings.app.network.proxy.description': 'Route network traffic through a proxy server.',
    'settings.app.network.httpProxy': 'HTTP Proxy',
    'settings.app.network.httpsProxy': 'HTTPS Proxy',
    'settings.app.network.bypassRules': 'Bypass Rules',
    'settings.app.network.proxyPlaceholder': 'http://proxy.example.com:8080',
    'settings.app.network.bypassPlaceholder': 'localhost, 127.0.0.1, .example.com',
    'settings.app.network.error.protocol': 'Must be http://, https://, socks4://, or socks5:// URL',
    'settings.app.network.error.invalidUrl': 'Invalid URL format',
    'settings.app.network.error.failedSave': 'Failed to save',
    'settings.app.network.scope.label': 'Proxy scope',
    'settings.app.network.scope.all': 'All',
    'settings.app.network.scope.ai': 'AI only',
    'settings.app.network.scope.browser': 'Browser only',
    'settings.app.network.scope.allDescription': 'Route both AI requests and browser traffic through the proxy.',
    'settings.app.network.scope.aiDescription': 'Only route model and tool requests through the proxy.',
    'settings.app.network.scope.browserDescription': 'Only route the embedded browser through the proxy.',
    'settings.app.network.bedrockHttpMode.label': 'HTTP transport',
    'settings.app.network.bedrockHttpMode.description': 'Choose the HTTP transport used for direct model requests. Automatic uses HTTP/2 when possible and falls back to HTTP/1.1 for proxy compatibility.',
    'settings.app.network.bedrockHttpMode.auto': 'Auto',
    'settings.app.network.bedrockHttpMode.http1': 'HTTP/1.1',
    'settings.app.network.bedrockHttpMode.http2': 'HTTP/2',
    'settings.app.network.bedrockHttpMode.autoDescription': 'Prefer HTTP/2 and automatically fall back to HTTP/1.1 when direct requests hit compatibility issues.',
    'settings.app.network.bedrockHttpMode.http1Description': 'Use the most compatible transport, especially when proxies or older gateways are involved.',
    'settings.app.network.bedrockHttpMode.http2Description': 'Prefer the newer transport when the upstream service and network path both support it cleanly.',
    'settings.app.network.bedrockHttpMode.http2ProxyHint': 'When AI proxy routing is enabled, direct requests may still fall back to HTTP/1.1 because the current proxy tunnel path is HTTP/1.1-only.',
    'settings.app.network.remoteHint': 'Remote mode: these proxy settings apply to this desktop client. Remote server AI traffic must be configured on the server itself.',
    'settings.app.about.title': 'About',
    'settings.app.about.version': 'Version',
    'settings.app.about.checkForUpdates': 'Check for updates',
    'settings.app.about.checkNow': 'Check Now',
    'settings.app.about.checking': 'Checking...',
    'settings.app.about.updateReady': 'Update ready',
    'settings.app.about.restartToUpdate': 'Restart to Update to v{version}',
    'settings.app.about.downloadingProgress': 'Downloading v{version} ({progress}%)',
    'settings.appearance.title': 'Appearance',
    'settings.appearance.defaultTheme.title': 'Default Theme',
    'settings.appearance.mode.label': 'Mode',
    'settings.appearance.mode.system': 'System',
    'settings.appearance.mode.light': 'Light',
    'settings.appearance.mode.dark': 'Dark',
    'settings.appearance.colorTheme.label': 'Color theme',
    'settings.appearance.font.label': 'Font',
    'settings.appearance.font.system': 'System',
    'settings.appearance.font.inter': 'Inter',
    'settings.appearance.font.anthropicSans': 'XClaw Sans',
    'settings.appearance.font.anthropicSerif': 'XClaw Serif',
    'settings.appearance.font.geist': 'Geist',
    'settings.appearance.font.geistMono': 'Geist Mono',
    'settings.appearance.font.firaCode': 'Fira Code',
    'settings.appearance.font.jetbrainsMono': 'JetBrains Mono',
    'settings.appearance.font.monaspaceArgon': 'Monaspace Argon',
    'settings.appearance.font.monaspaceKrypton': 'Monaspace Krypton',
    'settings.appearance.font.monaspaceNeon': 'Monaspace Neon',
    'settings.appearance.font.monaspaceXenon': 'Monaspace Xenon',
    'settings.appearance.font.tsangerJinKai': 'Tsanger JinKai',
    'settings.appearance.language.label': 'Language',
    'settings.appearance.language.option.zhCN': '中文',
    'settings.appearance.language.option.en': 'English',
    'settings.appearance.themeWarning': 'Theme warning',
    'settings.appearance.usingBundledFallback': 'using bundled fallback',
    'settings.appearance.usingDefaultTheme': 'using default theme',
    'settings.appearance.workspaceThemes.title': 'Workspace Themes',
    'settings.appearance.workspaceThemes.description': 'Override theme settings per workspace',
    'settings.appearance.workspaceThemes.useDefault': 'Use Default',
    'settings.appearance.workspaceThemes.useDefaultWith': 'Use Default ({label})',
    'settings.appearance.interface.title': 'Interface',
    'settings.server.title': 'Server',
    'settings.server.remoteAccess': 'Remote access',
    'settings.server.enableServerMode': 'Enable server mode',
    'settings.server.allowRemoteConnections': 'Allow remote connections to this app over your network.',
    'settings.server.restartRequired': 'A restart is required for these changes to take effect.',
    'settings.server.restartNow': 'Restart now',
    'settings.server.connectionSection': 'Connection',
    'settings.server.port': 'Port',
    'settings.server.token': 'Token',
    'settings.server.certificate': 'Certificate',
    'settings.server.privateKey': 'Private key',
    'settings.server.notConfigured': 'Not configured',
    'settings.server.insecureWarning': 'Your server is accepting insecure connections.',
    'settings.server.noTlsWarning': 'TLS is not configured. Traffic will not be encrypted.',
    'settings.server.unsupported': 'Server settings are not available in this build.',
    'settings.server.portValidation': 'Port must be between 1024 and 65535.',
    'settings.server.privateKeyRequired': 'A private key is required when a certificate is configured.',
    'settings.server.certificateRequired': 'A certificate is required when a private key is configured.',
    'settings.server.saved': 'Server settings saved.',
    'settings.server.failedToSave': 'Failed to save server settings.',
    'settings.server.copiedToClipboard': 'Copied {label} to clipboard.',
    'settings.server.copyFailed': 'Failed to copy to clipboard.',
    'settings.tunnel.title': 'Quick Remote Access',
    'settings.tunnel.description': 'Access your XClaw from anywhere via Cloudflare Tunnel. No account needed, no configuration required.',
    'settings.tunnel.start': 'Enable Remote Access',
    'settings.tunnel.stop': 'Stop',
    'settings.tunnel.active': 'Remote access is active',
    'settings.tunnel.started': 'Remote access enabled',
    'settings.tunnel.stopped': 'Remote access disabled',
    'settings.tunnel.failedToStart': 'Failed to start tunnel',
    'settings.tunnel.failedToStop': 'Failed to stop tunnel',
    'settings.tunnel.webUrl': 'Web UI',
    'settings.tunnel.hint': 'Open web.xclaw.team on your phone, enter the URL and token above to connect. The URL changes each time. Remote access stops when you close the app.',
    'settings.appearance.connectionIcons.label': 'Connection icons',
    'settings.appearance.connectionIcons.description': 'Show provider icons in the session list and model selector',
    'settings.appearance.richToolDescriptions.label': 'Rich tool descriptions',
    'settings.appearance.richToolDescriptions.description': 'Add action names and intent descriptions to all tool calls. Provides richer activity context in sessions.',
    'settings.appearance.toolIcons.title': 'Tool Icons',
    'settings.appearance.toolIcons.description': 'Icons shown next to CLI commands in chat activity. Stored in ~/.xclaw/tool-icons/.',
    'settings.appearance.toolIcons.searchPlaceholder': 'Search tools...',
    'settings.appearance.toolIcons.empty': 'No tool icon mappings found',
    'settings.appearance.toolIcons.table.icon': 'Icon',
    'settings.appearance.toolIcons.table.tool': 'Tool',
    'settings.appearance.toolIcons.table.commands': 'Commands',
    'settings.input.title': 'Input',
    'settings.input.typing.title': 'Typing',
    'settings.input.typing.description': 'Control how text is entered in the chat input.',
    'settings.input.typing.autoCapitalisation.label': 'Auto capitalisation',
    'settings.input.typing.autoCapitalisation.description': 'Automatically capitalise the first letter when typing a message.',
    'settings.input.typing.spellCheck.label': 'Spell check',
    'settings.input.typing.spellCheck.description': 'Underline misspelled words while typing.',
    'settings.input.sending.title': 'Sending',
    'settings.input.sending.description': 'Choose how to send messages.',
    'settings.input.sending.sendMessageWith.label': 'Send message with',
    'settings.input.sending.sendMessageWith.description': 'Keyboard shortcut for sending messages',
    'settings.input.sending.enterDescription': 'Use Shift+Enter for new lines',
    'settings.input.sending.cmdEnterDescription': 'Use Enter for new lines',
    'settings.input.voice.title': 'Voice Input',
    'settings.input.voice.description': 'Record speech locally and insert the transcript into the chat input. Transcription language follows the app language.',
    'settings.input.voice.enabled.label': 'Enable voice input',
    'settings.input.voice.enabled.description': 'Enable the keyboard shortcut for local voice input in the chat input.',
    'settings.input.voice.model.label': 'Model',
    'settings.input.voice.model.description': 'Choose which local Whisper model to use for transcription.',
    'settings.input.voice.files.label': 'Model files',
    'settings.input.voice.files.downloaded': '{label} is downloaded locally.',
    'settings.input.voice.files.notDownloaded': '{label} is not downloaded yet.',
    'settings.input.voice.files.downloading': 'Downloading {label}…',
    'settings.input.voice.files.requiresDownload': 'Download a model before first use.',
    'settings.input.voice.shortcut.label': 'Shortcut',
    'settings.input.voice.shortcut.description': 'Works while XClaw is focused.',
    'settings.input.voice.download': 'Download',
    'settings.input.voice.remove': 'Remove',
    'settings.input.voice.unassigned': 'Unassigned',
    'settings.input.learning.title': 'Learning',
    'settings.input.learning.description': 'Control whether XClaw suggests reusable skills from successful repeatable workflows.',
    'settings.input.learning.skillProposals.label': 'Suggest reusable skills',
    'settings.input.learning.skillProposals.description': 'After successful multi-step turns, show a proposal card instead of silently creating a skill. Dismissing a suggestion suppresses similar ones for seven days.',
    'settings.memory.title': 'Memory',
    'settings.memory.edit': 'Edit',
    'settings.memory.delete': 'Delete',
    'settings.memory.save': 'Save',
    'settings.memory.cancel': 'Cancel',
    'settings.memory.saving': 'Saving...',
    'settings.memory.mode.editing': 'Editing',
    'settings.memory.mode.memory': 'Memory',
    'settings.memory.placeholder': 'Start your memory document here. XClaw reads this to understand your preferences and project context.',
    'settings.memory.entries': 'entries',
    'settings.memory.entrypoint': 'Entrypoint',
    'settings.memory.confirmDelete': 'Delete "{name}" from durable memory?',
    'settings.memory.confirmOverwrite': 'A durable memory with this name already exists. Replace it?',
    'settings.memory.saved': 'Memory saved',
    'settings.memory.updated': 'Memory updated',
    'settings.memory.deleted': 'Memory deleted',
    'settings.memory.failedLoad': 'Failed to load memory',
    'settings.memory.failedSave': 'Failed to save memory',
    'settings.memory.failedDelete': 'Failed to delete memory',
    'settings.memory.revert': 'Revert',
    'settings.memory.fileLabel': 'Markdown file',
    'settings.memory.filePending': 'This note will be stored as a markdown file after the first save.',
    'settings.memory.about.title': 'Memory',
    'settings.memory.about.description': 'Treat this like a note to future XClaw. Just write what should be remembered later.',
    'settings.memory.about.paragraph1': 'Keep project context here. If it feels like a personal profile setting, put it in Preferences instead.',
    'settings.memory.user.title': 'Personal memory',
    'settings.memory.user.description': 'Advanced, cross-workspace notes. Most users only need Preferences.',
    'settings.memory.user.cardTitle': 'Personal note',
    'settings.memory.user.cardDescription': 'Only use this for durable personal notes that should apply across workspaces.',
    'settings.memory.user.placeholder': 'Example: Prefer concise explanations. Default to Chinese unless I ask otherwise.',
    'settings.memory.user.unsaved': 'You have unsaved personal memory changes.',
    'settings.memory.user.add': 'New memory document',
    'settings.memory.user.openPreferences': 'Open Preferences',
    'settings.memory.user.empty.title': 'No user memory saved',
    'settings.memory.user.empty.description': 'Advanced user memory documents will appear here when you or the agent choose to store them.',
    'settings.memory.workspace.title': 'Project memory',
    'settings.memory.workspace.description': 'One simple markdown note for this workspace.',
    'settings.memory.workspace.cardTitle': 'Workspace note',
    'settings.memory.workspace.cardDescription': 'Write conventions, constraints, background, or anything XClaw should keep in mind for this project.',
    'settings.memory.workspace.placeholder': 'Example: We deploy on Fridays only. Keep release notes short. Prefer Chinese for customer-facing copy.',
    'settings.memory.workspace.unsaved': 'You have unsaved workspace memory changes.',
    'settings.memory.workspace.add': 'New memory document',
    'settings.memory.workspace.empty.title': 'No workspace memory saved',
    'settings.memory.workspace.empty.description': 'When the agent stores durable project facts, they will appear here for this workspace.',
    'settings.memory.recall.title': 'Session Recall and Pulse',
    'settings.memory.recall.description': 'These layers are read-only here and stay separate from durable memory.',
    'settings.memory.recall.paragraph1': 'Session recall is generated from bounded search over prior sessions in the current workspace. It is not written back as durable memory automatically.',
    'settings.memory.recall.paragraph2': 'Pulse memory remains a separate briefing layer for Pulse workflows and does not merge into user or workspace memory unless you explicitly move information yourself.',
    'settings.memory.form.addTitle': 'New memory document',
    'settings.memory.form.editTitle': 'Edit memory document',
    'settings.memory.form.userHint': 'User memory is advanced, cross-workspace context. Stable profile details still belong in Preferences.',
    'settings.memory.form.workspaceHint': 'Workspace memory stays scoped to the current project and is stored as markdown.',
    'settings.memory.form.name.label': 'Title',
    'settings.memory.form.name.description': 'Short name for what XClaw should remember.',
    'settings.memory.form.name.placeholder': 'Timezone preference',
    'settings.memory.form.description.label': 'Summary',
    'settings.memory.form.description.description': 'Optional one-line summary. If you leave it empty, XClaw will infer one from the title.',
    'settings.memory.form.description.placeholder': 'Default timezone for scheduling',
    'settings.memory.form.type.label': 'Type',
    'settings.memory.form.type.description': 'Type is written to frontmatter and only matters if you want to categorize this memory more precisely.',
    'settings.memory.form.document.label': 'Markdown document',
    'settings.memory.form.document.description': 'This is the raw markdown file that will be written to disk, including YAML frontmatter.',
    'settings.memory.form.type.user.preference': 'Preference',
    'settings.memory.form.type.user.profile': 'Profile',
    'settings.memory.form.type.user.reference': 'Reference',
    'settings.memory.form.type.workspace.project': 'Project',
    'settings.memory.form.type.workspace.feedback': 'Feedback',
    'settings.memory.form.type.workspace.reference': 'Reference',
    'settings.memory.form.body.label': 'Body',
    'settings.memory.form.body.description': 'Edit the markdown body with the same editor stack used elsewhere in the app.',
    'settings.memory.form.body.placeholder': 'Write the durable note or project context...',
    'settings.memory.form.advanced.title': 'Advanced details',
    'settings.memory.form.advanced.description': 'Only open this if you need to tune type, summary, or inspect the raw file.',
    'settings.memory.form.filename': 'File',
    'chat.skillProposal.label': 'Skill suggestion',
    'chat.skillProposal.slug': 'Suggested slug',
    'chat.skillProposal.target': 'Update skill',
    'chat.skillProposal.dismiss': 'Dismiss for 7 days',
    'chat.skillProposal.accept': 'Create skill',
    'chat.skillProposal.acceptUpdate': 'Update skill',
    'chat.skillProposal.accepted': 'Skill proposal saved',
    'chat.skillProposal.acceptFailed': 'Could not save skill proposal',
    'chat.skillProposal.dismissed': 'Skill proposal dismissed',
    'chat.skillProposal.dismissedDescription': 'Similar suggestions will stay suppressed for seven days.',
    'chat.skillProposal.dismissFailed': 'Could not dismiss skill proposal',
    'chat.skillProposal.historyLabel': 'Recent learning',
    'chat.skillProposal.cooldown': 'Similar suggestions muted until',
    'chat.skillProposal.historyAcceptedCreate': 'Created workspace skill',
    'chat.skillProposal.historyAcceptedUpdate': 'Updated workspace skill',
    'chat.skillProposal.historyDismissed': 'Dismissed for cooldown',
    'input.voice.recordingHint': 'Listening… release {key} to transcribe',
    'input.voice.transcribingInline': 'Transcribing your speech…',
    'settings.preferences.title': 'Preferences',
    'settings.preferences.basicInfo.title': 'Basic Info',
    'settings.preferences.basicInfo.description': 'Help XClaw personalize responses to you.',
    'settings.preferences.name.label': 'Name',
    'settings.preferences.name.description': 'How XClaw should address you.',
    'settings.preferences.name.placeholder': 'Your name',
    'settings.preferences.timezone.label': 'Timezone',
    'settings.preferences.timezone.description': "Used for relative dates like 'tomorrow' or 'next week'.",
    'settings.preferences.timezone.placeholder': 'e.g., America/New_York',
    'settings.preferences.language.label': 'Language',
    'settings.preferences.language.description': "Preferred language for XClaw's responses.",
    'settings.preferences.language.placeholder': 'e.g., English',
    'settings.preferences.location.title': 'Location',
    'settings.preferences.location.description': 'Enables location-aware responses like weather, local time, and regional context.',
    'settings.preferences.city.label': 'City',
    'settings.preferences.city.description': 'Your city for local information and context.',
    'settings.preferences.city.placeholder': 'e.g., New York',
    'settings.preferences.country.label': 'Country',
    'settings.preferences.country.description': 'Your country for regional formatting and context.',
    'settings.preferences.country.placeholder': 'e.g., USA',
    'settings.preferences.notes.title': 'Notes',
    'settings.preferences.notes.description': 'Free-form context that helps XClaw understand your preferences.',
    'settings.preferences.notes.placeholder': "Any additional context you'd like XClaw to know...",
    'settings.preferences.memoryBoundary.title': 'Memory vs Preferences',
    'settings.preferences.memoryBoundary.description': 'Keep stable profile data here, like your name, timezone, and location. Use Memory for longer-lived project or personal notes that XClaw should remember later.',
    'settings.preferences.memoryBoundary.open': 'Open Memory',
    'settings.workspace.title': 'Workspace Settings',
    'settings.workspace.noWorkspaceSelected': 'No workspace selected',
    'settings.workspace.info.title': 'Workspace Info',
    'settings.workspace.info.name': 'Name',
    'settings.workspace.info.icon': 'Icon',
    'settings.workspace.info.untitled': 'Untitled',
    'settings.workspace.renameTitle': 'Rename workspace',
    'settings.workspace.renamePlaceholder': 'Enter workspace name...',
    'settings.workspace.permissions.title': 'Permissions',
    'settings.workspace.permissions.defaultMode.label': 'Default mode',
    'settings.workspace.permissions.defaultMode.description': 'Control what AI can do',
    'settings.workspace.permissions.mode.safeLabel': 'Explore',
    'settings.workspace.permissions.mode.safeDescription': 'Read-only, no changes allowed',
    'settings.workspace.permissions.mode.askLabel': 'Ask',
    'settings.workspace.permissions.mode.askDescription': 'Prompts before making edits',
    'settings.workspace.permissions.mode.allowAllLabel': 'Auto',
    'settings.workspace.permissions.mode.allowAllDescription': 'Full autonomous execution',
    'settings.workspace.modeCycling.title': 'Mode Cycling',
    'settings.workspace.modeCycling.description': 'Select which modes to cycle through with Shift+Tab',
    'settings.workspace.modeCycling.error': 'At least 2 modes required',
    'settings.workspace.mode.safe.label': 'Explore',
    'settings.workspace.mode.safe.description': 'Read-only, no changes allowed',
    'settings.workspace.mode.ask.label': 'Ask',
    'settings.workspace.mode.ask.description': 'Prompts before making edits',
    'settings.workspace.mode.allow-all.label': 'Auto',
    'settings.workspace.mode.allow-all.description': 'Full autonomous execution',
    'settings.workspace.defaultSources.title': 'Default Sources',
    'settings.workspace.defaultSources.description': 'Sources auto-enabled for new sessions',
    'settings.workspace.defaultSources.empty': 'No sources configured in this workspace.',
    'settings.workspace.advanced.title': 'Advanced',
    'settings.workspace.advanced.workingDirectory.label': 'Default Working Directory',
    'settings.workspace.advanced.workingDirectory.notSet': 'Not set (uses session folder)',
    'settings.workspace.advanced.localMcp.label': 'Local MCP Servers',
    'settings.workspace.advanced.localMcp.description': 'Enable stdio subprocess servers',
    'settings.shortcuts.title': 'Shortcuts',
    'settings.shortcuts.category.general': 'General',
    'settings.shortcuts.category.navigation': 'Navigation',
    'settings.shortcuts.category.view': 'View',
    'settings.shortcuts.category.navigator': 'Navigator',
    'settings.shortcuts.category.chat': 'Chat',
    'settings.shortcuts.section.listNavigation': 'List Navigation',
    'settings.shortcuts.section.sessionList': 'Session List',
    'settings.shortcuts.section.chatInput': 'Chat Input',
    'settings.shortcuts.navigateItemsInList': 'Navigate items in list',
    'settings.shortcuts.goToFirstItem': 'Go to first item',
    'settings.shortcuts.goToLastItem': 'Go to last item',
    'settings.shortcuts.focusChatInput': 'Focus chat input',
    'settings.shortcuts.openContextMenu': 'Open context menu',
    'settings.shortcuts.addFilterAsExcluded': 'Add filter as excluded',
    'settings.shortcuts.sendMessage': 'Send message',
    'settings.shortcuts.newLine': 'New line',
    'settings.shortcuts.closeDialogOrBlurInput': 'Close dialog / blur input',
    'settings.shortcuts.deleteSession': 'Delete session',
    'settings.shortcuts.renameSession': 'Rename session',
    'settings.shortcuts.section.agentTree': 'Agent Tree',
    'settings.shortcuts.collapseFolder': 'Collapse folder',
    'settings.shortcuts.expandFolder': 'Expand folder',
    'settings.shortcuts.key.rightClick': 'Right-click',
    'settings.shortcuts.key.click': 'Click',
    'action.app.newChat.label': 'New Chat',
    'action.app.newChat.description': 'Create a new chat session',
    'action.app.newChatInPanel.label': 'New Chat in Panel',
    'action.app.newChatInPanel.description': 'Create a new chat session in a new panel',
    'action.app.settings.label': 'Settings',
    'action.app.settings.description': 'Open application settings',
    'action.app.toggleTheme.label': 'Toggle Theme',
    'action.app.toggleTheme.description': 'Switch between light and dark mode',
    'action.app.search.label': 'Search',
    'action.app.search.description': 'Open search panel',
    'action.app.keyboardShortcuts.label': 'Keyboard Shortcuts',
    'action.app.keyboardShortcuts.description': 'Show keyboard shortcuts reference',
    'action.app.newWindow.label': 'New Window',
    'action.app.newWindow.description': 'Open a new window',
    'action.app.quit.label': 'Quit',
    'action.app.quit.description': 'Quit the application',
    'action.nav.focusSidebar.label': 'Focus Sidebar',
    'action.nav.focusNavigator.label': 'Focus Navigator',
    'action.nav.focusChat.label': 'Focus Chat',
    'action.nav.nextZone.label': 'Focus Next Zone',
    'action.nav.goBack.label': 'Go Back',
    'action.nav.goBack.description': 'Navigate to previous session',
    'action.nav.goForward.label': 'Go Forward',
    'action.nav.goForward.description': 'Navigate to next session',
    'action.nav.goBackAlt.label': 'Go Back',
    'action.nav.goBackAlt.description': 'Navigate to previous session (arrow key)',
    'action.nav.goForwardAlt.label': 'Go Forward',
    'action.nav.goForwardAlt.description': 'Navigate to next session (arrow key)',
    'action.view.toggleSidebar.label': 'Toggle Sidebar',
    'action.view.toggleFocusMode.label': 'Toggle Focus Mode',
    'action.view.toggleFocusMode.description': 'Hide both sidebars for distraction-free work',
    'action.navigator.selectAll.label': 'Select All',
    'action.navigator.clearSelection.label': 'Clear Selection',
    'action.panel.focusNext.label': 'Focus Next Panel',
    'action.panel.focusNext.description': 'Move focus to the next panel',
    'action.panel.focusPrev.label': 'Focus Previous Panel',
    'action.panel.focusPrev.description': 'Move focus to the previous panel',
    'action.chat.stopProcessing.label': 'Stop Processing',
    'action.chat.stopProcessing.description': 'Cancel the current agent task (double-press)',
    'action.chat.cyclePermissionMode.label': 'Cycle Permission Mode',
    'action.chat.cyclePermissionMode.description': 'Switch between Explore, Ask, and Execute modes',
    'action.chat.nextSearchMatch.label': 'Next Search Match',
    'action.chat.prevSearchMatch.label': 'Previous Search Match',
    'action.chat.toggleVoiceInput.label': 'Toggle Voice Input',
    'action.chat.toggleVoiceInput.description': 'Start or stop local voice dictation while XClaw is focused',
    'settings.permissions.title': 'Permissions',
    'settings.permissions.about.title': 'About Permissions',
    'settings.permissions.about.paragraph1': 'Permissions control how much autonomy your agent has. In Explore mode, the agent can only read and research, which is perfect for understanding a problem before committing to changes. When you are ready, switch to Execute mode to let the agent implement the plan autonomously.',
    'settings.permissions.about.paragraph2': 'A good workflow: start in Explore to let the agent investigate, review the proposed plan, then execute with confidence.',
    'settings.permissions.default.title': 'Default Permissions',
    'settings.permissions.default.description': 'App-level patterns allowed in Explore mode. Commands not on this list are blocked.',
    'settings.permissions.default.empty': 'No default permissions found.',
    'settings.permissions.default.emptyHint': 'Default permissions should be at ~/.xclaw/permissions/default.json',
    'settings.permissions.workspace.title': 'Workspace Customizations',
    'settings.permissions.workspace.description': 'Workspace-level patterns that extend the app defaults above.',
    'settings.permissions.workspace.empty': 'No custom permissions configured.',
    'settings.permissions.workspace.emptyHint': 'Create a permissions.json file in your workspace to add custom rules.',
    'settings.permissions.writeTo': 'Write to',
    'settings.permissions.comment.listDirectoryContents': 'List directory contents',
    'settings.permissions.comment.longListing': 'Long listing (ls -l alias)',
    'settings.permissions.comment.listAllIncludingHidden': 'List all including hidden (ls -la alias)',
    'settings.permissions.comment.displayDirectoryTree': 'Display directory tree structure',
    'settings.permissions.comment.determineFileType': 'Determine file type',
    'settings.permissions.comment.displayFileMetadata': 'Display file status and metadata',
    'settings.permissions.comment.estimateDiskUsage': 'Estimate file/directory disk usage',
    'settings.permissions.comment.reportFilesystemUsage': 'Report filesystem disk space usage',
    'settings.permissions.comment.countLinesWordsBytes': 'Count lines, words, bytes in files',
    'settings.permissions.comment.addLineNumbers': 'Add line numbers to text output',
    'settings.permissions.comment.outputFirstPart': 'Output first part of files',
    'settings.permissions.comment.outputLastPart': 'Output last part of files',
    'settings.permissions.comment.customBlockedTool': 'Custom blocked tool',
    'settings.permissions.comment.customBashPattern': 'Custom bash pattern',
    'settings.permissions.comment.customMcpPattern': 'Custom MCP pattern',
    'settings.permissions.comment.customApiEndpoint': 'Custom API endpoint',
    'settings.permissions.comment.allowedWritePath': 'Allowed write path',
    'permissionsTable.column.access': 'Access',
    'permissionsTable.column.type': 'Type',
    'permissionsTable.column.pattern': 'Pattern',
    'permissionsTable.column.comment': 'Comment',
    'permissionsTable.type.tool': 'Tool',
    'permissionsTable.type.bash': 'Bash',
    'permissionsTable.type.api': 'API',
    'permissionsTable.type.mcp': 'MCP',
    'permissionsTable.status.allowed': 'Allowed',
    'permissionsTable.status.blocked': 'Blocked',
    'permissionsTable.status.ask': 'Ask',
    'permissionsTable.searchPlaceholder': 'Search patterns...',
    'permissionsTable.empty': 'No permissions configured',
    'permissionsTable.viewFullscreen': 'View Fullscreen',
    'permissionsTable.ruleSingular': 'rule',
    'permissionsTable.rulePlural': 'rules',
    'permissionsTable.ruleCount': '{count} {label}',
    'permissionsTable.patternCopied': 'Pattern copied to clipboard',
    'permissionsTable.patternCopyFailed': 'Failed to copy pattern',
    'dataTable.noResults': 'No results.',
    'dataTable.total': '{count} total',
    'dataTable.previous': 'Previous',
    'dataTable.pageOf': 'Page {page} of {total}',
    'dataTable.next': 'Next',
    'infoDataTable.searchPlaceholder': 'Search...',
    'infoDataTable.authRequiredData': 'Authenticate with this source to view available data',
    'infoGroupedList.authRequiredTools': 'Authenticate with this source to view available tools',
    'infoPage.errorLoadingContent': 'Error loading content',
    'toolsTable.column.tool': 'Tool',
    'toolsTable.empty': 'No tools available',
    'autoRulesTable.flags': 'Flags',
    'autoRulesTable.template': 'Template',
    'autoRulesTable.searchPlaceholder': 'Search rules...',
    'autoRulesTable.empty': 'No auto-apply rules configured',
    'sourceStatus.connectedDescription': 'Source is connected and working',
    'sourceStatus.authDescription': 'Source requires authentication to connect',
    'sourceStatus.failed': 'Connection Failed',
    'sourceStatus.failedDescription': 'Failed to connect to source',
    'sourceStatus.untestedDescription': 'Connection has not been tested',
    'sourceStatus.disabledDescription': 'Local MCP servers are disabled in Settings',
    'transportBanner.authFailed': 'Authentication failed. Verify XCLAW_SERVER_TOKEN.',
    'transportBanner.protocolMismatch': 'Protocol mismatch between client and server versions.',
    'transportBanner.timeout': 'Connection to {url} timed out. Server may be unreachable.',
    'transportBanner.network': 'Could not connect to {url}. Is the remote server running?',
    'transportBanner.closed': 'WebSocket closed with code {code}{reason}.',
    'transportBanner.waiting': 'Waiting for remote server connection.',
    'transportBanner.connectingTitle': 'Connecting to remote server',
    'transportBanner.connectingDescription': 'Connecting to {url}...',
    'transportBanner.retryInMs': 'retry in {ms}ms',
    'transportBanner.retrying': 'retrying',
    'transportBanner.reconnectingTitle': 'Reconnecting to remote server',
    'transportBanner.reconnectingDescription': '{reason} ({retry}, attempt {attempt})',
    'transportBanner.failedTitle': 'Cannot connect to remote server',
    'transportBanner.disconnectedTitle': 'Connection to remote server lost',
    'transportBanner.statusTitle': 'Remote server connection status',
    'transportBanner.retry': 'Retry',
    'slashMenu.compactContext': 'Compact Context',
    'slashMenu.compactContextDescription': 'Summarize conversation context to free up token budget',
    'slashMenu.modes': 'Modes',
    'slashMenu.commands': 'Commands',
    'slashMenu.recentWorkingDirectories': 'Recent Working Directories',
    'slashMenu.searchCommands': 'Search commands...',
    'slashMenu.noCommandsFound': 'No commands found',
    'slashMenu.useAtForSkillsAndFiles': 'Use @ for skills and files',
    'input.placeholder.whatWouldYouLike': 'What would you like to work on?',
    'input.placeholder.switchModes': 'Use Shift + Tab to switch between Explore and Auto',
    'input.placeholder.mention': 'Type @ to mention files, folders, or skills',
    'input.placeholder.labels': 'Type # to apply labels to this conversation',
    'input.placeholder.newLine': 'Press Shift + Return to add a new line',
    'input.placeholder.toggleSidebar': 'Press {key} + B to toggle the sidebar',
    'input.placeholder.focusMode': 'Press {key} + . for focus mode',
    'input.placeholder.voiceInput': 'Hold {key} for voice input',
    'input.voice.emptyResult': 'No speech was detected in that recording.',
    'input.voice.transcribeError': 'Voice transcription failed.',
    'input.voice.unavailable': 'Voice input is unavailable in this environment.',
    'input.voice.disabled': 'Enable voice input in Settings → Input first.',
    'input.voice.modelMissing': 'Download the selected Whisper model before using voice input.',
    'input.voice.browserUnsupported': 'This environment does not support microphone recording.',
    'input.voice.recordError': 'Recording failed. Check microphone permissions and try again.',
    'input.voice.permissionError': 'Microphone access was denied.',
    'input.fileCountSingular': '{count} file',
    'input.fileCountPlural': '{count} files',
    'input.attachFiles': 'Attach Files',
    'input.chooseSources': 'Choose Sources',
    'input.sourceCount': '{count} sources',
    'input.unavailable': 'Unavailable',
    'input.model': 'Model',
    'input.connectionUnavailableTitle': 'Connection Unavailable',
    'input.connectionUnavailableDescription': 'The connection used by this session has been removed. Create a new session to continue.',
    'input.connectionDefault': 'Connection default',
    'input.notAuthenticated': 'Not authenticated',
    'input.usingConnection': 'Using {name}',
    'input.thinkingNotSupported': 'Not supported by this model',
    'input.extendedReasoningDepth': 'Extended reasoning depth',
    'input.context': 'Context',
    'input.tokensUsed': '{count} tokens used',
    'input.contextUsedWait': '{percent}% context used — wait for current operation',
    'input.contextUsedCompact': '{percent}% context used — click to compact',
    'input.folder': 'Folder',
    'input.workInFolder': 'Work in Folder',
    'input.workingDirectory': 'Working directory',
    'input.onBranch': 'on {branch}',
    'input.chooseWorkingDirectory': 'Choose working directory',
    'input.filterFolders': 'Filter folders...',
    'input.noFoldersFound': 'No folders found',
    'input.chooseFolder': 'Choose Folder...',
    'settings.labels.title': 'Labels',
    'settings.labels.about.title': 'About Labels',
    'settings.labels.about.paragraph1': 'Labels help you organize sessions with colored tags. Use them to categorize conversations by project, topic, or priority, making it easy to filter and find related sessions later.',
    'settings.labels.about.paragraph2': 'Each label can optionally carry a value with a specific type, such as text, number, or date. This turns labels into structured metadata, for example a priority label with value 3, or a due label with a date.',
    'settings.labels.about.paragraph3': 'Auto-apply rules assign labels automatically when a message matches a regex pattern. For example, pasting a Linear issue URL can auto-tag the session with the project name and issue ID, with no manual tagging needed.',
    'settings.labels.hierarchy.title': 'Label Hierarchy',
    'settings.labels.hierarchy.description': 'All labels configured for this workspace. Labels can be nested to form groups.',
    'settings.labels.hierarchy.empty': 'No labels configured.',
    'settings.labels.hierarchy.emptyHint': 'Labels can be created by the agent or by editing labels/config.json in your workspace.',
    'settings.labels.autoApply.title': 'Auto-Apply Rules',
    'settings.labels.autoApply.description': 'Regex patterns that automatically apply labels when matched in user messages. For example, paste a Linear issue URL and automatically tag the session with the project name and issue ID.',
    'settings.ai.title': 'AI',
    'settings.ai.reauthenticate': 'Re-authenticate',
    'settings.ai.useDefault': 'Use default',
    'settings.ai.inheritFromAppSettings': 'Inherit from app settings',
    'settings.ai.closeEsc': 'Close (Esc)',
    'settings.ai.renameConnection.title': 'Rename Connection',
    'settings.ai.renameConnection.placeholder': 'Enter connection name...',
    'settings.ai.credentialHealth.title': 'Credential Issue Detected',
    'settings.ai.credentialHealth.fileCorrupted': 'Credential file is corrupted. Please re-authenticate.',
    'settings.ai.credentialHealth.decryptionFailed': 'Credentials from another machine detected. Please re-authenticate on this device.',
    'settings.ai.credentialHealth.noDefaultCredentials': 'No credentials found for your default connection.',
    'settings.ai.credentialHealth.default': 'Credential issue detected.',
    'settings.ai.connection.validating': 'Validating...',
    'settings.ai.connection.valid': 'Connection valid',
    'settings.ai.connection.invalid': 'Validation failed',
    'settings.ai.connection.unknown': 'Unknown',
    'settings.ai.connection.notAuthenticated': 'Not authenticated',
    'settings.ai.connection.setAsDefault': 'Set as default',
    'settings.ai.connection.validate': 'Validate Connection',
    'settings.ai.default.title': 'Default',
    'settings.ai.default.description': 'Settings for new chats when no workspace override is set.',
    'settings.ai.default.connection.label': 'Connection',
    'settings.ai.default.connection.description': 'API connection for new chats',
    'settings.ai.default.model.label': 'Model',
    'settings.ai.default.model.description': 'AI model for new chats',
    'settings.ai.default.thinking.label': 'Thinking',
    'settings.ai.default.thinking.description': 'Reasoning depth for new chats',
    'settings.ai.workspaceOverrides.title': 'Workspace Overrides',
    'settings.ai.workspaceOverrides.description': 'Override default settings per workspace.',
    'settings.ai.workspaceOverrides.usingDefaults': 'Using defaults',
    'settings.ai.connections.title': 'Connections',
    'settings.ai.connections.description': 'Manage your AI provider connections.',
    'settings.ai.connections.empty': 'No connections configured. Add a connection to get started.',
    'settings.ai.connections.add': 'Add Connection',
    'settings.ai.performance.title': 'Performance',
    'settings.ai.performance.description': 'Cost and caching options.',
    'settings.ai.performance.extendedContext.label': 'Extended Context (1M)',
    'settings.ai.performance.extendedContext.description': 'Use 1M token context window for Opus 4.6. Disable to use 200K and conserve usage limits.',
    'settings.ai.performance.extendedPromptCache.label': 'Extended prompt cache (1 hour)',
    'settings.ai.performance.extendedPromptCache.description': 'Cache prompts for 1 hour instead of 5 minutes. Only applies to Claude models via Anthropic API. Reduces cost for long sessions but increases cache write cost.',

    'topbar.toggleSidebar': 'Toggle Sidebar',
    'topbar.craftMenu': 'XClaw menu',
    'topbar.newChat': 'New Chat',
    'topbar.newWindow': 'New Window',
    'topbar.settings': 'Settings',
    'topbar.settingsDialog': 'Settings...',
    'topbar.help': 'Help',
    'topbar.helpAndDocs': 'Help & Documentation',
    'topbar.keyboardShortcuts': 'Keyboard Shortcuts',
    'topbar.debug': 'Debug',
    'topbar.checkForUpdates': 'Check for Updates',
    'topbar.installUpdate': 'Install Update',
    'topbar.toggleDevTools': 'Toggle DevTools',
    'topbar.quitXClaw': 'Quit XClaw',
    'topbar.goBack': 'Back',
    'topbar.goForward': 'Forward',
    'topbar.addPanelMenu': 'Add panel menu',
    'topbar.newSessionInPanel': 'New Session in Panel',
    'topbar.newBrowserWindow': 'New Browser Window',
    'topbar.allDocumentation': 'All Documentation',
    'topbar.sources': 'Sources',
    'topbar.skills': 'Skills',
    'topbar.statuses': 'Statuses',
    'topbar.permissions': 'Permissions',
    'topbar.automations': 'Pulse',
    'chat.share.openInBrowser': 'Open in Browser',
    'chat.share.copyLink': 'Copy Link',
    'chat.share.updateShare': 'Update Share',
    'chat.share.stopSharing': 'Stop Sharing',
    'chat.share.shareOnline': 'Share Online',
    'multiSelect.entity.session': 'Session',
    'multiSelect.entity.source': 'Source',
    'multiSelect.entity.skill': 'Skill',
    'multiSelect.use': 'Use',
    'multiSelect.toToggle': 'to toggle',
    'multiSelect.forRange': 'for range',
    'multiSelect.press': 'Press',
    'multiSelect.toClearSelection': 'to clear selection',
    'multiSelect.changeStatus': 'Change Status',
    'multiSelect.setLabels': 'Set Labels',
    'multiSelect.archive': 'Archive',
    'batchSessionMenu.unflagAll': 'Unflag All',
    'batchSessionMenu.flagAll': 'Flag All',
    'fileViewer.failedToLoadFile': 'Failed to load file',
    'fileViewer.noFileSelected': 'No file selected',
    'fileViewer.clickPathHint': 'Click a file path in the chat to view it here',
    'fileViewer.loadingContent': 'Loading content...',
    'fileViewer.errorLoadingFile': 'Error loading file',
    'permissionRequest.title': 'Permission required',
    'permissionRequest.tool': 'Tool',
    'permissionRequest.allow': 'Allow',
    'permissionRequest.alwaysAllow': 'Always Allow',
    'permissionRequest.deny': 'Deny',
    'permissionRequest.tip': '"Always Allow" remembers this command for the session',
    'adminApproval.title': 'Admin approval required',
    'adminApproval.installingNeedsApproval': 'Installing {appName} needs your Mac admin approval.',
    'adminApproval.passwordPrompt': 'You’ll see your regular macOS password/Touch ID prompt.',
    'adminApproval.why': 'Why',
    'adminApproval.impact': 'Impact',
    'adminApproval.approve': 'Approve',
    'adminApproval.rememberAria': 'Remember this exact command for {minutes} minutes',
    'adminApproval.rememberForMinutes': 'Remember for {minutes} min',
    'searchableModel.searchPlaceholder': 'Search models...',
    'searchableModel.noModelsFound': 'No models found',
    'tableOfContents.noHeadings': 'No headings',
    'sessionStatusMenu.filterPlaceholder': 'Filter statuses...',
    'sessionStatusMenu.noStatusFound': 'No status found',
    'sessionStatusMenu.unarchive': 'Unarchive',
    'labelValuePopover.datePlaceholder': 'tomorrow, next friday...',
    'labelValuePopover.selectDate': 'Select date',
    'labelValuePopover.numberPlaceholder': 'Enter number...',
    'labelValuePopover.valuePlaceholder': 'Enter value...',
    'labelValuePopover.remove': 'Remove',
    'labelMenu.addNewLabel': 'Add New Label',
    'labelMenu.states': 'States',

    'workspaceSwitcher.selectWorkspace': 'Select workspace',
    'workspaceSwitcher.workspace': 'Workspace',
    'workspaceSwitcher.openInNewWindow': 'Open in new window',
    'workspaceSwitcher.addWorkspace': 'Add Workspace...',
    'workspaceSwitcher.createdWorkspace': 'Created workspace "{name}"',

    'sidebarMenu.openInNewWindow': 'Open in New Window',
    'sidebarMenu.markAllRead': 'Mark All Read',
    'sidebarMenu.configureStatuses': 'Configure Statuses',
    'sidebarMenu.addNewLabel': 'Add New Label',
    'sidebarMenu.editLabels': 'Edit Labels',
    'sidebarMenu.deleteLabel': 'Delete Label',
    'sidebarMenu.editViews': 'Edit Views',
    'sidebarMenu.deleteView': 'Delete View',
    'sidebarMenu.addSource': 'Add Source',
    'sidebarMenu.learnMoreSources': 'Learn More about Sources',
    'sidebarMenu.learnMoreApis': 'Learn More about APIs',
    'sidebarMenu.learnMoreMcp': 'Learn More about MCP',
    'sidebarMenu.learnMoreLocalFolders': 'Learn More about Local Folders',
    'sidebarMenu.addSkill': 'Add Skill',
    'sidebarMenu.addAutomation': 'Add Pulse',
    'sidebarMenu.learnMoreAutomations': 'Learn More about Pulse',

    'shell.newSession': 'New Session',
    'shell.inbox': 'Inbox',
    'shell.plugins': 'Skills & Apps',
    'shell.allSessions': 'All Sessions',
    'shell.flagged': 'Flagged',
    'shell.archived': 'Archived',
    'shell.labels': 'Labels',
    'shell.views': 'Views',
    'shell.sources': 'Sources',
    'shell.skills': 'Skills',
    'shell.automations': 'Pulse',
    'shell.automationRules': 'Automations',
    'shell.channels': 'Channels',
    'shell.files': 'Files',
    'shell.workbench': 'Workbench',
    'shell.settings': 'Settings',
    'shell.whatsNew': "What's New",
    'shell.apis': 'APIs',
    'shell.mcps': 'MCPs',
    'shell.localFolders': 'Local Folders',
    'shell.scheduled': 'Scheduled',
    'shell.eventBased': 'Event-based',
    'shell.agentic': 'Agentic',
    'shell.searchStatusesAndLabels': 'Search statuses & labels...',
    'shell.filterChats': 'Filter Chats',
    'shell.statuses': 'Statuses',
    'shell.group': 'Group',
    'shell.date': 'Date',
    'shell.status': 'Status',
    'shell.search': 'Search',
    'shell.noLabelsConfigured': 'No labels configured',
    'shell.noMatchingStatusesOrLabels': 'No matching statuses or labels',
    'shell.allSkills': 'All Skills',
    'shell.allAutomations': 'All Pulses',
    'shell.altClickToExclude': 'Alt click to exclude',
    'shell.addSource': 'Add Source',
    'shell.addSkill': 'Add Skill',
    'shell.addAutomation': 'Add Pulse',
    'shell.editFile': 'Edit File',
    'shell.failedCreateBrowserWindow': 'Failed to create browser window',
    'shell.failedDeleteSource': 'Failed to delete source',
    'shell.failedDeleteSkill': 'Failed to delete skill',
    'shell.deletedSource': 'Deleted source',
    'shell.deletedSkill': 'Deleted skill: {slug}',
    'sessionActions.flagged': 'Session flagged',
    'sessionActions.flaggedDescription': 'Added to your flagged items',
    'sessionActions.unflagged': 'Flag removed',
    'sessionActions.unflaggedDescription': 'Removed from flagged items',
    'sessionActions.archived': 'Session archived',
    'sessionActions.archivedDescription': 'Moved to archive',
    'sessionActions.restored': 'Session restored',
    'sessionActions.restoredDescription': 'Moved from archive',
    'sessionActions.deleted': 'Session deleted',
    'shell.scheduledInfo': 'Scheduling requires your machine to be running. It can be locked, but must be powered on.',
    'shell.deleteAutomationTitle': 'Delete Pulse',
    'shell.deleteAutomationDescription': 'Are you sure you want to delete {name}? This will remove the Pulse from your automations.json configuration.',
    'status.backlog': 'Backlog',
    'status.todo': 'Todo',
    'status.needsReview': 'Needs Review',
    'status.done': 'Done',
    'status.cancelled': 'Cancelled',

    'main.source': 'Source',
    'main.skill': 'Skill',
    'main.noSourcesConfigured': 'No sources configured',
    'main.noSkillsConfigured': 'No skills configured',
    'main.noAutomationsConfigured': 'No Pulses configured',
    'main.noSessionSelected': 'No session selected',
    'main.selectConversation': 'Select a conversation to get started',

    'channels.empty.title': 'No channel patterns yet',
    'channels.empty.description': 'Built-in IM channel patterns will appear here.',
    'channels.notFound': 'Channel not found',
    'channels.copied': 'Pattern copied to clipboard',
    'channels.saved': 'Channel configuration saved',
    'channels.resetToSaved': 'Reverted unsaved changes',
    'channels.overview.title': 'IM Channels',
    'channels.overview.tagline': 'A three-pane catalog for WeChat, Feishu, Slack, Discord, Telegram, and similar integration patterns.',
    'channels.overview.description': 'Pick a platform to configure only the minimum fields needed for connection.',
    'channels.overview.connectedDescription': 'Review the channels you have already configured, verified, or connected, then jump back into setup when needed.',
    'channels.backToAll': 'All Channels',
    'channels.tab.discover': 'Discover',
    'channels.tab.connected': 'Connected',
    'channels.connected.loading': 'Checking connected channels...',
    'channels.connected.emptyTitle': 'No connected channels yet',
    'channels.connected.emptyDescription': 'Connect a channel from Discover first. Once a platform has saved local setup, it will appear here.',
    'channels.connected.openDiscover': 'Go to Discover',
    'channels.connectionStyle': 'Connection style',
    'channels.exactSessionSupport': 'Exact session callback',
    'channels.recommended': 'Recommended use',
    'channels.requiredFields': 'Required fields',
    'channels.localConfig.title': 'Minimal channel setup',
    'channels.localConfig.description': 'Only keep the current required fields. Advanced routing, previews, and implementation notes are hidden for now.',
    'channels.status.configured': 'Configured',
    'channels.status.configuredDescription': 'Required fields are complete. This channel is ready for real backend wiring.',
    'channels.status.draft': 'Draft',
    'channels.status.draftDescription': 'Configuration has started, but some required fields are still missing.',
    'channels.status.disabledDescription': 'This channel is saved locally but not enabled yet.',
    'channels.coverage.full': 'Exact session callback',
    'channels.coverage.partial': 'Partial callback',
    'channels.coverage.none': 'No exact callback yet',
    'channels.methodKind.qr': 'QR pairing',
    'channels.methodKind.deepLink': 'Deep link',
    'channels.methodKind.webLink': 'Web link',
    'channels.methodKind.invite': 'Invite link',
    'channels.methodKind.oauth': 'OAuth / console',
    'channels.methodKind.webhook': 'Webhook / callback',
    'channels.section.coverage': 'Connection overview',
    'channels.section.coverageDescription': 'What this platform supports in the current frontend model.',
    'channels.section.methods': 'Built-in link methods',
    'channels.section.methodsDescription': 'Each card below is a frontend-ready pattern you can wire to real credentials later.',
    'channels.section.overviewMethodsDescription': 'These are the connection patterns already modeled in the frontend.',
    'channels.section.basicConfig': 'Basic setup',
    'channels.section.basicConfigDescription': 'Turn the channel on and give this integration a clear display name.',
    'channels.section.platformConfig': 'Platform setup',
    'channels.section.platformConfigDescription': 'Only keep the minimum fields needed to connect this platform.',
    'channels.section.connected': 'Connected channels',
    'channels.section.connectedDescription': 'Channels with saved local setup are collected here, with the current connection status kept visible.',
    'channels.guidedSetup': 'This platform uses a guided connection flow, so there are no manual fields here right now.',
    'channels.savedFailed': 'Failed to save channel configuration.',
    'channels.connect': 'Connect',
    'channels.connected': 'Channel connected',
    'channels.verified': 'Channel credentials verified',
    'channels.connectFailed': 'Failed to connect channel.',
    'channels.wechat.serviceTitle': 'Connection service',
    'channels.wechat.serviceDescription': 'Point XClaw to the local service that exposes the WeChat QR endpoints.',
    'channels.wechat.serviceFieldDescription': 'Usually the local controller or bridge address that serves /api/v1/channels/wechat/*.',
    'channels.wechat.scanTitle': 'WeChat QR setup',
    'channels.wechat.scanDescription': 'Generate a QR code, scan it in WeChat, then wait for the account to be handed back.',
    'channels.wechat.missingGatewayUrl': 'Enter a local service URL first.',
    'channels.wechat.connectFailed': 'WeChat connection failed.',
    'channels.wechat.timeout': 'Timed out waiting for the WeChat scan result.',
    'channels.wechat.connected': 'WeChat connected',
    'channels.wechat.connectedDescription': 'The scanned account has been saved locally and can be reused later.',
    'channels.wechat.connectedLocalOnly': 'WeChat account scanned and saved locally.',
    'channels.wechat.accountIdLabel': 'Account ID',
    'channels.wechat.qrAlt': 'WeChat QR code',
    'channels.wechat.scanning': 'Scan this QR code in WeChat',
    'channels.wechat.scanHint': 'Keep this page open while the local service waits for the account handoff.',
    'channels.wechat.waitingGateway': 'Waiting for the local service to come online...',
    'channels.wechat.loadingQr': 'Requesting a QR code...',
    'channels.wechat.connecting': 'Binding the scanned account...',
    'channels.wechat.retry': 'Retry',
    'channels.wechat.idleDescription': 'Use XClaw’s built-in local bridge to generate a QR code and pair a personal WeChat account.',
    'channels.wechat.idleUnavailableDescription': 'XClaw could not prepare the built-in WeChat service yet. Try again in a moment or check the logs.',
    'channels.wechat.scanAction': 'Scan QR code',
    'channels.wechat.scanUnavailableAction': 'WeChat service unavailable',
    'channels.wechat.scanPendingAction': 'Waiting for built-in service',
    'channels.wechat.gatewayOnline': 'Local gateway reachable',
    'channels.wechat.gatewayOffline': 'Local gateway unavailable',
    'channels.wechat.gatewayChecking': 'Checking the local service...',
    'channels.wechat.gatewayOnlineDescription': 'XClaw can reach the active WeChat bridge for QR setup and account handoff.',
    'channels.wechat.gatewayOfflineDescription': 'XClaw could not reach the active WeChat bridge. Let the app finish starting or check the logs before scanning.',
    'channels.wechat.gatewayCheckingDescription': 'XClaw is checking whether the active WeChat bridge is reachable.',
    'channels.wechat.gatewayDefaultUnavailable': 'XClaw could not reach the built-in WeChat bridge yet.',
    'channels.wechat.gatewayCustomUnavailable': 'XClaw could not connect to the configured local service address.',
    'channels.wechat.gatewayMissingServiceTitle': 'Built-in WeChat service is unavailable',
    'channels.wechat.gatewayMissingServiceDescription': 'XClaw normally starts the local WeChat bridge by itself. If this warning stays here, the built-in service failed to respond. Wait a moment, or point Advanced setup at another compatible local bridge for debugging.',
    'channels.wechat.gatewayConnectFailed': 'WeChat account saved locally, but the gateway still needs attention.',
    'channels.wechat.gatewayConnectFailedDescription': 'The QR scan succeeded and the account was saved locally, but the gateway did not finish binding it to XClaw.',
    'channels.wechat.savedLocallyTitle': 'WeChat account saved locally',
    'channels.wechat.savedLocallyDescription': 'A scanned WeChat account is already stored on this machine, but XClaw has not confirmed the gateway binding yet.',
    'channels.section.credentials': 'Credentials',
    'channels.section.credentialsDescription': 'Store the platform identifiers and secrets that power this channel.',
    'channels.section.routing': 'Routing and callbacks',
    'channels.section.routingDescription': 'Control how links are generated, how callbacks are previewed, and where messages should land.',
    'channels.section.preview': 'Method preview',
    'channels.section.previewDescription': 'See the selected link pattern with your current config values filled in.',
    'channels.section.highlights': 'Highlights',
    'channels.section.highlightsDescription': 'Good defaults and caveats extracted into product language.',
    'channels.section.notes': 'Implementation notes',
    'channels.section.notesDescription': 'Things we should keep in mind when we wire the backend later.',
    'channels.section.docs': 'References',
    'channels.section.docsDescription': 'Open the official console or setup docs when you need to create credentials.',
    'channels.section.platforms': 'Built-in channel catalog',
    'channels.section.platformsDescription': 'Pick a platform in the middle list, or jump in from the cards below.',
    'channels.section.connectionStatus': 'Connection status',
    'channels.section.connectionStatusDescription': 'Check whether this channel is ready, and only open advanced setup if you need callback or debugging details.',
    'channels.section.conversations': 'Bound conversations',
    'channels.section.conversationsDescription': 'Recent channel threads that XClaw has already mapped into workspace sessions.',
    'channels.section.advanced': 'Advanced setup',
    'channels.section.advancedDescription': 'Only open this if you need custom callback URLs, a custom local bridge address, or troubleshooting details.',
    'channels.section.nextStep': 'Next step',
    'channels.advanced.show': 'Show advanced setup',
    'channels.advanced.hide': 'Hide advanced setup',
    'channels.action.addBot': 'Add bot',
    'channels.action.addBotDescription': 'Open the invite link to finish the bot installation on the platform side.',
    'channels.action.openPlatform': 'Open platform',
    'channels.action.openPlatformDescription': 'Open the platform page to finish the remaining setup step.',
    'channels.conversations.unread': 'Unread',
    'channels.conversations.lastInbound': 'Last inbound',
    'channels.conversations.open': 'Open chat',
    'channels.conversations.emptyTitle': 'No conversations yet',
    'channels.conversations.emptyDescription': 'Once a WeChat message reaches XClaw, the mapped workspace session will appear here.',
    'channels.conversations.emptyDescriptionGeneric': 'Once this channel delivers a message into XClaw, the mapped workspace session will appear here.',
    'channels.previewValue': 'Preview',
    'channels.previewEmpty': 'Select a method to preview the generated link or endpoint.',
    'channels.field.enabled': 'Enable this channel',
    'channels.field.enabledDescription': 'Only enabled channels should be considered by future runtime bindings.',
    'channels.field.displayName': 'Display name',
    'channels.field.displayNameDescription': 'Shown in the right panel header and used as the human-readable integration name.',
    'channels.field.connectionMethod': 'Connection method',
    'channels.field.connectionMethodDescription': 'Pick the built-in link pattern you want this channel to drive.',
    'channels.field.gatewayUrl': 'Gateway URL',
    'channels.field.gatewayUrlDescription': 'Leave this empty for the built-in bridge. Only change it when you are debugging against another local service.',
    'channels.field.accountId': 'Account ID',
    'channels.field.accountIdDescription': 'The account identifier returned after QR pairing completes.',
    'channels.field.appId': 'App ID',
    'channels.field.appIdDescription': 'Feishu app identifier from the official open platform.',
    'channels.field.appSecret': 'App Secret',
    'channels.field.appSecretDescription': 'Used with the app ID to fetch tenant tokens and subscribe to events.',
    'channels.field.openChatId': 'Open Chat ID',
    'channels.field.openChatIdDescription': 'Required when you want to jump back to the exact bot conversation.',
    'channels.field.inviteToken': 'Invite token',
    'channels.field.inviteTokenDescription': 'Used to generate a direct group-join link for rollout or community entry.',
    'channels.field.botToken': 'Bot token',
    'channels.field.botTokenDescription': 'Platform bot token used for message operations and channel access.',
    'channels.field.signingSecret': 'Signing secret',
    'channels.field.signingSecretDescription': 'Verifies incoming platform events against your callback endpoint.',
    'channels.field.teamId': 'Team ID',
    'channels.field.teamIdDescription': 'Required for native client and web fallback links.',
    'channels.field.botUserId': 'Bot User ID',
    'channels.field.botUserIdDescription': 'Used together with the workspace/team ID to jump into the target bot conversation.',
    'channels.field.applicationId': 'Application ID',
    'channels.field.applicationIdDescription': 'Discord application/client ID used to generate the invite URL.',
    'channels.field.serverId': 'Server ID',
    'channels.field.serverIdDescription': 'Optional target guild/server reference for later routing and filtering.',
    'channels.field.botUsername': 'Bot username',
    'channels.field.botUsernameDescription': 'Public bot handle without the leading @.',
    'channels.field.chatId': 'Chat ID',
    'channels.field.chatIdDescription': 'Optional target chat or group identifier used for callback mapping.',
    'channels.field.startPayload': 'Start payload',
    'channels.field.startPayloadDescription': 'Used to attach a lightweight context string to the bot deeplink.',
    'channels.field.webhookUrl': 'Webhook URL',
    'channels.field.webhookUrlDescription': 'Optional incoming webhook or event endpoint used during testing.',
    'channels.field.callbackBaseUrl': 'Callback base URL',
    'channels.field.callbackBaseUrlDescription': 'Used to build preview URLs for local callback endpoints and webhook patterns.',
    'channels.field.publicBaseUrl': 'Public Base URL',
    'channels.field.publicBaseUrlDescription': 'Public HTTPS address that forwards requests to this XClaw instance so platform callbacks can reach it.',
    'channels.field.notes': 'Notes',
    'channels.field.notesDescription': 'Optional notes for rollout, environment differences, or mapping decisions.',
    'channels.field.notesPlaceholder': 'Write down environment-specific decisions, tenant notes, or routing reminders...',
    'channels.status.idle': 'Not connected',
    'channels.status.idleDescription': 'Save your credentials, then run connect to verify them against the platform.',
    'channels.status.saved': 'Saved',
    'channels.status.savedDescription': 'Configuration saved. Run connect again to verify the current credentials.',
    'channels.status.verified': 'Verified',
    'channels.status.connected': 'Connected',
    'channels.status.error': 'Error',
    'channels.lastActivity': 'Last activity',
    'channels.connection.runtime': 'Local runtime',
    'channels.connection.callbackPath': 'Callback path',
    'channels.connection.localCallbackUrl': 'Local callback URL',
    'channels.connection.callbackUrl': 'Public callback URL',
    'channels.connection.openUrl': 'Open URL',
    'channels.connection.inviteUrl': 'Invite URL',
    'channels.open': 'Open',
    'channels.message.slackVerifiedWithCallback': 'Slack credentials verified. Add the callback URL in Slack Event Subscriptions.',
    'channels.message.slackVerifiedNeedsPublicBaseUrl': 'Slack credentials verified. Add a public base URL before wiring Slack callbacks.',
    'channels.message.discordVerified': 'Discord credentials verified. Use the invite URL to install the bot into a server.',
    'channels.message.feishuVerifiedWithCallback': 'Feishu credentials verified. Add the callback URL in Feishu event subscriptions.',
    'channels.message.feishuVerifiedNeedsPublicBaseUrl': 'Feishu credentials verified. Add a public base URL before wiring callback delivery.',
    'channels.message.feishuLongConnectionStarting': 'Feishu credentials verified. XClaw is starting the built-in long connection.',
    'channels.message.feishuLongConnectionConnected': 'Feishu long connection connected. Messages can reach this XClaw instance without a public callback URL.',
    'channels.message.feishuLongConnectionFailed': 'Feishu credentials verified. XClaw could not start the built-in long connection.',
    'channels.message.telegramVerifiedNeedsPublicBaseUrl': 'Telegram bot token verified. Add a public base URL to enable webhooks.',
    'channels.message.telegramConnected': 'Telegram bot token verified and webhook configured.',
    'channels.message.slackWebhookReceived': 'Slack webhook received by XClaw.',
    'channels.message.feishuWebhookReceived': 'Feishu webhook received by XClaw.',
    'channels.message.telegramWebhookReceived': 'Telegram webhook received by XClaw.',
    'channels.message.wechatSaved': 'WeChat account saved locally.',
    'channels.warning.publicBaseUrlInvalid': 'Public base URL is invalid.',
    'channels.warning.publicBaseUrlRequired': 'Add a public base URL so the platform can reach your callback endpoint.',
    'channels.warning.runtimeUnavailable': 'Callback runtime is not available, so incoming webhook delivery is still blocked.',
    'channels.warning.feishuLongConnectionUnavailable': 'Built-in Feishu long connection is unavailable: {reason}',
    'channels.warning.discordInviteRequired': 'Install the bot with the invite URL below to finish the Discord-side setup.',
    'channels.warning.runtimeUnavailableWithReason': 'Channel webhook runtime is unavailable: {reason}',

    'sources.type.api': 'API',
    'sources.type.mcp': 'MCP',
    'sources.type.local': 'Local',
    'sources.status.connected': 'Connected',
    'sources.status.authRequired': 'Auth Required',
    'sources.status.disconnected': 'Disconnected',
    'sources.status.notTested': 'Not Tested',
    'sources.status.disabled': 'Disabled',
    'sources.filter.api': 'API',
    'sources.filter.mcp': 'MCP',
    'sources.filter.localFolder': 'local folder',
    'sources.empty.filtered': 'No {type} sources configured.',
    'sources.empty.none': 'No sources configured.',
    'sources.description': 'Sources connect your agent to external data — MCP servers, REST APIs, and local folders.',
    'sources.addSource': 'Add Source',
    'mcpWorkspace.title': 'MCP',
    'mcpWorkspace.tagline': 'Model Context Protocol servers for tools, context, and external actions.',
    'mcpWorkspace.addMcp': 'Add MCP',
    'mcpWorkspace.backToAll': 'All MCP Servers',
    'mcpWorkspace.stats.total': 'Installed',
    'mcpWorkspace.stats.connected': 'Connected',
    'mcpWorkspace.stats.attention': 'Needs Attention',
    'mcpWorkspace.stats.local': 'Local Servers',
    'mcpWorkspace.tab.all': 'All',
    'mcpWorkspace.tab.attention': 'Needs Attention',
    'mcpWorkspace.tab.connected': 'Connected',
    'mcpWorkspace.tab.local': 'Local',
    'mcpWorkspace.tab.remote': 'Remote',
    'mcpWorkspace.empty.title': 'No MCP sources yet',
    'mcpWorkspace.empty.description': 'Connect remote MCP servers or local stdio servers so your agent can use external tools.',
    'mcpWorkspace.empty.filtered': 'No MCP sources in this view.',
    'mcpWorkspace.empty.filteredDescription': 'Try another tab or add a new MCP source for this workspace.',
    'mcpWorkspace.cardMenu': 'MCP actions',
    'mcpWorkspace.openDetails': 'Open Details',
    'mcpWorkspace.authenticate': 'Authenticate',
    'mcpWorkspace.openSettings': 'Open Settings',
    'mcpWorkspace.inspect': 'Inspect',
    'mcpWorkspace.authBearer': 'Bearer',
    'mcpWorkspace.authNone': 'No auth',
    'mcpWorkspace.serverUrl': 'Server URL',
    'mcpWorkspace.command': 'Command',
    'mcpWorkspace.localServersDisabled': 'Local MCP servers are disabled',
    'mcpWorkspace.localServersDisabledDescription': 'Enable local MCP servers in Workspace settings to use stdio-based MCP tools.',

    'skills.empty.none': 'No skills configured',
    'skills.empty.addFirst': 'No skills installed yet',
    'skills.empty.browseStore': 'Browse the Discover tab to find and install skills.',
    'skills.description': 'Skills are reusable instructions that teach your agent specialized behaviors.',
    'skills.addSkill': 'Add Skill',
    'skills.discover': 'Discover',
    'skills.installed': 'Installed',
    'skills.install': 'Install',
    'skills.installedState': 'Installed',
    'skills.seeAll': 'See All',
    'skills.showLess': 'Show Less',
    'skills.backToAll': 'All Skills',
    'skills.backToStore': 'Skill Store',
    'skills.cardMenu': 'Skill actions',
    'skills.openDetails': 'Open Details',
    'skills.runSkill': 'Run',
    'skills.showInFinder': 'Show In Finder',
    'skills.sourceLabel': 'from',
    'skills.source.claudeCode': 'Claude Code',
    'skills.source.cursor': 'Cursor',
    'skills.source.global': 'global',
    'skills.source.workspace': 'workspace',
    'skills.source.project': 'project',
    'skills.category.featured': 'Featured',
    'skills.category.productivity': 'Productivity',
    'skills.category.design': 'Design',
    'skills.category.contentCreation': 'Content Creation',
    'skills.category.development': 'Development',
    'annotations.followUp.title': 'Follow-up',
    'annotations.followUp.placeholder': 'Add comments the agent should consider in the next turn…',
    'annotations.followUp.moreSubmitActions': 'More submit actions',
    'chat.escapeInterruptPrefix': 'Press',
    'chat.escapeInterruptSuffix': 'again to interrupt',
    'chat.empty.changePrompt': 'What would you like to change?',
    'chat.empty.changeHint': "Just describe it — I'll handle the rest",
    'authRequest.status.connected': '{name} Connected',
    'authRequest.status.cancelled': '{name} Cancelled',
    'authRequest.status.failed': '{name} Failed',
    'authRequest.signedInAs': 'Signed in as {email}',
    'authRequest.workspace': 'Workspace: {name}',
    'authRequest.enterValue': 'Enter {label}',
    'authRequest.signInWith': 'Sign in with {name}',
    'authRequest.credentialsEncryptedAtRest': 'Credentials are encrypted at rest',
    'resetApp.title': 'Reset App',
    'resetApp.description': 'This will permanently delete:',
    'resetApp.deleteWorkspaces': 'All workspaces and their settings',
    'resetApp.deleteCredentials': 'All credentials and API keys',
    'resetApp.deletePreferences': 'All preferences and session data',
    'resetApp.backupFirst': 'Back up any important data first!',
    'resetApp.cannotUndo': 'This action cannot be undone.',
    'resetApp.solvePrompt': 'To confirm, solve:',
    'resetApp.enterAnswer': 'Enter answer',
    'files.loadingPreview': 'Loading preview...',
    'files.previewUnavailable': 'Preview not available',
    'files.previewUnavailableDescription': 'This file type does not have an in-app preview yet. You can still open it with the system app.',
    'overlay.toolFailed': 'Tool Failed',
    'overlay.copyHtml': 'Copy HTML',
    'overlay.pdfPreview': 'PDF Preview',
    'overlay.htmlPreview': 'HTML Preview',
    'overlay.loadingHtml': 'Loading HTML...',
    'overlay.failedReadPdfFile': 'Failed to read PDF file',
    'overlay.failedReadHtmlFile': 'Failed to read HTML file',
    'overlay.failedReadDataFile': 'Failed to read data file',
    'overlay.failedParseDataFile': 'Failed to parse data file as JSON',
    'overlay.invalidDatatableData': 'File does not contain valid datatable data',
    'overlay.invalidSpreadsheetData': 'File does not contain valid spreadsheet data',
    'overlay.failedLoadImageCount': 'Failed to load {count} image(s)',
    'overlay.inputParams': 'Input Params',
    'overlay.code': 'Code',
    'overlay.read': 'Read',
    'overlay.write': 'Write',
    'overlay.readFailed': 'Read Failed',
    'overlay.writeFailed': 'Write Failed',
    'overlay.loadingImage': 'Loading image...',
    'overlay.loadingPdf': 'Loading PDF...',
    'overlay.imagePreview': 'Image preview',
    'terminal.copyCommand': 'Copy command',
    'terminal.copyOutput': 'Copy output',
    'turnCard.viewFileChanges': 'View file changes',
    'turnCard.viewTurnDetails': 'View turn details',
    'turnCard.acceptPlan': 'Accept Plan',
    'turnCard.accept': 'Accept',
    'turnCard.acceptAndSendFollowUps': 'Accept & Send Follow-ups',
    'turnCard.branchOptions': 'Branch options',
    'turnCard.branchFromThisMessage': 'Branch From This message',
    'turnCard.branchDescription': 'Explore an alternate direction without disrupting this conversation\'s flow.',
    'turnCard.streaming': 'Streaming...',
    'turnCard.todoList': 'Todo List',
    'turnCard.thinking': 'Thinking...',
    'turnCard.preparingResponse': 'Preparing response...',
    'skills.section.description': 'Description',
    'skills.section.source': 'Source',
    'skills.section.screenshots': 'Screenshots',

    'files.home': 'Home',
    'files.desktop': 'Desktop',
    'files.downloads': 'Downloads',
    'files.workspace': 'Workspace',
    'files.skills': 'Skills',
    'files.searchPlaceholder': 'Search',
    'files.loading': 'Loading files...',
    'files.loadError': 'Failed to load directory',
    'files.empty': 'No files found',
    'files.name': 'Name',
    'files.modified': 'Date Modified',
    'files.size': 'Size',
    'files.items': '{count} items',
    'files.selected': '1 selected',
    'files.noneSelected': '0 selected',
    'files.drop.prompt': 'Release to move files here',
    'files.drop.description': 'Dropped files will be moved into the current folder.',
    'files.drop.importing': 'Moving files...',
    'files.drop.success': 'Moved {count} file(s) into the current folder.',
    'files.drop.partial': 'Moved {count} file(s) and skipped {skipped}.',
    'files.drop.failed': 'Failed to move dropped files into the current folder.',
    'files.drop.noLocalFiles': 'No usable local file paths were found in this drop.',

    'sourceMenu.showInFileManager': 'Show in {name}',
    'sourceMenu.deleteSource': 'Delete Source',
    'skillMenu.showInFileManager': 'Show in {name}',
    'skillMenu.deleteSkill': 'Delete Skill',

    'automationMenu.enable': 'Enable',
    'automationMenu.disable': 'Disable',
    'automationMenu.runTest': 'Run Test',
    'automationMenu.editConfiguration': 'Edit Configuration',
    'automations.empty.none': 'No automation rules configured',
    'automations.empty.create': 'Create your first automation rule',
    'automations.empty.hint': 'Click "New Rule" above to set up scheduled tasks, event triggers, or prompt automations.',
    'automations.empty.examples': 'Here are a few examples to get you started.',
    'automations.paused': 'Paused',
    'automations.pageDescription': 'Create automation rules that run your tasks automatically and feed the Pulse briefing.',
    'automations.pulsePreviewDescription': 'Briefing shows the Pulse daily summary: cards, standing asks, and follow-up suggestions generated from your workspace context.',
    'automations.rulesDetailDescription': 'Automation Rules shows trigger conditions, execution history, and controls for the workflows behind Pulse.',
    'automations.description': 'Automation Rules run actions when events occur — execute commands on schedules, react to label changes, or trigger prompts automatically.',
    'automations.addAutomation': 'Add rule',
    'automations.new': 'New Pulse',
    'automations.newRule': 'New Rule',
    'automations.backToAll': 'All Rules',
    'automations.optimize': 'Tune Briefing',
    'automations.viewMode.pulse': 'Briefing',
    'automations.viewMode.rules': 'Automation Rules',
    'automations.viewRules': 'Open rules',
    'automations.viewRulesCount': 'View {count} automation rules',
    'automations.tab.all': 'All',
    'automations.tab.enabled': 'Enabled',
    'automations.tab.disabled': 'Disabled',
    'automations.searchPlaceholder': 'Search rules...',
    'automations.noneFound': 'No rules found',
    'automations.noneConfigured': 'No automation rules configured.',
    'automations.clearSearch': 'Clear search',
    'automations.prompt': 'Prompt',
    'automations.webhook': 'Webhook',
    'automations.lastRan': 'Last ran {time}',
    'automations.example.emailDigest': 'Daily Email Digest',
    'automations.example.emailDigestDesc': 'Summarize unread emails from the last 24h by sender and priority; highlight action items.',
    'automations.example.birthday': 'Birthday & Anniversary Reminder',
    'automations.example.birthdayDesc': 'Check upcoming birthdays and anniversaries; suggest gift ideas based on past preferences.',
    'automations.example.creative': 'Creative Project Tracker',
    'automations.example.creativeDesc': 'Monitor progress on side projects and suggest next steps when work gets stuck.',
    'automations.example.sprint': 'Weekly Sprint Summary',
    'automations.example.sprintDesc': 'Every Friday, summarize completed vs. planned tasks, calculate velocity, and forecast next sprint capacity.',
    'automations.example.health': 'System Health Monitor',
    'automations.example.healthDesc': 'Run a daily check on critical business tools and services, and alert on downtime or performance issues.',
    'automations.example.expense': 'Expense Report Compiler',
    'automations.example.expenseDesc': 'Gather receipts from email and apps, auto-categorize them, and draft monthly expense reports.',

    'batchAutomation.enabled': '{count} {noun} enabled',
    'batchAutomation.disabled': '{count} {noun} disabled',
    'batchAutomation.deleted': '{count} {noun} deleted',
    'batchAutomation.selected': '{count} {noun} selected',
    'batchAutomation.automation': 'rule',
    'batchAutomation.automations': 'rules',
    'batchAutomation.enableAll': 'Enable All',
    'batchAutomation.disableAll': 'Disable All',

    'workspace.failedCreate': 'Failed to create workspace',
    'workspace.close': 'Close',
    'workspace.addWorkspace': 'Add Workspace',
    'workspace.choiceDescription': 'Where your ideas meet the tools to make them happen.',
    'workspace.createNew': 'Create new',
    'workspace.startFresh': 'Start fresh with an empty workspace.',
    'workspace.openFolder': 'Open folder',
    'workspace.chooseExistingFolder': 'Choose an existing folder as workspace.',
    'workspace.back': 'Back',
    'workspace.createWorkspace': 'Create workspace',
    'workspace.createDescription': 'Enter a name and choose where to store your workspace.',
    'workspace.name': 'Workspace name',
    'workspace.namePlaceholder': 'My Workspace',
    'workspace.defaultLocation': 'Default location',
    'workspace.defaultLocationSubtitle': 'under .xclaw folder',
    'workspace.chooseLocation': 'Choose a location',
    'workspace.chooseLocationSubtitle': 'Pick a place to put your new workspace.',
    'workspace.creating': 'Creating...',
    'workspace.create': 'Create',
    'workspace.slugExists': 'A workspace named "{slug}" already exists',
    'workspace.chooseExistingTitle': 'Choose existing folder',
    'workspace.chooseExistingDescription': 'Choose any folder to use as workspace.',
    'workspace.noFolderSelected': 'No folder selected',
    'workspace.opening': 'Opening...',
    'workspace.open': 'Open',

    'headerMenu.openInNewWindow': 'Open in New Window',
    'headerMenu.learnMore': 'Learn More',
    'sessionMenu.unflag': 'Unflag',
    'sessionMenu.archive': 'Archive',
    'sessionMenu.unarchive': 'Unarchive',
    'sessionMenu.markAsUnread': 'Mark as Unread',
    'sessionMenu.rename': 'Rename',
    'sessionMenu.regenerateTitle': 'Regenerate Title',
    'sessionMenu.openInNewPanel': 'Open in New Panel',
    'sessionMenu.copyPath': 'Copy Path',
    'sessionMenu.share': 'Share',
    'sessionMenu.shared': 'Shared',
    'sessionMenu.status': 'Status',
    'sessionMenu.labels': 'Labels',
    'sessionMenu.flag': 'Flag',
    'sessionMenu.linkCopied': 'Link copied to clipboard',
    'sessionMenu.failedShare': 'Failed to share',
    'sessionMenu.pathCopied': 'Path copied to clipboard',
    'sessionMenu.titleRefreshed': 'Title refreshed',
    'sessionMenu.failedRefreshTitle': 'Failed to refresh title',
    'sessionMenu.unknownError': 'Unknown error',
    'shareMenu.openInBrowser': 'Open in Browser',
    'shareMenu.copyLink': 'Copy Link',
    'shareMenu.updateShare': 'Update Share',
    'shareMenu.stopSharing': 'Stop Sharing',
    'shareMenu.shareUpdated': 'Share updated',
    'shareMenu.failedUpdateShare': 'Failed to update share',
    'shareMenu.sharingStopped': 'Sharing stopped',
    'shareMenu.failedStopSharing': 'Failed to stop sharing',
    'sessionSearch.placeholder': 'Search titles and content...',
    'sessionSearch.close': 'Close search',
    'sessionSearch.loading': 'Loading…',
    'sessionSearch.results': '{count} results',
    'sessionList.noResultsInCurrentFilter': 'No results in current filter',
    'sessionList.noSessionsFound': 'No sessions found',
    'sessionList.searchedTitlesAndContent': 'Searched titles and message content',
    'sessionList.clearSearch': 'Clear search',
    'sessionList.sessions': 'Sessions',
    'sessionList.group.today': 'Today',
    'sessionList.group.yesterday': 'Yesterday',
    'sessionList.group.currentView': 'In Current View',
    'sessionList.group.otherConversations': 'Other Conversations',
    'sessionList.archivedEmptyTitle': 'No archived sessions',
    'sessionList.archivedEmptyDescription': 'Sessions you archive will appear here. Archive sessions to keep your list tidy while preserving conversations.',
    'sessionList.emptyTitle': 'No sessions yet',
    'sessionList.emptyDescription': 'Sessions with your agent appear here. Start one to get going.',
    'sessionList.renameSession': 'Rename Session',
    'sessionList.enterSessionName': 'Enter session name...',
    'sessionInfo.titleLabel': 'Title',
    'sessionInfo.untitledPlaceholder': 'Untitled',
    'sessionFiles.title': 'Session Files',
    'sessionFiles.viewInFileManager': 'View in {name}',
    'sessionFiles.showInFileManager': 'Show in {name}',
    'sessionFiles.emptyDescription': 'Files attached or created by this chat will appear here.',
    'sessionFiles.directory': 'Directory',
    'sessionFiles.expandAction': 'expand',
    'sessionFiles.revealAction': 'reveal',
    'sessionFiles.itemTooltip': '{path}\n{type}\n\nClick to {action}, double-click to open',
    'chat.sessionFallbackTitle': 'Session',
    'chat.sessionMissing': 'This session no longer exists',
    'mentionMenu.header': 'Mention files, skills, sources',
    'mentionMenu.noResults': 'No results',
    'mentionMenu.skill': 'Skill',
    'mentionMenu.source': 'Source',
    'skillInfo.notFound': 'Skill not found',
    'skillInfo.failedLoad': 'Failed to load skill',
    'skillInfo.deleted': 'Deleted skill: {name}',
    'skillInfo.failedDelete': 'Failed to delete skill',
    'skillInfo.installed': 'Installed skill: {name}',
    'skillInfo.failedInstall': 'Failed to install skill',
    'skillInfo.requiredSources': 'Required Sources',
    'skillInfo.permissionModes': 'Permission Modes',
    'skillInfo.permissionModesDescription': 'How "Always Allowed Tools" interacts with permission modes:',
    'skillInfo.permission.explore': 'Explore',
    'skillInfo.permission.exploreDescription': 'Blocked - write tools blocked regardless',
    'skillInfo.permission.ask': 'Ask to Edit',
    'skillInfo.permission.askDescription': 'Auto-approved - no prompts for allowed tools',
    'skillInfo.permission.auto': 'Auto',
    'skillInfo.permission.autoDescription': 'No effect - all tools already auto-approved',
    'skillInfo.noInstructions': '*No instructions provided.*',
    'labels.searchPlaceholder': 'Search labels...',
    'labels.emptyConfigured': 'No labels configured',
    'serverDirectoryBrowser.title': 'Select Server Directory',
    'serverDirectoryBrowser.enterPath': 'Enter path...',
    'serverDirectoryBrowser.go': 'Go',
    'serverDirectoryBrowser.failedListDirectory': 'Failed to list directory',
    'serverDirectoryBrowser.truncated': 'Showing the first {shown} folders out of {total}. Narrow the path if the folder you want is missing.',
    'serverDirectoryBrowser.noSubdirectories': 'No subdirectories. Use the path input above to navigate.',
    'serverDirectoryBrowser.symlink': 'symlink',
    'serverDirectoryBrowser.manualHint': 'Enter the full path on the server:',
    'serverDirectoryBrowser.wrongPlatformPath': 'This looks like a path from a different OS. Enter a path that exists on the server.',
    'sourceInfo.notFound': 'Source not found',
    'sourceInfo.failedLoad': 'Failed to load source',
    'sourceInfo.failedLoadTools': 'Failed to load tools',
    'sourceInfo.deleted': 'Deleted source: {name}',
    'sourceInfo.failedDelete': 'Failed to delete source',
    'sourceInfo.sourceDisabled': 'Source Disabled',
    'sourceInfo.sourceDisabledDescription': 'Local MCP servers are disabled in Settings > Advanced. Enable them to use this source.',
    'sourceInfo.connectionDescription.mcpStdio': 'Local command that spawns this MCP server.',
    'sourceInfo.connectionDescription.mcpRemote': 'Server URL and connection status.',
    'sourceInfo.connectionDescription.api': 'Base URL for API requests.',
    'sourceInfo.connectionDescription.local': 'Filesystem path for this source.',
    'sourceInfo.connectionDescription.default': 'Connection details.',
    'sourceInfo.permissionsDescription.mcp': 'Tool patterns allowed in Explore mode.',
    'sourceInfo.permissionsDescription.api': 'API endpoints allowed in Explore mode.',
    'sourceInfo.permissionsDescription.default': 'Access rules for Explore mode.',
    'sourceInfo.lastTested': 'Last Tested',
    'sourceInfo.toolsDescription': 'Operations exposed by this server.',
    'sourceInfo.documentationDescription': 'Context and guidelines for the agent.',
    'automationInfo.paused': 'Paused',
    'automationInfo.pausedDescription': 'This Pulse is turned off. Enable it to start running again.',
    'automationInfo.when': 'When',
    'automationInfo.whenDescription': 'What causes this Pulse to run',
    'automationInfo.onlyWhenMatching': 'Only when matching',
    'automationInfo.repeats': 'Repeats',
    'automationInfo.scheduleExpression': 'Schedule expression',
    'automationInfo.nextRuns': 'Next runs',
    'automationInfo.if': 'If',
    'automationInfo.ifDescription': 'Conditions that must pass before actions run',
    'automationInfo.then': 'Then',
    'automationInfo.thenDescription': '{count} {noun} to perform',
    'automationInfo.action': 'action',
    'automationInfo.actions': 'actions',
    'automationInfo.accessLevel': 'Access Level',
    'automationInfo.lastRuns': 'Last {count} runs',
    'automationInfo.rawConfig': 'Raw config',
    'automationInfo.noActivityYet': 'No activity yet.',
    'automationInfo.openSession': 'Open session',
    'automationInfo.retry': 'Retry',
    'automationInfo.copyPayload': 'Copy payload',
    'automationInfo.noResponse': 'No response',
    'automationInfo.attempts': 'Attempts',
    'automationInfo.emptyPrompt': 'Empty prompt',

    // Automation utility translations (utils.ts, types.ts)
    'automation.justNow': 'Just now',
    'automation.minAgo': '{n}m ago',
    'automation.hrAgo': '{n}h ago',
    'automation.dAgo': '{n}d ago',
    'automation.invalidSchedule': 'Invalid schedule',
    'automation.everyMinute': 'Every minute',
    'automation.everyNMinutes': 'Every {n} minutes',
    'automation.everyHourAt': 'Every hour at :{mm}',
    'automation.dailyAt': 'Daily at {time}',
    'automation.weekdaysAt': 'Weekdays at {time}',
    'automation.weekendsAt': 'Weekends at {time}',
    'automation.atTimeWeekday': 'At {time} (weekday: {dow})',
    'automation.monthlyOnDay': 'Monthly on day {dom} at {time}',
    'automation.durationMs': '{n}ms',
    'automation.durationS': '{n}s',
    'automation.matching': 'matching',
    'automation.atLabel': 'at',
    'automation.field.permissionMode': 'permission mode',
    'automation.field.sessionStatus': 'session status',
    'automation.field.isFlagged': 'flagged',
    'automation.field.labels': 'label',
    'automation.field.sessionName': 'session name',
    'automation.afterTime': 'after {time}',
    'automation.beforeTime': 'before {time}',
    'automation.anyTime': 'any time',
    'automation.any': 'any',
    'automation.changedFromTo': '{field} changed from {from} to {to}',
    'automation.hasContaining': 'has {field} "{value}"',
    'automation.notFlagged': 'not flagged',
    'automation.isFlagged': 'is flagged',
    'automation.fieldIsNot': '{field} is not {value}',
    'automation.fieldIs': '{field} is {value}',
    'automation.andNot': 'and not',
    'automation.and': 'and',
    'automation.or': 'or',
    'automation.unknownCondition': 'unknown condition',
    'automation.conditionTime': 'Time',
    'automation.conditionState': 'State',
    'automation.condition': 'Condition',
    'automation.event.LabelAdd': 'Label Added',
    'automation.event.LabelRemove': 'Label Removed',
    'automation.event.LabelConfigChange': 'Label Settings Changed',
    'automation.event.PermissionModeChange': 'Permission Changed',
    'automation.event.FlagChange': 'Flag Changed',
    'automation.event.TodoStateChange': 'Task Updated',
    'automation.event.SessionStatusChange': 'Status Changed',
    'automation.event.SchedulerTick': 'Scheduled',
    'automation.event.PreToolUse': 'Before Tool Runs',
    'automation.event.PostToolUse': 'After Tool Runs',
    'automation.event.PostToolUseFailure': 'When Tool Fails',
    'automation.event.Notification': 'Notification',
    'automation.event.UserPromptSubmit': 'Message Sent',
    'automation.event.SessionStart': 'Session Started',
    'automation.event.SessionEnd': 'Session Ended',
    'automation.event.Stop': 'Agent Stopped',
    'automation.event.SubagentStart': 'Sub-agent Started',
    'automation.event.SubagentStop': 'Sub-agent Stopped',
    'automation.event.PreCompact': 'Before Memory Cleanup',
    'automation.event.PermissionRequest': 'Permission Requested',
    'automation.event.Setup': 'Initial Setup',
    'automation.permission.safe': 'Explore',
    'automation.permission.ask': 'Ask',
    'automation.permission.allowAll': 'Execute',
    'automation.nextRun': 'Next run: {time} ({tz})',
    'automation.matches': 'Matches: {pattern}',
    'automation.onEvent': 'On {event}',

    // Pulse
    'pulse.notEnabled.title': 'A daily briefing that works for you',
    'pulse.notEnabled.description': 'Pulse analyzes your recent conversations and interests to generate a personalized daily briefing. Expand cards for details, continue the conversation, or tell it what to focus on tomorrow.',
    'pulse.notEnabled.enabling': 'Enabling...',
    'pulse.notEnabled.enable': 'Enable Pulse',
    'pulse.generating.title': 'Generating today\'s briefing',
    'pulse.generating.description': 'Analyzing your recent context and preparing today\'s content. This usually takes a minute.',
    'pulse.emptyToday.noUpdates': 'No new updates today',
    'pulse.emptyToday.startFirst': 'Start with your first session',
    'pulse.emptyToday.keepChatting': 'Keep chatting and connecting sources — Pulse will learn what matters to you.',
    'pulse.emptyToday.noHistory': 'Pulse will stay idle until this workspace has some conversation history. Start chatting first, then come back for a daily briefing.',
    'pulse.failed.title': 'Today\'s briefing failed to generate',
    'pulse.failed.retryHint': 'You can retry immediately.',
    'pulse.failed.refreshing': 'Refreshing...',
    'pulse.failed.regenerate': 'Regenerate',
    'pulse.backToToday': 'Back to today',
    'pulse.refresh': 'Refresh',
    'pulse.toast.loadFailed': 'Failed to load Pulse',
    'pulse.toast.enableFailed': 'Failed to enable Pulse',
    'pulse.toast.refreshFailed': 'Failed to refresh Pulse',
    'pulse.toast.chatFailed': 'Failed to start chat',
    'pulse.historyAriaLabel': 'View history',
    'pulse.cardDrawer.summary': 'Summary',
    'pulse.cardDrawer.whyThis': 'Why this',
    'pulse.cardDrawer.sources': 'Sources',
    'pulse.cardDrawer.imageSource': 'Image source',
    'pulse.cardDrawer.nextSteps': 'Next steps',
    'pulse.cardDrawer.nextActions': 'Next actions',
    'pulse.cardDrawer.continueChat': 'Continue chat',
    'pulse.cardDrawer.closeDetails': 'Close details',
    'pulse.cardDrawer.drawerDescription': '{kind} card details with summary, rationale, sources, and next steps.',
    'pulse.cardDrawer.pulseDetail': 'Pulse detail',
    'pulse.cardDrawer.authorBy': 'by {author}',
    'pulse.preview.moreOptions': 'More options',
    'pulse.preview.manageSettings': 'Manage settings',
    'pulse.preview.improveUpdate': 'Improve tomorrow\'s update',
    'pulse.preview.newFeature': 'New feature',
    'pulse.preview.featureTitle': 'Pulse now brings your scheduled updates into one place.',
    'pulse.preview.featureBody': 'You can create and edit updates here or from any chat by simply describing what you want and when you want it. Existing rules can gradually fold into this surface too.',
    'pulse.preview.settingsTitle': 'I will not show anything from your email or calendar here by default.',
    'pulse.preview.settingsBody': 'Once settings are wired up, this area can handle delivery time, source permissions, and privacy preferences. {count} underlying rules are still available in the Rules view.',

    // Pulse card kinds
    'pulse.kind.followup': 'Follow-up',
    'pulse.kind.research': 'Research',
    'pulse.kind.monitor': 'Monitor',
    'pulse.kind.suggestion': 'Suggestion',

    // Pulse footer copy
    'pulse.footer.0': 'That\'s all for today. The more we chat, the better your briefing gets.',
    'pulse.footer.1': 'Today\'s highlights are done. Keep the conversation going — tomorrow\'s briefing will be even more relevant.',
    'pulse.footer.2': 'Wrapping up. The more you share, the better I know what to watch for you.',
    'pulse.footer.3': 'No more updates today. Give me more context and future briefings will feel more tuned in.',
    'pulse.footer.4': 'That\'s it for now. Keep sharing your plans and preferences — the briefing will keep getting sharper.',

    // Pulse chat context
    'pulse.chatContext': 'Please continue the conversation about this topic. Help me understand more, answer questions, or suggest next steps.',

    // Pulse quick prompts
    'pulse.quickPrompt.0': 'Every Monday, show me what is worth following up on',
    'pulse.quickPrompt.1': 'Remind me about planning, pilots, and launch blockers',
    'pulse.quickPrompt.2': 'Send me daily updates on AI products and models',
    'pulse.quickPrompt.3': 'Keep tracking memory, multimodal, and agent evals',

    // Pulse preference blocks
    'pulse.preference.thinking.title': 'What is on your mind lately? I can remember it for tomorrow\'s update.',
    'pulse.preference.thinking.prompt.0': 'I am curious about...',
    'pulse.preference.thinking.prompt.1': 'Keep me posted on...',
    'pulse.preference.thinking.prompt.2': 'I want to keep tracking...',
    'pulse.preference.thinking.prompt.3': 'Share freely...',
    'pulse.preference.domain.title': 'Beyond AI itself, what adjacent areas should I watch?',
    'pulse.preference.domain.prompt.0': 'Semis and compute',
    'pulse.preference.domain.prompt.1': 'Learning tech',
    'pulse.preference.domain.prompt.2': 'Enterprise apps',
    'pulse.preference.domain.prompt.3': 'Creative industries',

    // Skills - article markdown fallbacks
    'skills.article.viewVideo': 'View video',
    'skills.article.viewOriginal': 'View original',

    // Plan dropdown
    'plan.acceptPlan': 'Accept Plan',
    'plan.accept': 'Accept',
    'plan.executeImmediately': 'Execute the plan immediately',
    'plan.acceptCompact': 'Accept & Compact',
    'plan.compactDescription': 'Works best for complex, longer plans',

    // Inline edit execution
    'inlineEdit.editing': 'Editing...',
    'inlineEdit.cancel': 'Cancel',
    'inlineEdit.done': 'Done',
    'inlineEdit.failed': 'Failed',
    'inlineEdit.dismiss': 'Dismiss',
    'inlineEdit.retry': 'Retry',

    // Diff viewer controls
    'diff.splitView': 'Switch to split view',
    'diff.unifiedView': 'Switch to unified view',
    'diff.enableHighlight': 'Enable background highlighting',
    'diff.disableHighlight': 'Disable background highlighting',

    // Item navigator
    'nav.previousItem': 'Previous item',
    'nav.selectItem': 'Select item',
    'nav.nextItem': 'Next item',

    // Overlay titles
    'overlay.terminal': 'Terminal',
    'overlay.json': 'JSON',
    'overlay.copyCode': 'Copy code',
    'overlay.mermaidDiagram': 'Mermaid Diagram',

    // Zoom controls
    'zoom.presets': 'Zoom presets',
    'zoom.out': 'Zoom out (\u2318\u2212)',
    'zoom.in': 'Zoom in (\u2318+)',
    'zoom.reset': 'Reset zoom (\u23180)',

    // Editor bubble menus
    'editor.bold': 'Bold',
    'editor.italic': 'Italic',
    'editor.strikethrough': 'Strikethrough',
    'editor.code': 'Code',
    'editor.math': 'Math',
    'editor.done': 'Done',

    // Attachments
    'attachment.clickToOpen': 'Click to open {name}',
    'attachment.queued': 'queued',

    // Task actions
    'taskAction.clickForActions': 'Click for task actions',

    // Chat display
    'chatDisplay.openInNewWindow': 'Open in new window',

    // Onboarding
    'onboarding.sessionExpired': 'Session Expired',

    // Preferences (standalone page)
    'preferences.title': 'Preferences',
    'preferences.namePlaceholder': 'Your name',
    'preferences.additionalInfoPlaceholder': "Any additional information you'd like to share with the AI assistant...",

    // Files
    'files.preview': 'Preview',

    // Session status
    'sessionStatus.changeTodoState': 'Change todo state',
  },
  'zh-CN': {
    'app.crash.title': '出了点问题',
    'app.crash.description': '请重启应用。错误已自动上报。',
    'common.reload': '重新加载',
    'common.cancel': '取消',
    'common.copy': '复制',
    'common.copied': '已复制',
    'common.delete': '删除',
    'common.clear': '清除',
    'common.create': '创建',
    'common.open': '打开',
    'common.back': '返回',
    'common.browse': '浏览',
    'common.workspace': '工作区',
    'common.workbench': '工作台',
    'common.enable': '启用',
    'common.disable': '停用',
    'common.edit': '编辑',
    'common.continue': '继续',
    'common.save': '保存',
    'common.saveAndSend': '保存并发送',
    'common.reset': '重置',
    'common.change': '更改',
    'common.loading': '加载中...',
    'common.saving': '保存中...',
    'common.uploading': '上传中...',
    'common.select': '选择',
    'common.preview': '预览',
    'common.file': '文件',
    'common.dataTable': '数据表',
    'common.spreadsheet': '表格',
    'common.image': '图片',
    'common.pdf': 'PDF',
    'common.command': '命令',
    'common.output': '输出',
    'common.row': '行',
    'common.rows': '行',
    'common.column': '列',
    'common.columns': '列',
    'common.noRows': '暂无数据',
    'common.tableControls': '表格控制',
    'common.label': '标签',
    'common.original': '原始',
    'common.modified': '修改后',
    'common.copyPath': '复制路径',
    'common.copyAll': '复制全部',
    'common.copyCode': '复制代码',
    'common.loadFailed': '加载失败',
    'common.rendering': '渲染中...',
    'common.openFile': '打开文件',
    'common.revealIn': '在 {name} 中显示',
    'common.viewFullscreen': '全屏查看',
    'common.html': 'HTML',
    'common.loadingData': '正在加载数据...',
    'common.sortBy': '排序方式',
    'common.groupBy': '分组方式',
    'common.clearAll': '清除全部',
    'common.closeEsc': '关闭 (Esc)',
    'common.contextBadge': '上下文标记',
    'common.copyJson': '复制 JSON',
    'common.exportTable': '导出表格',
    'datatable.yes': '是',
    'datatable.no': '否',
    'datatable.granularity.hour': '小时',
    'datatable.granularity.day': '天',
    'datatable.granularity.month': '月',
    'datatable.granularity.year': '年',
    'common.plainText': '纯文本',
    'common.searchLanguages': '搜索语言...',
    'common.noLanguagesFound': '没有找到语言',
    'common.codeLanguage': '代码语言',
    'common.selectCodeLanguage': '选择代码语言',
    'common.default': '默认',
    'common.duplicate': '复制',
    'common.include': '包含',
    'common.exclude': '排除',
    'common.info': '信息',
    'common.markdown': 'Markdown',
    'common.metadata': '元数据',
    'common.slug': 'Slug',
    'common.name': '名称',
    'common.description': '描述',
    'common.location': '位置',
    'common.type': '类型',
    'common.url': 'URL',
    'common.status': '状态',
    'common.sources': '数据源',
    'common.labels': '标签',
    'common.documentation': '文档',
    'common.permissions': '权限',
    'common.connection': '连接',
    'common.tools': '工具',
    'common.followUp': '跟进',
    'common.selectedText': '选中文本',
    'common.branch': '分支',
    'common.instructions': '说明',
    'common.settings': '设置',
    'common.recentActivity': '最近活动',
    'common.event': '事件',
    'common.timing': '时机',
    'common.timezone': '时区',
    'common.method': '方法',
    'common.duration': '耗时',
    'common.error': '错误',
    'common.response': '响应',
    'common.never': '从未',
    'common.active': '启用',
    'common.disabled': '停用',
    'common.beta': '测试版',
    'browser.windowOptions': '浏览器窗口选项',
    'browser.hideWindow': '隐藏窗口',
    'browser.closeWindowEntirely': '彻底关闭窗口',
    'browser.newTab': '新标签页',
    'browser.localFile': '本地文件',
    'browser.back': '后退',
    'browser.forward': '前进',
    'browser.reload': '重新加载',
    'browser.stopLoading': '停止加载',
    'browser.addressPlaceholder': '输入网址或搜索…',
    'browser.tabActionsAria': '{label} 操作',
    'browser.usingHost': '正在使用 {hostname}',
    'browser.emptyState.title': '这个浏览器已经准备好给 Agents 用了，也给你用 ;)',
    'browser.emptyState.description': '让任意会话使用这个浏览器，或者再打开一个，去完成调研、表单填写、QA 检查或数据提取。',
    'browser.emptyState.safetyHint': '只有在你明确要求时，XClaw 才会控制浏览器窗口。',
    'browser.showWindow': '显示浏览器窗口',
    'browser.openSessionUsingWindow': '打开正在使用此窗口的会话',
    'browser.openSessionWhichUsedWindow': '打开曾使用此窗口的会话',
    'browser.terminate': '终止浏览器',
    'turnCard.responding': '回复中...',
    'turnCard.stepsCompleted': '步骤已完成',
    'turnCard.starting': '开始中...',
    'turnCard.errorSuffix': '· {count} 个错误',

    'onboarding.common.continue': '继续',
    'onboarding.common.required': '必填',
    'onboarding.common.optional': '可选',
    'onboarding.welcome.updateTitle': '更新设置',
    'onboarding.welcome.title': '欢迎使用 XClaw',
    'onboarding.welcome.updateDescription': '更新你的 API 连接，或调整当前配置。',
    'onboarding.welcome.description': '给 Agent 配上它应得的体验。连接任意模型，整理你的会话，把真正重要的工作做起来。',
    'onboarding.welcome.checking': '检查中...',
    'onboarding.welcome.getStarted': '开始使用',
    'onboarding.provider.title': '欢迎使用 XClaw',
    'onboarding.provider.description': '你想用哪种方式连接？',
    'onboarding.provider.claudeDescription': '使用你的 Anthropic 订阅。',
    'onboarding.provider.chatgptDescription': '使用你的 ChatGPT Plus 或 Pro 订阅。',
    'onboarding.provider.copilotDescription': '使用你的 GitHub Copilot 订阅。',
    'onboarding.provider.otherProvider': '我使用其他提供商',
    'onboarding.provider.otherProviderDescription': 'Anthropic、AWS Bedrock、OpenRouter、Google，或任意兼容的提供商。',
    'onboarding.provider.localModel': '本地模型',
    'onboarding.provider.localModelDescription': '通过 Ollama 在本地运行模型。',
    'onboarding.provider.setupLater': '稍后设置',
    'onboarding.apiSetup.segment.anthropicDescriptionLine1': '使用 Claude Agent SDK 作为主 Agent。',
    'onboarding.apiSetup.segment.anthropicDescriptionLine2': '可通过 Claude 订阅或 API Key 配置。',
    'onboarding.apiSetup.segment.piDescriptionLine1': '使用 XClaw Backend 作为主 Agent。',
    'onboarding.apiSetup.segment.piDescriptionLine2': '可通过 API Key、OAuth 订阅或自定义端点配置。',
    'onboarding.apiSetup.option.claudeOauth': '使用你的 Claude 订阅，获得不限量访问。',
    'onboarding.apiSetup.option.anthropicApiKey': '通过 Anthropic、OpenRouter 或兼容 API 按量付费。',
    'onboarding.apiSetup.option.chatgptOauth': '通过 XClaw Backend 使用你的 ChatGPT 订阅。',
    'onboarding.apiSetup.option.copilotOauth': '通过 XClaw Backend 使用你的 GitHub Copilot 订阅。',
    'onboarding.apiSetup.option.apiKeyName': 'API Key',
    'onboarding.apiSetup.option.piApiKey': '通过 XClaw Backend 使用提供商预设（Anthropic、OpenAI、Google 等）。',
    'onboarding.apiSetup.title': '设置你的 Agent',
    'onboarding.apiSetup.descriptionLine1': '选择你希望如何驱动 AI Agent。',
    'onboarding.apiSetup.descriptionLine2': '后续你仍然可以继续添加更多连接。',
    'onboarding.credentials.connecting': '连接中...',
    'onboarding.credentials.validating': '校验中...',
    'onboarding.credentials.chatgpt.title': '连接 ChatGPT',
    'onboarding.credentials.chatgpt.description': '使用你的 ChatGPT 订阅来驱动 XClaw。',
    'onboarding.credentials.chatgpt.signIn': '使用 ChatGPT 登录',
    'onboarding.credentials.chatgpt.help': '点击上方按钮，用你的 OpenAI 账号登录。系统会打开浏览器完成认证。',
    'onboarding.credentials.chatgpt.success': '连接成功！你的 ChatGPT 订阅已就绪。',
    'onboarding.credentials.copilot.title': '连接 GitHub Copilot',
    'onboarding.credentials.copilot.description': '使用你的 GitHub Copilot 订阅来驱动 XClaw。',
    'onboarding.credentials.copilot.waitingAuthorization': '等待授权中...',
    'onboarding.credentials.copilot.signIn': '使用 GitHub 登录',
    'onboarding.credentials.copilot.enterCode': '请在 GitHub 中输入此授权码：',
    'onboarding.credentials.copilot.copied': '已复制到剪贴板',
    'onboarding.credentials.copilot.browserHint': '浏览器应该已经打开到 github.com/login/device',
    'onboarding.credentials.copilot.help': '点击上方按钮，用你的 GitHub 账号登录。',
    'onboarding.credentials.copilot.success': '连接成功！你的 GitHub Copilot 订阅已就绪。',
    'onboarding.credentials.claude.codeTitle': '输入授权码',
    'onboarding.credentials.claude.codeDescription': '从浏览器页面复制授权码，并粘贴到下方。',
    'onboarding.credentials.claude.title': '连接 Claude 账号',
    'onboarding.credentials.claude.description': '使用你的 Claude 订阅来驱动多 Agent 工作流。',
    'onboarding.credentials.claude.signIn': '使用 Claude 登录',
    'onboarding.credentials.apiConfiguration.title': 'API 配置',
    'onboarding.credentials.apiConfiguration.piDescription': '选择一个提供商预设并填写 API Key。若要接入任意兼容 Anthropic 的端点，请使用 Anthropic API Key 模式。',
    'onboarding.credentials.apiConfiguration.anthropicDescription': '输入你的 API Key。你也可以按需配置 OpenRouter、Ollama 或其他兼容 API 的自定义端点。',
    'onboarding.oauth.authorizationCode': '授权码',
    'onboarding.oauth.authorizationCodePlaceholder': '在这里粘贴你的授权码',
    'onboarding.localModel.title': '本地模型',
    'onboarding.localModel.description': '连接到 Ollama 或任意兼容 OpenAI 的本地服务。',
    'onboarding.localModel.endpointRequired': '必须填写端点地址。',
    'onboarding.localModel.modelRequired': '至少需要填写一个模型名。',
    'onboarding.localModel.endpointHint': 'Ollama 默认端口是 11434。如果你使用了自定义配置，请在这里修改。',
    'onboarding.localModel.model': '模型',
    'onboarding.localModel.modelHintPrefix': '可填写任意通过',
    'onboarding.localModel.modelHintSuffix': '拉取过的模型，多个模型请用逗号分隔。',
    'onboarding.completion.settingUp': '正在设置...',
    'onboarding.completion.title': '一切就绪！',
    'onboarding.completion.saving': '正在保存你的配置...',
    'onboarding.completion.description': '现在就开始聊天，进入工作状态吧。',
    'onboarding.gitBash.title': '需要 Git Bash',
    'onboarding.gitBash.description': 'XClaw 在 Windows 上运行 shell 命令时需要 Git Bash，但你的系统里暂时没有找到它。',
    'onboarding.gitBash.installTitle': '安装 Git for Windows',
    'onboarding.gitBash.installDescription': '这是获取 Git Bash 最简单的方式，免费并且已包含所需组件。',
    'onboarding.gitBash.download': '下载 Git for Windows',
    'onboarding.gitBash.installedTitle': '已经安装了 Git？',
    'onboarding.gitBash.installedDescription': '如果 Git 安装在非标准路径，你可以手动指定 bash.exe 的位置。',
    'onboarding.gitBash.usePath': '使用这个路径',
    'onboarding.gitBash.recheck': '重新检查',
    'onboarding.apiKey.preset.custom': '自定义',
    'onboarding.apiKey.preset.piDirect': 'XClaw Backend（直连）',
    'onboarding.apiKey.placeholder.pasteKey': '请在这里粘贴你的密钥...',
    'onboarding.apiKey.label': 'API Key',
    'onboarding.apiKey.endpoint': '端点',
    'onboarding.apiKey.endpointPlaceholder': 'https://your-api-endpoint.com',
    'onboarding.apiKey.protocol': '协议',
    'onboarding.apiKey.protocol.openaiCompatible': '兼容 OpenAI',
    'onboarding.apiKey.protocol.anthropicCompatible': '兼容 Anthropic',
    'onboarding.apiKey.protocolHint': '大多数第三方 API（如 Ollama、vLLM、DashScope）都使用 OpenAI 兼容协议。',
    'onboarding.apiKey.authentication': '认证方式',
    'onboarding.apiKey.authentication.iamCredentials': 'IAM 凭证',
    'onboarding.apiKey.authentication.environment': '环境配置（AWS CLI）',
    'onboarding.apiKey.aws.accessKeyId': 'Access Key ID',
    'onboarding.apiKey.aws.secretAccessKey': 'Secret Access Key',
    'onboarding.apiKey.aws.secretAccessKeyPlaceholder': '填写你的 Secret Access Key',
    'onboarding.apiKey.aws.sessionToken': 'Session Token',
    'onboarding.apiKey.aws.sessionTokenPlaceholder': '用于临时凭证（STS）',
    'onboarding.apiKey.aws.environmentHintPrefix': '使用你现有的 AWS 凭证链',
    'onboarding.apiKey.aws.environmentHintSuffix': '以及环境变量。',
    'onboarding.apiKey.aws.region': 'AWS 区域',
    'onboarding.apiKey.error.selectTierModels': '请为每个档位选择一个模型。',
    'onboarding.apiKey.error.accessKeyRequired': '使用 IAM 认证时必须填写 Access Key ID。',
    'onboarding.apiKey.error.secretKeyRequired': '使用 IAM 认证时必须填写 Secret Access Key。',
    'onboarding.apiKey.error.defaultModelRequired': '自定义端点必须填写默认模型。',
    'onboarding.apiKey.tier.best': '最佳',
    'onboarding.apiKey.tier.bestDescription': '能力最强',
    'onboarding.apiKey.tier.balanced': '均衡',
    'onboarding.apiKey.tier.balancedDescription': '适合日常使用',
    'onboarding.apiKey.tier.fast': '快速',
    'onboarding.apiKey.tier.fastDescription': '适合摘要与工具调用',
    'onboarding.apiKey.loadingModels': '正在加载模型...',
    'onboarding.apiKey.selectModel': '选择模型...',
    'onboarding.apiKey.searchModels': '搜索模型...',
    'onboarding.apiKey.defaultModel': '默认模型',
    'onboarding.apiKey.defaultModelPlaceholder': '例如：claude-opus-4-6, claude-sonnet-4-6, claude-haiku-4-5',
    'onboarding.apiKey.defaultModelHint': '使用逗号分隔多个模型。第一个模型会作为默认模型，最后一个用于摘要。',
    'onboarding.apiKey.defaultModelPlaceholderBedrock': '例如：company-sonnet、us.anthropic.claude-sonnet-4-6，或 arn:aws:bedrock:...:application-inference-profile/...',
    'onboarding.apiKey.defaultModelHintBedrock': '使用逗号分隔多个模型。支持别名、Bedrock 模型 ID 和 inference profile ARN。第一个模型会作为默认模型，最后一个用于摘要。',
    'onboarding.apiKey.bedrockArnMappings': 'ARN 映射',
    'onboarding.apiKey.bedrockArnMappingsHint': '给 Bedrock inference profile ARN 配一个易读别名，然后在上面的默认模型里直接使用这个别名。',
    'onboarding.apiKey.addArnMapping': '添加映射',
    'onboarding.apiKey.bedrockArnMappingsEmpty': '还没有 ARN 映射。如果你打算直接使用 Bedrock 模型 ID，或者直接把 ARN 填到上面的默认模型里，这里可以留空。',
    'onboarding.apiKey.customEndpointHint': '自定义端点必须填写这里，请使用提供商对应的模型 ID。',

    'menu.edit': '编辑',
    'menu.view': '视图',
    'menu.window': '窗口',
    'menu.undo': '撤销',
    'menu.redo': '重做',
    'menu.cut': '剪切',
    'menu.copy': '复制',
    'menu.paste': '粘贴',
    'menu.selectAll': '全选',
    'menu.zoomIn': '放大',
    'menu.zoomOut': '缩小',
    'menu.resetZoom': '重置缩放',
    'menu.toggleFocusMode': '切换专注模式',
    'menu.toggleSidebar': '切换侧边栏',
    'menu.minimize': '最小化',
    'menu.maximize': '最大化',

    'settings.page.app.label': '应用',
    'settings.page.app.description': '电源、代理、更新',
    'settings.page.notifications.label': '通知',
    'settings.page.notifications.description': '桌面和应用内提醒',
    'settings.notifications.title': '通知',
    'settings.notifications.desktop.title': '桌面',
    'settings.notifications.desktop.label': '桌面通知',
    'settings.notifications.desktop.description': 'AI 在聊天中完成任务后通知你。',
    'settings.notifications.completion.title': '任务完成',
    'settings.notifications.completion.description': 'AI 完成对话后的通知方式。',
    'settings.notifications.sound.title': '提示音',
    'settings.notifications.sound.label': '完成提示音',
    'settings.notifications.sound.description': 'AI 完成任务后播放提示音。',
    'settings.notifications.sound.none': '无',
    'settings.notifications.island.title': '灵动通知',
    'settings.notifications.island.description': '在 MacBook 刘海区域显示会话状态。',
    'settings.notifications.island.label': '灵动通知',
    'settings.notifications.island.detail': '在刘海附近显示浮动状态指示器，包含处理动画、完成勾选和会话列表。',
    'settings.page.ai.label': 'AI',
    'settings.page.ai.description': '模型、思考与连接',
    'settings.page.appearance.label': '外观',
    'settings.page.appearance.description': '主题、字体、工具图标',
    'settings.page.input.label': '输入',
    'settings.page.input.description': '发送键、拼写检查',
    'settings.page.memory.label': '记忆',
    'settings.page.memory.description': '告诉 AI 你的个人信息、偏好和项目上下文',
    'settings.page.workspace.label': '工作区',
    'settings.page.workspace.description': '名称、图标、工作目录',
    'settings.page.server.label': '服务器',
    'settings.page.server.description': '远程访问与 TLS',
    'settings.page.permissions.label': '权限',
    'settings.page.permissions.description': 'Explore 模式规则',
    'settings.page.labels.label': '标签',
    'settings.page.labels.description': '管理会话标签',
    'settings.page.shortcuts.label': '快捷键',
    'settings.page.shortcuts.description': '键盘快捷键',
    'settings.page.preferences.label': '偏好',
    'settings.page.preferences.description': '用户偏好设置',
    'settings.openInNewWindow': '在新窗口中打开',
    'settings.app.title': '应用',
    'settings.app.saving': '保存中...',
    'settings.app.notifications.title': '通知',
    'settings.app.notifications.desktop.label': '桌面通知',
    'settings.app.notifications.desktop.description': '当 AI 在聊天中完成工作时通知你。',
    'settings.app.power.title': '电源',
    'settings.app.power.keepAwake.label': '保持屏幕常亮',
    'settings.app.power.keepAwake.description': '在会话运行时阻止屏幕自动关闭。',
    'settings.app.power.keepAwake.remoteHint': '远程模式下，这个选项目前只会跟踪当前桌面客户端上的本地会话。',
    'settings.app.sidebar.title': '侧边栏',
    'settings.app.sidebar.channels.label': '显示频道入口',
    'settings.app.sidebar.channels.description': '只在侧边栏里隐藏频道入口，不会删除任何已保存的频道配置。',
    'settings.app.sidebar.automationRules.label': '显示自动化入口',
    'settings.app.sidebar.automationRules.description': '只在侧边栏里隐藏自动化入口，不会删除任何已保存的自动化配置。',
    'settings.app.execution.title': '执行环境',
    'settings.app.execution.description': '新的 Agent 子进程将使用此环境运行。正在运行的任务会保持当前进程直到重启。',
    'settings.app.execution.environment.label': '执行环境',
    'settings.app.execution.environment.description': '在本地主机上直接运行 Agent 子进程，或使用 Docker 沙箱隔离。',
    'settings.app.execution.environment.local': '本地',
    'settings.app.execution.environment.localDescription': '最快启动速度。命令直接在本机运行。',
    'settings.app.execution.environment.docker': 'Docker',
    'settings.app.execution.environment.dockerDescription': '使用 Docker 容器包装 Agent 子进程，实现进程隔离和资源限制。',
    'settings.app.execution.dockerImage': 'Docker 镜像',
    'settings.app.execution.dockerImagePlaceholder': 'node:22-slim',
    'settings.app.execution.dockerCpus': 'CPU 限制',
    'settings.app.execution.dockerCpusPlaceholder': '2',
    'settings.app.execution.dockerMemoryMb': '内存限制 (MB)',
    'settings.app.execution.dockerMemoryMbPlaceholder': '4096',
    'settings.app.execution.error.imageRequired': '请填写 Docker 镜像名称',
    'settings.app.execution.error.invalidCpus': 'CPU 限制必须为正数',
    'settings.app.execution.error.invalidMemory': '内存限制必须为正整数 (MB)',
    'settings.app.execution.error.failedSave': '保存失败',
    'settings.app.network.title': '网络',
    'settings.app.network.proxy.label': 'HTTP 代理',
    'settings.app.network.proxy.description': '通过代理服务器转发网络流量。',
    'settings.app.network.httpProxy': 'HTTP 代理',
    'settings.app.network.httpsProxy': 'HTTPS 代理',
    'settings.app.network.bypassRules': '绕过规则',
    'settings.app.network.proxyPlaceholder': 'http://proxy.example.com:8080',
    'settings.app.network.bypassPlaceholder': 'localhost, 127.0.0.1, .example.com',
    'settings.app.network.error.protocol': '必须是 http://、https://、socks4:// 或 socks5:// 地址',
    'settings.app.network.error.invalidUrl': 'URL 格式无效',
    'settings.app.network.error.failedSave': '保存失败',
    'settings.app.network.scope.label': '代理范围',
    'settings.app.network.scope.all': '全部',
    'settings.app.network.scope.ai': '仅 AI',
    'settings.app.network.scope.browser': '仅浏览器',
    'settings.app.network.scope.allDescription': '同时通过代理转发 AI 请求和内置浏览器流量。',
    'settings.app.network.scope.aiDescription': '只通过代理转发模型和工具请求。',
    'settings.app.network.scope.browserDescription': '只让内置浏览器走代理。',
    'settings.app.network.bedrockHttpMode.label': 'HTTP 传输',
    'settings.app.network.bedrockHttpMode.description': '选择 direct 模型请求使用的 HTTP 传输方式。自动模式会尽量使用 HTTP/2，在代理兼容性需要时退回 HTTP/1.1。',
    'settings.app.network.bedrockHttpMode.auto': '自动',
    'settings.app.network.bedrockHttpMode.http1': 'HTTP/1.1',
    'settings.app.network.bedrockHttpMode.http2': 'HTTP/2',
    'settings.app.network.bedrockHttpMode.autoDescription': '优先使用 HTTP/2，并在 direct 请求遇到兼容性问题时自动退回 HTTP/1.1。',
    'settings.app.network.bedrockHttpMode.http1Description': '优先使用兼容性最强的传输方式，尤其适合代理或较老的网关链路。',
    'settings.app.network.bedrockHttpMode.http2Description': '当上游服务和网络链路都稳定支持时，优先使用更新的传输方式。',
    'settings.app.network.bedrockHttpMode.http2ProxyHint': '开启 AI 代理时，direct 请求仍可能退回 HTTP/1.1，因为现有代理隧道路径只支持 HTTP/1.1 CONNECT。',
    'settings.app.network.remoteHint': '远程模式下，这里的代理设置只作用于当前桌面客户端。远端服务器上的 AI 流量需要在服务器端单独配置。',
    'settings.app.about.title': '关于',
    'settings.app.about.version': '版本',
    'settings.app.about.checkForUpdates': '检查更新',
    'settings.app.about.checkNow': '立即检查',
    'settings.app.about.checking': '检查中...',
    'settings.app.about.updateReady': '更新已就绪',
    'settings.app.about.restartToUpdate': '重启并更新到 v{version}',
    'settings.app.about.downloadingProgress': '正在下载 v{version}（{progress}%）',
    'settings.appearance.title': '外观',
    'settings.appearance.defaultTheme.title': '默认主题',
    'settings.appearance.mode.label': '模式',
    'settings.appearance.mode.system': '跟随系统',
    'settings.appearance.mode.light': '浅色',
    'settings.appearance.mode.dark': '深色',
    'settings.appearance.colorTheme.label': '颜色主题',
    'settings.appearance.font.label': '字体',
    'settings.appearance.font.system': '系统',
    'settings.appearance.font.inter': 'Inter',
    'settings.appearance.font.anthropicSans': 'XClaw Sans',
    'settings.appearance.font.anthropicSerif': 'XClaw Serif',
    'settings.appearance.font.geist': 'Geist',
    'settings.appearance.font.geistMono': 'Geist Mono',
    'settings.appearance.font.firaCode': 'Fira Code',
    'settings.appearance.font.jetbrainsMono': 'JetBrains Mono',
    'settings.appearance.font.monaspaceArgon': 'Monaspace Argon',
    'settings.appearance.font.monaspaceKrypton': 'Monaspace Krypton',
    'settings.appearance.font.monaspaceNeon': 'Monaspace Neon',
    'settings.appearance.font.monaspaceXenon': 'Monaspace Xenon',
    'settings.appearance.font.tsangerJinKai': '仓耳今楷',
    'settings.appearance.language.label': '语言',
    'settings.appearance.language.option.zhCN': '中文',
    'settings.appearance.language.option.en': 'English',
    'settings.appearance.themeWarning': '主题警告',
    'settings.appearance.usingBundledFallback': '使用内置回退主题',
    'settings.appearance.usingDefaultTheme': '使用默认主题',
    'settings.appearance.workspaceThemes.title': '工作区主题',
    'settings.appearance.workspaceThemes.description': '为不同工作区覆盖主题设置',
    'settings.appearance.workspaceThemes.useDefault': '使用默认值',
    'settings.appearance.workspaceThemes.useDefaultWith': '使用默认值（{label}）',
    'settings.appearance.interface.title': '界面',
    'settings.server.title': '服务器',
    'settings.server.remoteAccess': '远程访问',
    'settings.server.enableServerMode': '启用服务器模式',
    'settings.server.allowRemoteConnections': '允许通过你的网络远程连接到此应用。',
    'settings.server.restartRequired': '这些更改需要重启后才会生效。',
    'settings.server.restartNow': '立即重启',
    'settings.server.connectionSection': '连接',
    'settings.server.port': '端口',
    'settings.server.token': '令牌',
    'settings.server.certificate': '证书',
    'settings.server.privateKey': '私钥',
    'settings.server.notConfigured': '未配置',
    'settings.server.insecureWarning': '你的服务器当前正在接受不安全连接。',
    'settings.server.noTlsWarning': '尚未配置 TLS，传输流量不会被加密。',
    'settings.server.unsupported': '当前构建版本不提供服务器设置。',
    'settings.server.portValidation': '端口号必须在 1024 到 65535 之间。',
    'settings.server.privateKeyRequired': '配置证书时必须同时提供私钥。',
    'settings.server.certificateRequired': '配置私钥时必须同时提供证书。',
    'settings.server.saved': '服务器设置已保存。',
    'settings.server.failedToSave': '保存服务器设置失败。',
    'settings.server.copiedToClipboard': '已将 {label} 复制到剪贴板。',
    'settings.server.copyFailed': '复制到剪贴板失败。',
    'settings.tunnel.title': '快速远程访问',
    'settings.tunnel.description': '通过 Cloudflare Tunnel 从任何地方访问你的 XClaw，无需注册账号，无需任何配置。',
    'settings.tunnel.start': '启用远程访问',
    'settings.tunnel.stop': '停止',
    'settings.tunnel.active': '远程访问已开启',
    'settings.tunnel.started': '远程访问已启用',
    'settings.tunnel.stopped': '远程访问已关闭',
    'settings.tunnel.failedToStart': '启动隧道失败',
    'settings.tunnel.failedToStop': '停止隧道失败',
    'settings.tunnel.webUrl': '网页端',
    'settings.tunnel.hint': '在手机上打开 web.xclaw.team，输入上方的地址和令牌即可连接。每次启动地址会变化，关闭应用后远程访问自动断开。',
    'settings.appearance.connectionIcons.label': '连接图标',
    'settings.appearance.connectionIcons.description': '在会话列表和模型选择器中显示提供方图标',
    'settings.appearance.richToolDescriptions.label': '丰富工具描述',
    'settings.appearance.richToolDescriptions.description': '为所有工具调用添加动作名称和意图描述，在会话中提供更丰富的活动上下文。',
    'settings.appearance.toolIcons.title': '工具图标',
    'settings.appearance.toolIcons.description': '聊天活动中 CLI 命令旁显示的图标，存储在 ~/.xclaw/tool-icons/。',
    'settings.appearance.toolIcons.searchPlaceholder': '搜索工具...',
    'settings.appearance.toolIcons.empty': '没有找到工具图标映射',
    'settings.appearance.toolIcons.table.icon': '图标',
    'settings.appearance.toolIcons.table.tool': '工具',
    'settings.appearance.toolIcons.table.commands': '命令',
    'settings.input.title': '输入',
    'settings.input.typing.title': '输入行为',
    'settings.input.typing.description': '控制聊天输入框中的文本输入方式。',
    'settings.input.typing.autoCapitalisation.label': '自动首字母大写',
    'settings.input.typing.autoCapitalisation.description': '输入消息时自动将首字母大写。',
    'settings.input.typing.spellCheck.label': '拼写检查',
    'settings.input.typing.spellCheck.description': '输入时为拼写错误的单词加下划线。',
    'settings.input.sending.title': '发送',
    'settings.input.sending.description': '选择发送消息的方式。',
    'settings.input.sending.sendMessageWith.label': '发送消息使用',
    'settings.input.sending.sendMessageWith.description': '发送消息的键盘快捷键',
    'settings.input.sending.enterDescription': '使用 Shift+Enter 换行',
    'settings.input.sending.cmdEnterDescription': '使用 Enter 换行',
    'settings.input.voice.title': '语音输入',
    'settings.input.voice.description': '在本地录音并将转写结果插入聊天输入框，转写语言会跟随应用界面语言。',
    'settings.input.voice.enabled.label': '启用语音输入',
    'settings.input.voice.enabled.description': '启用聊天输入框中的本地语音输入快捷键。',
    'settings.input.voice.model.label': '模型',
    'settings.input.voice.model.description': '选择用于转写的本地 Whisper 模型。',
    'settings.input.voice.files.label': '模型文件',
    'settings.input.voice.files.downloaded': '{label} 已下载到本地。',
    'settings.input.voice.files.notDownloaded': '{label} 尚未下载。',
    'settings.input.voice.files.downloading': '正在下载 {label}…',
    'settings.input.voice.files.requiresDownload': '首次使用前需要先下载模型。',
    'settings.input.voice.shortcut.label': '快捷键',
    'settings.input.voice.shortcut.description': '仅在 XClaw 处于聚焦状态时可用。',
    'settings.input.voice.download': '下载',
    'settings.input.voice.remove': '移除',
    'settings.input.voice.unassigned': '未分配',
    'settings.input.learning.title': '学习建议',
    'settings.input.learning.description': '控制 XClaw 是否会从成功且可复用的流程里建议生成技能。',
    'settings.input.learning.skillProposals.label': '建议可复用技能',
    'settings.input.learning.skillProposals.description': '在成功的多步骤回合后，只展示技能提案卡片，不会静默创建技能。忽略后会在七天内压制相似建议。',
    'settings.memory.title': '记忆',
    'settings.memory.edit': '编辑',
    'settings.memory.delete': '删除',
    'settings.memory.save': '保存',
    'settings.memory.cancel': '取消',
    'settings.memory.saving': '保存中...',
    'settings.memory.mode.editing': '编辑中',
    'settings.memory.mode.memory': '记忆',
    'settings.memory.placeholder': '在这里编写你的记忆文档。XClaw 会读取这份文档来了解你的偏好和项目上下文。',
    'settings.memory.entries': '条目',
    'settings.memory.entrypoint': '入口文件',
    'settings.memory.confirmDelete': '要从持久记忆中删除“{name}”吗？',
    'settings.memory.confirmOverwrite': '已经存在同名持久记忆，要替换它吗？',
    'settings.memory.saved': '记忆已保存',
    'settings.memory.updated': '记忆已更新',
    'settings.memory.deleted': '记忆已删除',
    'settings.memory.failedLoad': '加载记忆失败',
    'settings.memory.failedSave': '保存记忆失败',
    'settings.memory.failedDelete': '删除记忆失败',
    'settings.memory.revert': '还原',
    'settings.memory.fileLabel': 'Markdown 文件',
    'settings.memory.filePending': '首次保存后，这条备注会以 Markdown 文件形式存储。',
    'settings.memory.about.title': '记忆',
    'settings.memory.about.description': '把这里当成写给未来 XClaw 的备注就行，直接写需要长期记住的内容。',
    'settings.memory.about.paragraph1': '项目相关的背景、约定和限制写在这里；如果更像个人资料或稳定偏好，就放到偏好页。',
    'settings.memory.user.title': '个人记忆',
    'settings.memory.user.description': '高级跨工作区备注。大多数用户只需要偏好页就够了。',
    'settings.memory.user.cardTitle': '个人备注',
    'settings.memory.user.cardDescription': '只有当这条长期备注需要跨多个工作区生效时，才写在这里。',
    'settings.memory.user.placeholder': '例如：默认用中文回答。解释尽量简洁，除非我要求展开。',
    'settings.memory.user.unsaved': '你有未保存的个人记忆改动。',
    'settings.memory.user.add': '新建记忆文档',
    'settings.memory.user.openPreferences': '打开偏好',
    'settings.memory.user.empty.title': '还没有用户记忆',
    'settings.memory.user.empty.description': '当你或代理选择保存高级用户记忆文档后，它们会显示在这里。',
    'settings.memory.workspace.title': '项目记忆',
    'settings.memory.workspace.description': '当前工作区的一份简单 Markdown 备注。',
    'settings.memory.workspace.cardTitle': '项目备注',
    'settings.memory.workspace.cardDescription': '把项目约定、限制、背景信息，或任何希望 XClaw 长期记住的内容写在这里。',
    'settings.memory.workspace.placeholder': '例如：只有周五发版。Release note 要简短。面向客户的文案默认用中文。',
    'settings.memory.workspace.unsaved': '你有未保存的项目记忆改动。',
    'settings.memory.workspace.add': '新建记忆文档',
    'settings.memory.workspace.empty.title': '还没有工作区记忆',
    'settings.memory.workspace.empty.description': '当代理保存项目层面的长期事实后，它们会显示在这里。',
    'settings.memory.recall.title': '会话回忆与 Pulse',
    'settings.memory.recall.description': '这两层在这里是只读的，并且与持久记忆保持分离。',
    'settings.memory.recall.paragraph1': '会话回忆来自当前工作区内历史会话的有界检索，不会自动回写成持久记忆。',
    'settings.memory.recall.paragraph2': 'Pulse 记忆仍然是 Pulse 工作流专用的 briefing 层，除非你手动迁移信息，否则不会并入用户或工作区记忆。',
    'settings.memory.form.addTitle': '新建记忆文档',
    'settings.memory.form.editTitle': '编辑记忆文档',
    'settings.memory.form.userHint': '用户记忆属于高级跨工作区上下文。稳定资料仍建议放在偏好页。',
    'settings.memory.form.workspaceHint': '工作区记忆只属于当前项目，并且以 Markdown 文档形式存储。',
    'settings.memory.form.name.label': '标题',
    'settings.memory.form.name.description': '给这条记忆起一个简短名字。',
    'settings.memory.form.name.placeholder': '时区偏好',
    'settings.memory.form.description.label': '摘要',
    'settings.memory.form.description.description': '可选的一句话摘要。留空时，XClaw 会根据标题自动补一个。',
    'settings.memory.form.description.placeholder': '安排日程时默认使用的时区',
    'settings.memory.form.type.label': '类型',
    'settings.memory.form.type.description': '类型会写进 frontmatter，只有在你想更精细分类时才需要改它。',
    'settings.memory.form.document.label': 'Markdown 文档',
    'settings.memory.form.document.description': '这里展示最终会写入磁盘的原始 Markdown 文件，包含 YAML frontmatter。',
    'settings.memory.form.type.user.preference': '偏好',
    'settings.memory.form.type.user.profile': '资料',
    'settings.memory.form.type.user.reference': '参考',
    'settings.memory.form.type.workspace.project': '项目',
    'settings.memory.form.type.workspace.feedback': '反馈',
    'settings.memory.form.type.workspace.reference': '参考',
    'settings.memory.form.body.label': '正文',
    'settings.memory.form.body.description': '正文使用应用里现有的 Markdown 编辑器栈。',
    'settings.memory.form.body.placeholder': '写下这条长期笔记或项目上下文...',
    'settings.memory.form.advanced.title': '高级细节',
    'settings.memory.form.advanced.description': '只有在你想调整类型、摘要或查看原始文件时再展开这里。',
    'settings.memory.form.filename': '文件',
    'chat.skillProposal.label': '技能建议',
    'chat.skillProposal.slug': '建议的 slug',
    'chat.skillProposal.target': '更新技能',
    'chat.skillProposal.dismiss': '七天内不再提示',
    'chat.skillProposal.accept': '创建技能',
    'chat.skillProposal.acceptUpdate': '更新技能',
    'chat.skillProposal.accepted': '技能提案已保存',
    'chat.skillProposal.acceptFailed': '无法保存技能提案',
    'chat.skillProposal.dismissed': '技能提案已忽略',
    'chat.skillProposal.dismissedDescription': '相似建议会在七天内保持压制。',
    'chat.skillProposal.dismissFailed': '无法忽略技能提案',
    'chat.skillProposal.historyLabel': '最近学习记录',
    'chat.skillProposal.cooldown': '相似建议将压制到',
    'chat.skillProposal.historyAcceptedCreate': '已创建工作区技能',
    'chat.skillProposal.historyAcceptedUpdate': '已更新工作区技能',
    'chat.skillProposal.historyDismissed': '已忽略并进入冷却',
    'input.voice.recordingHint': '正在收音，松开 {key} 开始转写',
    'input.voice.transcribingInline': '正在转写语音…',
    'settings.preferences.title': '偏好',
    'settings.preferences.basicInfo.title': '基本信息',
    'settings.preferences.basicInfo.description': '帮助 XClaw 更好地根据你个人情况进行回复。',
    'settings.preferences.name.label': '姓名',
    'settings.preferences.name.description': 'XClaw 应该如何称呼你。',
    'settings.preferences.name.placeholder': '你的名字',
    'settings.preferences.timezone.label': '时区',
    'settings.preferences.timezone.description': '用于理解“明天”或“下周”这类相对日期。',
    'settings.preferences.timezone.placeholder': '例如：Asia/Shanghai',
    'settings.preferences.language.label': '语言',
    'settings.preferences.language.description': 'XClaw 回复时优先使用的语言。',
    'settings.preferences.language.placeholder': '例如：中文',
    'settings.preferences.location.title': '位置',
    'settings.preferences.location.description': '用于天气、本地时间和区域上下文等与位置相关的回复。',
    'settings.preferences.city.label': '城市',
    'settings.preferences.city.description': '你的城市，用于提供本地化信息和上下文。',
    'settings.preferences.city.placeholder': '例如：上海',
    'settings.preferences.country.label': '国家/地区',
    'settings.preferences.country.description': '你的国家或地区，用于区域格式和上下文。',
    'settings.preferences.country.placeholder': '例如：中国',
    'settings.preferences.notes.title': '备注',
    'settings.preferences.notes.description': '帮助 XClaw 了解你偏好的自由补充信息。',
    'settings.preferences.notes.placeholder': '补充任何你希望 XClaw 知道的上下文...',
    'settings.preferences.memoryBoundary.title': '记忆与偏好的分工',
    'settings.preferences.memoryBoundary.description': '姓名、时区、位置这类稳定资料放在这里。Memory 更适合保存以后还要记住的项目或个人备注。',
    'settings.preferences.memoryBoundary.open': '打开 Memory',
    'settings.workspace.title': '工作区设置',
    'settings.workspace.noWorkspaceSelected': '未选择工作区',
    'settings.workspace.info.title': '工作区信息',
    'settings.workspace.info.name': '名称',
    'settings.workspace.info.icon': '图标',
    'settings.workspace.info.untitled': '未命名',
    'settings.workspace.renameTitle': '重命名工作区',
    'settings.workspace.renamePlaceholder': '输入工作区名称...',
    'settings.workspace.permissions.title': '权限',
    'settings.workspace.permissions.defaultMode.label': '默认模式',
    'settings.workspace.permissions.defaultMode.description': '控制 AI 可以做什么',
    'settings.workspace.permissions.mode.safeLabel': '浏览',
    'settings.workspace.permissions.mode.safeDescription': '只读，不允许修改',
    'settings.workspace.permissions.mode.askLabel': '请求后编辑',
    'settings.workspace.permissions.mode.askDescription': '编辑前先征求确认',
    'settings.workspace.permissions.mode.allowAllLabel': '自动',
    'settings.workspace.permissions.mode.allowAllDescription': '完全自主执行',
    'settings.workspace.modeCycling.title': '模式切换',
    'settings.workspace.modeCycling.description': '选择 Shift+Tab 切换时会经过哪些模式',
    'settings.workspace.modeCycling.error': '至少需要保留 2 个模式',
    'settings.workspace.mode.safe.label': '浏览',
    'settings.workspace.mode.safe.description': '只读，不允许修改',
    'settings.workspace.mode.ask.label': '请求后编辑',
    'settings.workspace.mode.ask.description': '编辑前先征求确认',
    'settings.workspace.mode.allow-all.label': '自动',
    'settings.workspace.mode.allow-all.description': '完全自主执行',
    'settings.workspace.defaultSources.title': '默认数据源',
    'settings.workspace.defaultSources.description': '新建会话时自动启用的数据源',
    'settings.workspace.defaultSources.empty': '这个工作区里还没有配置数据源。',
    'settings.workspace.advanced.title': '高级',
    'settings.workspace.advanced.workingDirectory.label': '默认工作目录',
    'settings.workspace.advanced.workingDirectory.notSet': '未设置（使用会话目录）',
    'settings.workspace.advanced.localMcp.label': '本地 MCP 服务',
    'settings.workspace.advanced.localMcp.description': '启用 stdio 子进程服务',
    'settings.shortcuts.title': '快捷键',
    'settings.shortcuts.category.general': '通用',
    'settings.shortcuts.category.navigation': '导航',
    'settings.shortcuts.category.view': '视图',
    'settings.shortcuts.category.navigator': '导航器',
    'settings.shortcuts.category.chat': '聊天',
    'settings.shortcuts.section.listNavigation': '列表导航',
    'settings.shortcuts.section.sessionList': '会话列表',
    'settings.shortcuts.section.chatInput': '聊天输入',
    'settings.shortcuts.navigateItemsInList': '在列表中移动项目',
    'settings.shortcuts.goToFirstItem': '跳到第一个项目',
    'settings.shortcuts.goToLastItem': '跳到最后一个项目',
    'settings.shortcuts.focusChatInput': '聚焦聊天输入框',
    'settings.shortcuts.openContextMenu': '打开上下文菜单',
    'settings.shortcuts.addFilterAsExcluded': '将筛选添加为排除条件',
    'settings.shortcuts.sendMessage': '发送消息',
    'settings.shortcuts.newLine': '换行',
    'settings.shortcuts.closeDialogOrBlurInput': '关闭弹窗 / 取消输入框焦点',
    'settings.shortcuts.deleteSession': '删除会话',
    'settings.shortcuts.renameSession': '重命名会话',
    'settings.shortcuts.section.agentTree': '代理树',
    'settings.shortcuts.collapseFolder': '折叠文件夹',
    'settings.shortcuts.expandFolder': '展开文件夹',
    'settings.shortcuts.key.rightClick': '右键',
    'settings.shortcuts.key.click': '点击',
    'action.app.newChat.label': '新建聊天',
    'action.app.newChat.description': '创建一个新的聊天会话',
    'action.app.newChatInPanel.label': '在面板中新建聊天',
    'action.app.newChatInPanel.description': '在新面板中创建一个新的聊天会话',
    'action.app.settings.label': '设置',
    'action.app.settings.description': '打开应用设置',
    'action.app.toggleTheme.label': '切换主题',
    'action.app.toggleTheme.description': '在浅色和深色模式之间切换',
    'action.app.search.label': '搜索',
    'action.app.search.description': '打开搜索面板',
    'action.app.keyboardShortcuts.label': '键盘快捷键',
    'action.app.keyboardShortcuts.description': '显示键盘快捷键参考',
    'action.app.newWindow.label': '新建窗口',
    'action.app.newWindow.description': '打开一个新窗口',
    'action.app.quit.label': '退出',
    'action.app.quit.description': '退出应用',
    'action.nav.focusSidebar.label': '聚焦侧边栏',
    'action.nav.focusNavigator.label': '聚焦导航器',
    'action.nav.focusChat.label': '聚焦聊天区',
    'action.nav.nextZone.label': '聚焦下一个区域',
    'action.nav.goBack.label': '后退',
    'action.nav.goBack.description': '导航到上一个会话',
    'action.nav.goForward.label': '前进',
    'action.nav.goForward.description': '导航到下一个会话',
    'action.nav.goBackAlt.label': '后退',
    'action.nav.goBackAlt.description': '导航到上一个会话（方向键）',
    'action.nav.goForwardAlt.label': '前进',
    'action.nav.goForwardAlt.description': '导航到下一个会话（方向键）',
    'action.view.toggleSidebar.label': '切换侧边栏',
    'action.view.toggleFocusMode.label': '切换专注模式',
    'action.view.toggleFocusMode.description': '隐藏两侧边栏，进入无干扰工作模式',
    'action.navigator.selectAll.label': '全选',
    'action.navigator.clearSelection.label': '清除选择',
    'action.panel.focusNext.label': '聚焦下一个面板',
    'action.panel.focusNext.description': '将焦点移动到下一个面板',
    'action.panel.focusPrev.label': '聚焦上一个面板',
    'action.panel.focusPrev.description': '将焦点移动到上一个面板',
    'action.chat.stopProcessing.label': '停止处理',
    'action.chat.stopProcessing.description': '取消当前 AI 任务（双击触发）',
    'action.chat.cyclePermissionMode.label': '循环切换权限模式',
    'action.chat.cyclePermissionMode.description': '在浏览、请求后编辑和自动之间切换',
    'action.chat.nextSearchMatch.label': '下一个搜索结果',
    'action.chat.prevSearchMatch.label': '上一个搜索结果',
    'action.chat.toggleVoiceInput.label': '切换语音输入',
    'action.chat.toggleVoiceInput.description': '在 XClaw 聚焦时开始或停止本地语音听写',
    'settings.permissions.title': '权限',
    'settings.permissions.about.title': '关于权限',
    'settings.permissions.about.paragraph1': '权限决定 AI 能有多大自主性。在浏览模式下，AI 只能读取和调研，适合在真正动手前先理解问题。准备好了之后，再切换到自动模式，让 AI 自主执行方案。',
    'settings.permissions.about.paragraph2': '比较顺手的工作流是：先用浏览模式让 AI 调研，再审阅它提出的计划，最后有把握地执行。',
    'settings.permissions.default.title': '默认权限',
    'settings.permissions.default.description': '浏览模式下应用级允许的规则。未出现在列表中的命令都会被阻止。',
    'settings.permissions.default.empty': '没有找到默认权限。',
    'settings.permissions.default.emptyHint': '默认权限文件应位于 ~/.xclaw/permissions/default.json',
    'settings.permissions.workspace.title': '工作区自定义',
    'settings.permissions.workspace.description': '在应用默认权限之上追加的工作区级规则。',
    'settings.permissions.workspace.empty': '没有配置工作区自定义权限。',
    'settings.permissions.workspace.emptyHint': '在你的工作区中创建 permissions.json 文件即可添加自定义规则。',
    'settings.permissions.writeTo': '写入到',
    'settings.permissions.comment.listDirectoryContents': '列出目录内容',
    'settings.permissions.comment.longListing': '长列表显示（ls -l 别名）',
    'settings.permissions.comment.listAllIncludingHidden': '列出全部内容（含隐藏文件，ls -la 别名）',
    'settings.permissions.comment.displayDirectoryTree': '显示目录树结构',
    'settings.permissions.comment.determineFileType': '识别文件类型',
    'settings.permissions.comment.displayFileMetadata': '显示文件状态和元数据',
    'settings.permissions.comment.estimateDiskUsage': '估算文件或目录磁盘占用',
    'settings.permissions.comment.reportFilesystemUsage': '显示文件系统磁盘使用情况',
    'settings.permissions.comment.countLinesWordsBytes': '统计文件的行数、词数和字节数',
    'settings.permissions.comment.addLineNumbers': '给文本输出添加行号',
    'settings.permissions.comment.outputFirstPart': '输出文件开头部分',
    'settings.permissions.comment.outputLastPart': '输出文件结尾部分',
    'settings.permissions.comment.customBlockedTool': '自定义阻止工具',
    'settings.permissions.comment.customBashPattern': '自定义 Bash 规则',
    'settings.permissions.comment.customMcpPattern': '自定义 MCP 规则',
    'settings.permissions.comment.customApiEndpoint': '自定义 API 端点',
    'settings.permissions.comment.allowedWritePath': '允许写入路径',
    'permissionsTable.column.access': '权限',
    'permissionsTable.column.type': '类型',
    'permissionsTable.column.pattern': '规则',
    'permissionsTable.column.comment': '说明',
    'permissionsTable.type.tool': '工具',
    'permissionsTable.type.bash': 'Bash',
    'permissionsTable.type.api': 'API',
    'permissionsTable.type.mcp': 'MCP',
    'permissionsTable.status.allowed': '允许',
    'permissionsTable.status.blocked': '阻止',
    'permissionsTable.status.ask': '询问',
    'permissionsTable.searchPlaceholder': '搜索规则...',
    'permissionsTable.empty': '没有配置权限规则',
    'permissionsTable.viewFullscreen': '全屏查看',
    'permissionsTable.ruleSingular': '条规则',
    'permissionsTable.rulePlural': '条规则',
    'permissionsTable.ruleCount': '{count}{label}',
    'permissionsTable.patternCopied': '规则已复制到剪贴板',
    'permissionsTable.patternCopyFailed': '复制规则失败',
    'dataTable.noResults': '没有结果。',
    'dataTable.total': '共 {count} 条',
    'dataTable.previous': '上一页',
    'dataTable.pageOf': '第 {page} 页，共 {total} 页',
    'dataTable.next': '下一页',
    'infoDataTable.searchPlaceholder': '搜索...',
    'infoDataTable.authRequiredData': '请先为此数据源完成认证后再查看可用数据',
    'infoGroupedList.authRequiredTools': '请先为此数据源完成认证后再查看可用工具',
    'infoPage.errorLoadingContent': '加载内容时出错',
    'toolsTable.column.tool': '工具',
    'toolsTable.empty': '没有可用工具',
    'autoRulesTable.flags': '标志',
    'autoRulesTable.template': '模板',
    'autoRulesTable.searchPlaceholder': '搜索规则...',
    'autoRulesTable.empty': '未配置自动应用规则',
    'sourceStatus.connectedDescription': '数据源已连接并可正常使用',
    'sourceStatus.authDescription': '此数据源需要认证后才能连接',
    'sourceStatus.failed': '连接失败',
    'sourceStatus.failedDescription': '连接数据源失败',
    'sourceStatus.untestedDescription': '尚未测试连接',
    'sourceStatus.disabledDescription': '本地 MCP 服务器已在设置中禁用',
    'transportBanner.authFailed': '认证失败，请检查 XCLAW_SERVER_TOKEN。',
    'transportBanner.protocolMismatch': '客户端与服务端版本之间的协议不匹配。',
    'transportBanner.timeout': '连接到 {url} 超时，服务器可能不可达。',
    'transportBanner.network': '无法连接到 {url}。远程服务器是否已启动？',
    'transportBanner.closed': 'WebSocket 已关闭，代码为 {code}{reason}。',
    'transportBanner.waiting': '正在等待远程服务器连接。',
    'transportBanner.connectingTitle': '正在连接远程服务器',
    'transportBanner.connectingDescription': '正在连接到 {url}...',
    'transportBanner.retryInMs': '{ms} 毫秒后重试',
    'transportBanner.retrying': '正在重试',
    'transportBanner.reconnectingTitle': '正在重新连接远程服务器',
    'transportBanner.reconnectingDescription': '{reason}（{retry}，第 {attempt} 次尝试）',
    'transportBanner.failedTitle': '无法连接到远程服务器',
    'transportBanner.disconnectedTitle': '与远程服务器的连接已断开',
    'transportBanner.statusTitle': '远程服务器连接状态',
    'transportBanner.retry': '重试',
    'slashMenu.compactContext': '压缩上下文',
    'slashMenu.compactContextDescription': '总结当前对话上下文以释放 token 预算',
    'slashMenu.modes': '模式',
    'slashMenu.commands': '命令',
    'slashMenu.recentWorkingDirectories': '最近的工作目录',
    'slashMenu.searchCommands': '搜索命令...',
    'slashMenu.noCommandsFound': '未找到命令',
    'slashMenu.useAtForSkillsAndFiles': '输入 @ 可引用技能和文件',
    'input.placeholder.whatWouldYouLike': '你想处理什么内容？',
    'input.placeholder.switchModes': '按 Shift + Tab 可在浏览和自动模式之间切换',
    'input.placeholder.mention': '输入 @ 可提及文件、文件夹或技能',
    'input.placeholder.labels': '输入 # 可为当前对话添加标签',
    'input.placeholder.newLine': '按 Shift + Return 可换行',
    'input.placeholder.toggleSidebar': '按 {key} + B 可切换侧边栏',
    'input.placeholder.focusMode': '按 {key} + . 可进入专注模式',
    'input.placeholder.voiceInput': '按住 {key} 可语音输入',
    'input.voice.emptyResult': '这段录音里没有检测到语音。',
    'input.voice.transcribeError': '语音转写失败。',
    'input.voice.unavailable': '当前环境不支持语音输入。',
    'input.voice.disabled': '请先在 设置 → 输入 中启用语音输入。',
    'input.voice.modelMissing': '请先下载当前选择的 Whisper 模型，再使用语音输入。',
    'input.voice.browserUnsupported': '当前环境不支持麦克风录音。',
    'input.voice.recordError': '录音失败，请检查麦克风权限后重试。',
    'input.voice.permissionError': '麦克风访问被拒绝。',
    'input.fileCountSingular': '{count} 个文件',
    'input.fileCountPlural': '{count} 个文件',
    'input.attachFiles': '附加文件',
    'input.chooseSources': '选择数据源',
    'input.sourceCount': '{count} 个数据源',
    'input.unavailable': '不可用',
    'input.model': '模型',
    'input.connectionUnavailableTitle': '连接不可用',
    'input.connectionUnavailableDescription': '当前会话使用的连接已被移除。请创建一个新会话以继续。',
    'input.connectionDefault': '连接默认值',
    'input.notAuthenticated': '未认证',
    'input.usingConnection': '正在使用 {name}',
    'input.thinkingNotSupported': '当前模型不支持',
    'input.extendedReasoningDepth': '扩展推理深度',
    'input.context': '上下文',
    'input.tokensUsed': '已使用 {count} tokens',
    'input.contextUsedWait': '已使用 {percent}% 上下文，请等待当前操作完成',
    'input.contextUsedCompact': '已使用 {percent}% 上下文，点击压缩',
    'input.folder': '文件夹',
    'input.workInFolder': '在文件夹中工作',
    'input.workingDirectory': '工作目录',
    'input.onBranch': '分支 {branch}',
    'input.chooseWorkingDirectory': '选择工作目录',
    'input.filterFolders': '筛选文件夹...',
    'input.noFoldersFound': '未找到文件夹',
    'input.chooseFolder': '选择文件夹...',
    'settings.labels.title': '标签',
    'settings.labels.about.title': '关于标签',
    'settings.labels.about.paragraph1': '标签可以用彩色标记来组织会话。你可以按项目、主题或优先级给对话分类，后续筛选和查找相关会话会更方便。',
    'settings.labels.about.paragraph2': '每个标签还可以带一个特定类型的值，比如文本、数字或日期。这样标签就不仅是标签，还能承载结构化元数据，例如值为 3 的 priority，或者带日期的 due。',
    'settings.labels.about.paragraph3': '自动应用规则会在消息匹配正则时自动打上标签。比如粘贴一个 Linear issue 链接时，可以自动给会话加上项目名和 issue ID，不再需要手动标记。',
    'settings.labels.hierarchy.title': '标签层级',
    'settings.labels.hierarchy.description': '当前工作区配置的所有标签。标签可以嵌套形成分组。',
    'settings.labels.hierarchy.empty': '还没有配置标签。',
    'settings.labels.hierarchy.emptyHint': '你可以通过 agent 创建标签，也可以直接编辑工作区里的 labels/config.json。',
    'settings.labels.autoApply.title': '自动应用规则',
    'settings.labels.autoApply.description': '当用户消息匹配正则表达式时，自动应用对应标签。例如粘贴一个 Linear issue URL 时，自动给会话打上项目名和 issue ID。',
    'settings.ai.title': 'AI',
    'settings.ai.reauthenticate': '重新认证',
    'settings.ai.useDefault': '使用默认值',
    'settings.ai.inheritFromAppSettings': '继承应用设置',
    'settings.ai.closeEsc': '关闭（Esc）',
    'settings.ai.renameConnection.title': '重命名连接',
    'settings.ai.renameConnection.placeholder': '输入连接名称...',
    'settings.ai.credentialHealth.title': '检测到凭据问题',
    'settings.ai.credentialHealth.fileCorrupted': '凭据文件已损坏，请重新认证。',
    'settings.ai.credentialHealth.decryptionFailed': '检测到来自其他设备的凭据，请在当前设备上重新认证。',
    'settings.ai.credentialHealth.noDefaultCredentials': '未找到默认连接对应的凭据。',
    'settings.ai.credentialHealth.default': '检测到凭据问题。',
    'settings.ai.connection.validating': '验证中...',
    'settings.ai.connection.valid': '连接有效',
    'settings.ai.connection.invalid': '验证失败',
    'settings.ai.connection.unknown': '未知',
    'settings.ai.connection.notAuthenticated': '未认证',
    'settings.ai.connection.setAsDefault': '设为默认',
    'settings.ai.connection.validate': '验证连接',
    'settings.ai.default.title': '默认设置',
    'settings.ai.default.description': '当没有设置工作区覆盖项时，新聊天将使用这里的设置。',
    'settings.ai.default.connection.label': '连接',
    'settings.ai.default.connection.description': '新聊天默认使用的 API 连接',
    'settings.ai.default.model.label': '模型',
    'settings.ai.default.model.description': '新聊天默认使用的 AI 模型',
    'settings.ai.default.thinking.label': '思考深度',
    'settings.ai.default.thinking.description': '新聊天默认使用的推理深度',
    'settings.ai.workspaceOverrides.title': '工作区覆盖设置',
    'settings.ai.workspaceOverrides.description': '按工作区覆盖默认设置。',
    'settings.ai.workspaceOverrides.usingDefaults': '使用默认设置',
    'settings.ai.connections.title': '连接',
    'settings.ai.connections.description': '管理你的 AI 提供方连接。',
    'settings.ai.connections.empty': '还没有配置连接。添加一个连接即可开始使用。',
    'settings.ai.connections.add': '添加连接',
    'settings.ai.performance.title': '性能',
    'settings.ai.performance.description': '成本与缓存相关选项。',
    'settings.ai.performance.extendedContext.label': '扩展上下文（1M）',
    'settings.ai.performance.extendedContext.description': '为 Opus 4.6 使用 1M token 上下文窗口。关闭后改用 200K，以节省使用额度。',
    'settings.ai.performance.extendedPromptCache.label': '扩展提示缓存（1 小时）',
    'settings.ai.performance.extendedPromptCache.description': '将提示缓存时间从 5 分钟延长到 1 小时。仅对通过 Anthropic API 使用的 Claude 模型生效。可以降低长会话成本，但会增加缓存写入成本。',

    'topbar.toggleSidebar': '切换侧边栏',
    'topbar.newChat': '新建聊天',
    'topbar.newWindow': '新建窗口',
    'topbar.settings': '设置',
    'topbar.settingsDialog': '设置...',
    'topbar.help': '帮助',
    'topbar.helpAndDocs': '帮助与文档',
    'topbar.keyboardShortcuts': '键盘快捷键',
    'topbar.debug': '调试',
    'topbar.checkForUpdates': '检查更新',
    'topbar.installUpdate': '安装更新',
    'topbar.toggleDevTools': '切换开发者工具',
    'topbar.craftMenu': 'XClaw 菜单',
    'topbar.quitXClaw': '退出 XClaw',
    'topbar.goBack': '返回',
    'topbar.goForward': '前进',
    'topbar.addPanelMenu': '添加面板菜单',
    'topbar.newSessionInPanel': '在面板中新建会话',
    'topbar.newBrowserWindow': '新建浏览器窗口',
    'topbar.allDocumentation': '全部文档',
    'topbar.sources': '数据源',
    'topbar.skills': '技能',
    'topbar.statuses': '状态',
    'topbar.permissions': '权限',
    'topbar.automations': '动态',
    'chat.share.openInBrowser': '在浏览器中打开',
    'chat.share.copyLink': '复制链接',
    'chat.share.updateShare': '更新分享',
    'chat.share.stopSharing': '停止分享',
    'chat.share.shareOnline': '在线分享',
    'multiSelect.entity.session': '会话',
    'multiSelect.entity.source': '数据源',
    'multiSelect.entity.skill': '技能',
    'multiSelect.use': '使用',
    'multiSelect.toToggle': '可切换选择',
    'multiSelect.forRange': '可进行范围选择',
    'multiSelect.press': '按',
    'multiSelect.toClearSelection': '可清除选择',
    'multiSelect.changeStatus': '更改状态',
    'multiSelect.setLabels': '设置标签',
    'multiSelect.archive': '归档',
    'batchSessionMenu.unflagAll': '全部取消标记',
    'batchSessionMenu.flagAll': '全部标记',
    'fileViewer.failedToLoadFile': '加载文件失败',
    'fileViewer.noFileSelected': '未选择文件',
    'fileViewer.clickPathHint': '点击聊天中的文件路径即可在这里查看',
    'fileViewer.loadingContent': '正在加载内容...',
    'fileViewer.errorLoadingFile': '加载文件时出错',
    'permissionRequest.title': '需要权限',
    'permissionRequest.tool': '工具',
    'permissionRequest.allow': '允许',
    'permissionRequest.alwaysAllow': '始终允许',
    'permissionRequest.deny': '拒绝',
    'permissionRequest.tip': '“始终允许” 会在当前会话中记住这条命令',
    'adminApproval.title': '需要管理员批准',
    'adminApproval.installingNeedsApproval': '安装 {appName} 需要你的 Mac 管理员批准。',
    'adminApproval.passwordPrompt': '你会看到常规的 macOS 密码或 Touch ID 提示。',
    'adminApproval.why': '原因',
    'adminApproval.impact': '影响',
    'adminApproval.approve': '批准',
    'adminApproval.rememberAria': '记住这条命令 {minutes} 分钟',
    'adminApproval.rememberForMinutes': '记住 {minutes} 分钟',
    'searchableModel.searchPlaceholder': '搜索模型...',
    'searchableModel.noModelsFound': '未找到模型',
    'tableOfContents.noHeadings': '没有标题',
    'sessionStatusMenu.filterPlaceholder': '筛选状态...',
    'sessionStatusMenu.noStatusFound': '未找到状态',
    'sessionStatusMenu.unarchive': '取消归档',
    'labelValuePopover.datePlaceholder': '明天、下周五...',
    'labelValuePopover.selectDate': '选择日期',
    'labelValuePopover.numberPlaceholder': '输入数字...',
    'labelValuePopover.valuePlaceholder': '输入值...',
    'labelValuePopover.remove': '移除',
    'labelMenu.addNewLabel': '新增标签',
    'labelMenu.states': '状态',

    'workspaceSwitcher.selectWorkspace': '选择工作区',
    'workspaceSwitcher.workspace': '工作区',
    'workspaceSwitcher.openInNewWindow': '在新窗口中打开',
    'workspaceSwitcher.addWorkspace': '添加工作区...',
    'workspaceSwitcher.createdWorkspace': '已创建工作区“{name}”',

    'sidebarMenu.openInNewWindow': '在新窗口中打开',
    'sidebarMenu.markAllRead': '全部标记为已读',
    'sidebarMenu.configureStatuses': '配置状态',
    'sidebarMenu.addNewLabel': '新增标签',
    'sidebarMenu.editLabels': '编辑标签',
    'sidebarMenu.deleteLabel': '删除标签',
    'sidebarMenu.editViews': '编辑视图',
    'sidebarMenu.deleteView': '删除视图',
    'sidebarMenu.addSource': '添加数据源',
    'sidebarMenu.learnMoreSources': '了解更多数据源',
    'sidebarMenu.learnMoreApis': '了解更多 API',
    'sidebarMenu.learnMoreMcp': '了解更多 MCP',
    'sidebarMenu.learnMoreLocalFolders': '了解更多本地文件夹',
    'sidebarMenu.addSkill': '添加技能',
    'sidebarMenu.addAutomation': '添加动态',
    'sidebarMenu.learnMoreAutomations': '了解更多动态',

    'shell.newSession': '新建会话',
    'shell.inbox': '所有对话',
    'shell.plugins': '技能和应用',
    'shell.allSessions': '所有对话',
    'shell.flagged': '已标记',
    'shell.archived': '已归档',
    'shell.labels': '标签',
    'shell.views': '视图',
    'shell.sources': '数据源',
    'shell.skills': '技能',
    'shell.automations': '动态',
    'shell.automationRules': '自动化',
    'shell.channels': '频道',
    'shell.files': '文件',
    'shell.workbench': '工作台',
    'shell.settings': '设置',
    'shell.whatsNew': '新内容',
    'shell.apis': 'API',
    'shell.mcps': 'MCP',
    'shell.localFolders': '本地文件夹',
    'shell.scheduled': '定时',
    'shell.eventBased': '事件触发',
    'shell.agentic': 'Agentic',
    'shell.searchStatusesAndLabels': '搜索状态和标签...',
    'shell.filterChats': '筛选会话',
    'shell.statuses': '状态',
    'shell.group': '分组',
    'shell.date': '日期',
    'shell.status': '状态',
    'shell.search': '搜索',
    'shell.noLabelsConfigured': '还没有配置标签',
    'shell.noMatchingStatusesOrLabels': '没有匹配的状态或标签',
    'shell.allSkills': '全部技能',
    'shell.allAutomations': '全部动态',
    'shell.altClickToExclude': '按住 Alt 点击可排除',
    'shell.addSource': '添加数据源',
    'shell.addSkill': '添加技能',
    'shell.addAutomation': '添加动态',
    'shell.editFile': '编辑文件',
    'shell.failedCreateBrowserWindow': '创建浏览器窗口失败',
    'shell.failedDeleteSource': '删除数据源失败',
    'shell.failedDeleteSkill': '删除技能失败',
    'shell.deletedSource': '已删除数据源',
    'shell.deletedSkill': '已删除技能：{slug}',
    'sessionActions.flagged': '已标记会话',
    'sessionActions.flaggedDescription': '已添加到标记列表',
    'sessionActions.unflagged': '已取消标记',
    'sessionActions.unflaggedDescription': '已从标记列表移除',
    'sessionActions.archived': '已归档会话',
    'sessionActions.archivedDescription': '已移入归档',
    'sessionActions.restored': '已恢复会话',
    'sessionActions.restoredDescription': '已从归档中恢复',
    'sessionActions.deleted': '已删除会话',
    'shell.scheduledInfo': '定时任务需要你的机器保持运行。可以锁屏，但必须保持开机。',
    'shell.deleteAutomationTitle': '删除动态',
    'shell.deleteAutomationDescription': '确定要删除 {name} 吗？这会把这个动态从 automations.json 配置中移除。',
    'status.backlog': '待规划',
    'status.todo': '待办',
    'status.needsReview': '待审阅',
    'status.done': '已完成',
    'status.cancelled': '已取消',

    'main.source': '数据源',
    'main.skill': '技能',
    'main.noSourcesConfigured': '还没有配置数据源',
    'main.noSkillsConfigured': '还没有配置技能',
    'main.noAutomationsConfigured': '还没有配置动态',
    'main.noSessionSelected': '未选择会话',
    'main.selectConversation': '选择一个会话开始使用',

    'channels.empty.title': '还没有频道方案',
    'channels.empty.description': '这里会显示内置的 IM 频道接入方案。',
    'channels.notFound': '未找到频道',
    'channels.copied': '已复制到剪贴板',
    'channels.saved': '频道配置已保存',
    'channels.resetToSaved': '已恢复未保存的修改',
    'channels.overview.title': '频道',
    'channels.overview.tagline': '为微信、飞书、Slack、Discord、Telegram 等连接方式准备的三栏目录页。',
    'channels.overview.description': '选择一个平台，只配置当前接入真正需要的最少字段。',
    'channels.overview.connectedDescription': '这里会汇总你已经配置、验证或接入过的频道，方便直接回到对应平台继续处理。',
    'channels.backToAll': '全部频道',
    'channels.tab.discover': '发现',
    'channels.tab.connected': '已连接',
    'channels.connected.loading': '正在检查已连接频道...',
    'channels.connected.emptyTitle': '还没有已连接频道',
    'channels.connected.emptyDescription': '先去“发现”里接入一个频道。只要本地已经保存过配置，它就会出现在这里。',
    'channels.connected.openDiscover': '前往发现',
    'channels.connectionStyle': '连接方式',
    'channels.exactSessionSupport': '精确会话回链',
    'channels.recommended': '推荐使用场景',
    'channels.requiredFields': '必填字段',
    'channels.localConfig.title': '最小配置',
    'channels.localConfig.description': '这里只保留当前接入真正需要的字段。高级路由、预览和实现说明先全部隐藏。',
    'channels.status.configured': '已配置',
    'channels.status.configuredDescription': '必填字段已经补齐，这个频道已经具备接入真实后端的条件。',
    'channels.status.draft': '配置中',
    'channels.status.draftDescription': '已经开始填写，但还有必填字段没有补完。',
    'channels.status.disabledDescription': '这个频道已经保存到本地，但当前还没有启用。',
    'channels.coverage.full': '支持精确回链',
    'channels.coverage.partial': '部分支持回链',
    'channels.coverage.none': '暂不支持精确回链',
    'channels.methodKind.qr': '二维码配对',
    'channels.methodKind.deepLink': '深链',
    'channels.methodKind.webLink': '网页链接',
    'channels.methodKind.invite': '邀请链接',
    'channels.methodKind.oauth': 'OAuth / 控制台',
    'channels.methodKind.webhook': 'Webhook / 回调',
    'channels.section.coverage': '连接概览',
    'channels.section.coverageDescription': '当前前端模型下，这个平台能支持到什么程度。',
    'channels.section.methods': '内置链接方式',
    'channels.section.methodsDescription': '下面每张卡片都是已经在前端建模好的方案，后续只要接入真实凭证即可。',
    'channels.section.overviewMethodsDescription': '这些就是当前前端已经预留好的连接模式。',
    'channels.section.basicConfig': '基础配置',
    'channels.section.basicConfigDescription': '先决定是否启用这个频道，并设置一个清晰的展示名称。',
    'channels.section.platformConfig': '平台接入',
    'channels.section.platformConfigDescription': '这里只保留连接当前平台真正需要的最少字段。',
    'channels.section.connected': '已连接频道',
    'channels.section.connectedDescription': '这里会集中展示已经保存过本地配置的频道，并保留当前连接状态。',
    'channels.guidedSetup': '这个平台会走引导式接入流程，所以这里暂时不需要手动填写字段。',
    'channels.savedFailed': '频道配置保存失败。',
    'channels.connect': '连接',
    'channels.connected': '频道已连接',
    'channels.verified': '频道凭证已验证',
    'channels.connectFailed': '频道连接失败。',
    'channels.wechat.serviceTitle': '连接服务',
    'channels.wechat.serviceDescription': '把 XClaw 指向提供微信二维码接口的本地服务。',
    'channels.wechat.serviceFieldDescription': '通常就是提供 /api/v1/channels/wechat/* 的本地 controller 或 bridge 地址。',
    'channels.wechat.scanTitle': '微信扫码接入',
    'channels.wechat.scanDescription': '生成二维码后，用微信扫码，并等待本地服务把账号信息回传回来。',
    'channels.wechat.missingGatewayUrl': '请先填写本地服务地址。',
    'channels.wechat.connectFailed': '微信接入失败。',
    'channels.wechat.timeout': '等待微信扫码结果超时。',
    'channels.wechat.connected': '微信已连接',
    'channels.wechat.connectedDescription': '扫码得到的账号已经保存到本地，后续可以直接复用。',
    'channels.wechat.connectedLocalOnly': '微信账号已扫码并保存到本地。',
    'channels.wechat.accountIdLabel': '账号 ID',
    'channels.wechat.qrAlt': '微信二维码',
    'channels.wechat.scanning': '请使用微信扫描这个二维码',
    'channels.wechat.scanHint': '保持当前页面打开，等待本地服务完成账号回传。',
    'channels.wechat.waitingGateway': '正在等待本地服务就绪...',
    'channels.wechat.loadingQr': '正在请求二维码...',
    'channels.wechat.connecting': '正在绑定扫码得到的账号...',
    'channels.wechat.retry': '重试',
    'channels.wechat.idleDescription': '通过 XClaw 内置的本地桥接服务生成二维码，并接入一个个人微信账号。',
    'channels.wechat.idleUnavailableDescription': 'XClaw 还没准备好内置微信服务。稍后重试，或者查看日志确认启动情况。',
    'channels.wechat.scanAction': '扫描二维码',
    'channels.wechat.scanUnavailableAction': '微信服务暂不可用',
    'channels.wechat.scanPendingAction': '正在等待内置服务',
    'channels.wechat.gatewayOnline': '本地网关已连通',
    'channels.wechat.gatewayOffline': '本地网关不可用',
    'channels.wechat.gatewayChecking': '正在检查本地服务...',
    'channels.wechat.gatewayOnlineDescription': 'XClaw 已经可以访问当前可用的微信桥接服务，可用于二维码接入和账号回传。',
    'channels.wechat.gatewayOfflineDescription': 'XClaw 还无法访问当前可用的微信桥接服务。请等待应用完成启动，或查看日志后再扫码。',
    'channels.wechat.gatewayCheckingDescription': 'XClaw 正在检查当前可用的微信桥接服务是否可达。',
    'channels.wechat.gatewayDefaultUnavailable': 'XClaw 还没有连通内置微信桥接服务。',
    'channels.wechat.gatewayCustomUnavailable': 'XClaw 无法连接到当前配置的本地服务地址。',
    'channels.wechat.gatewayMissingServiceTitle': '内置微信服务暂不可用',
    'channels.wechat.gatewayMissingServiceDescription': '正常情况下，XClaw 会自己启动本地微信桥接服务。如果这个提示持续存在，说明内置服务还没有成功响应。你可以稍等片刻，或者在高级设置里改成别的兼容本地桥接地址做调试。',
    'channels.wechat.gatewayConnectFailed': '微信账号已经保存到本地，但网关侧还需要处理。',
    'channels.wechat.gatewayConnectFailedDescription': '二维码扫码本身已经成功，账号也保存到了本地，但网关还没有完成和 XClaw 的最终绑定。',
    'channels.wechat.savedLocallyTitle': '微信账号已保存到本地',
    'channels.wechat.savedLocallyDescription': '这台机器上已经存有一个扫码得到的微信账号，但 XClaw 还没有确认网关绑定是否完成。',
    'channels.section.credentials': '凭证',
    'channels.section.credentialsDescription': '这里填写平台标识、密钥和 bot 相关凭证。',
    'channels.section.routing': '路由与回调',
    'channels.section.routingDescription': '控制链接怎么生成、回调怎么预览、消息最后落到哪里。',
    'channels.section.preview': '方式预览',
    'channels.section.previewDescription': '用当前填写的字段，直接预览选中的链接模式会生成什么地址。',
    'channels.section.highlights': '亮点',
    'channels.section.highlightsDescription': '把适合的产品表达和实现边界提前讲清楚。',
    'channels.section.notes': '实现说明',
    'channels.section.notesDescription': '后面接后端时需要保留的关键注意点。',
    'channels.section.docs': '参考链接',
    'channels.section.docsDescription': '需要创建凭证时，可以直接打开官方控制台或设置文档。',
    'channels.section.platforms': '内置频道目录',
    'channels.section.platformsDescription': '你可以先从中间列表选，也可以直接从下面卡片跳进去。',
    'channels.section.connectionStatus': '连接状态',
    'channels.section.connectionStatusDescription': '先确认这个频道现在是否可用；只有在需要回调地址或排查问题时，再展开高级设置。',
    'channels.section.conversations': '已绑定对话',
    'channels.section.conversationsDescription': '这里展示已经映射成 XClaw 工作区会话的频道线程。',
    'channels.section.advanced': '高级设置',
    'channels.section.advancedDescription': '只有在你需要自定义回调地址、自定义本地桥接地址，或者排查问题时，才需要展开这里。',
    'channels.section.nextStep': '下一步',
    'channels.advanced.show': '显示高级设置',
    'channels.advanced.hide': '收起高级设置',
    'channels.action.addBot': '添加 Bot',
    'channels.action.addBotDescription': '打开邀请链接，完成平台侧的 Bot 安装。',
    'channels.action.openPlatform': '打开平台',
    'channels.action.openPlatformDescription': '打开平台页面，完成剩余的配置步骤。',
    'channels.conversations.unread': '未读',
    'channels.conversations.lastInbound': '最近入站',
    'channels.conversations.open': '打开对话',
    'channels.conversations.emptyTitle': '还没有对话',
    'channels.conversations.emptyDescription': '一旦微信消息进入 XClaw，对应映射的工作区会话就会显示在这里。',
    'channels.conversations.emptyDescriptionGeneric': '一旦这个频道有消息进入 XClaw，对应映射的工作区会话就会显示在这里。',
    'channels.previewValue': '预览结果',
    'channels.previewEmpty': '选择一种方式后，这里会显示生成出来的链接或端点。',
    'channels.field.enabled': '启用这个频道',
    'channels.field.enabledDescription': '只有启用后的频道，后续才应该被运行时绑定真正接入。',
    'channels.field.displayName': '展示名称',
    'channels.field.displayNameDescription': '显示在右侧标题里，也是这个集成的人类可读名称。',
    'channels.field.connectionMethod': '连接方式',
    'channels.field.connectionMethodDescription': '选择这个频道当前要驱动的内置链接模式。',
    'channels.field.gatewayUrl': '网关地址',
    'channels.field.gatewayUrlDescription': '默认留空就会使用内置桥接。只有在你想接到别的本地服务做调试时，才需要修改这里。',
    'channels.field.accountId': '账号 ID',
    'channels.field.accountIdDescription': '完成扫码配对后，由网关返回的账号标识。',
    'channels.field.appId': 'App ID',
    'channels.field.appIdDescription': '从飞书开放平台拿到的应用标识。',
    'channels.field.appSecret': 'App Secret',
    'channels.field.appSecretDescription': '和 App ID 一起使用，用来换取租户 token 和订阅事件。',
    'channels.field.openChatId': 'Open Chat ID',
    'channels.field.openChatIdDescription': '如果你希望回到精确 bot 会话，这个字段就是必需的。',
    'channels.field.inviteToken': '邀请 Token',
    'channels.field.inviteTokenDescription': '用于生成群邀请链接，适合社区分发或灰度放量。',
    'channels.field.botToken': 'Bot Token',
    'channels.field.botTokenDescription': '平台侧的 bot 凭证，用于消息操作和频道接入。',
    'channels.field.signingSecret': '签名密钥',
    'channels.field.signingSecretDescription': '用来校验平台回调事件是否真实可信。',
    'channels.field.teamId': '团队 ID',
    'channels.field.teamIdDescription': '原生客户端和网页回退链接都需要这个字段。',
    'channels.field.botUserId': 'Bot 用户 ID',
    'channels.field.botUserIdDescription': '和团队 ID 组合后，才能跳进目标 bot 会话。',
    'channels.field.applicationId': '应用 ID',
    'channels.field.applicationIdDescription': 'Discord 的 application/client ID，用于生成邀请链接。',
    'channels.field.serverId': '服务器 ID',
    'channels.field.serverIdDescription': '可选的 guild/server 标识，后续做路由或过滤时会用到。',
    'channels.field.botUsername': 'Bot 用户名',
    'channels.field.botUsernameDescription': '公开 bot 句柄，不带前面的 @。',
    'channels.field.chatId': '聊天 ID',
    'channels.field.chatIdDescription': '可选的聊天或群组标识，后面做回调映射时会用到。',
    'channels.field.startPayload': 'Start 参数',
    'channels.field.startPayloadDescription': '用于给 bot deeplink 附带一段轻量上下文。',
    'channels.field.webhookUrl': 'Webhook 地址',
    'channels.field.webhookUrlDescription': '测试阶段可选填写的 webhook 或事件入口。',
    'channels.field.callbackBaseUrl': '回调基础地址',
    'channels.field.callbackBaseUrlDescription': '用于拼接本地回调端点和 webhook 预览地址。',
    'channels.field.publicBaseUrl': '公网基础地址',
    'channels.field.publicBaseUrlDescription': '平台回调真正访问的 HTTPS 地址，需要转发到当前这台 XClaw 实例。',
    'channels.field.notes': '备注',
    'channels.field.notesDescription': '用于记录上线环境差异、租户信息或路由决策。',
    'channels.field.notesPlaceholder': '补充一些环境说明、租户备注或路由约定...',
    'channels.status.idle': '未连接',
    'channels.status.idleDescription': '先保存凭证，再执行一次连接校验当前平台配置。',
    'channels.status.saved': '已保存',
    'channels.status.savedDescription': '配置已经保存，需要重新执行连接来验证当前凭证。',
    'channels.status.verified': '已验证',
    'channels.status.connected': '已连接',
    'channels.status.error': '异常',
    'channels.lastActivity': '最近活动',
    'channels.connection.runtime': '本地运行时',
    'channels.connection.callbackPath': '回调路径',
    'channels.connection.localCallbackUrl': '本地回调地址',
    'channels.connection.callbackUrl': '公网回调地址',
    'channels.connection.openUrl': '打开链接',
    'channels.connection.inviteUrl': '邀请链接',
    'channels.open': '打开',
    'channels.message.slackVerifiedWithCallback': 'Slack 凭证已校验通过，下一步把下面的回调地址填到 Slack Event Subscriptions。',
    'channels.message.slackVerifiedNeedsPublicBaseUrl': 'Slack 凭证已校验通过，但要接平台回调，还需要先补上公网基础地址。',
    'channels.message.discordVerified': 'Discord 凭证已校验通过，下一步用下面的邀请链接把 bot 安装进目标服务器。',
    'channels.message.feishuVerifiedWithCallback': '飞书凭证已校验通过，下一步把下面的回调地址配置到飞书事件订阅里。',
    'channels.message.feishuVerifiedNeedsPublicBaseUrl': '飞书凭证已校验通过，但要接回调，还需要先补上公网基础地址。',
    'channels.message.feishuLongConnectionStarting': '飞书凭证已校验通过，XClaw 正在启动内置长连接。',
    'channels.message.feishuLongConnectionConnected': '飞书内置长连接已接通，现在不用公网回调地址也能把消息送到这台 XClaw。',
    'channels.message.feishuLongConnectionFailed': '飞书凭证已校验通过，但 XClaw 暂时没能启动内置长连接。',
    'channels.message.telegramVerifiedNeedsPublicBaseUrl': 'Telegram Bot Token 已校验通过，但要启用 webhook，还需要先补上公网基础地址。',
    'channels.message.telegramConnected': 'Telegram Bot Token 已校验通过，webhook 也已经完成配置。',
    'channels.message.slackWebhookReceived': 'XClaw 已经收到 Slack 回调。',
    'channels.message.feishuWebhookReceived': 'XClaw 已经收到飞书回调。',
    'channels.message.telegramWebhookReceived': 'XClaw 已经收到 Telegram 回调。',
    'channels.message.wechatSaved': '微信账号已经保存到本地。',
    'channels.warning.publicBaseUrlInvalid': '公网基础地址格式不正确。',
    'channels.warning.publicBaseUrlRequired': '还需要补上公网基础地址，平台侧才能真正访问这个回调端点。',
    'channels.warning.runtimeUnavailable': '本地回调运行时当前不可用，所以平台回调暂时还打不进来。',
    'channels.warning.feishuLongConnectionUnavailable': '飞书内置长连接当前不可用：{reason}',
    'channels.warning.discordInviteRequired': '还需要用下面的邀请链接把 bot 安装进目标 Discord 服务器。',
    'channels.warning.runtimeUnavailableWithReason': '频道回调运行时当前不可用：{reason}',

    'sources.type.api': 'API',
    'sources.type.mcp': 'MCP',
    'sources.type.local': '本地',
    'sources.status.connected': '已连接',
    'sources.status.authRequired': '需要鉴权',
    'sources.status.disconnected': '已断开',
    'sources.status.notTested': '未测试',
    'sources.status.disabled': '已禁用',
    'sources.filter.api': 'API',
    'sources.filter.mcp': 'MCP',
    'sources.filter.localFolder': '本地文件夹',
    'sources.empty.filtered': '还没有配置 {type} 数据源。',
    'sources.empty.none': '还没有配置数据源。',
    'sources.description': '数据源让你的 agent 可以连接外部数据，包括 MCP 服务、REST API 和本地文件夹。',
    'sources.addSource': '添加数据源',
    'mcpWorkspace.title': 'MCP',
    'mcpWorkspace.tagline': '把 Model Context Protocol 服务接进来，为 agent 提供工具、上下文和外部动作。',
    'mcpWorkspace.addMcp': '新增 MCP',
    'mcpWorkspace.backToAll': '全部 MCP',
    'mcpWorkspace.stats.total': '已安装',
    'mcpWorkspace.stats.connected': '已连接',
    'mcpWorkspace.stats.attention': '待处理',
    'mcpWorkspace.stats.local': '本地服务',
    'mcpWorkspace.tab.all': '全部',
    'mcpWorkspace.tab.attention': '待处理',
    'mcpWorkspace.tab.connected': '已连接',
    'mcpWorkspace.tab.local': '本地',
    'mcpWorkspace.tab.remote': '远程',
    'mcpWorkspace.empty.title': '还没有 MCP 数据源',
    'mcpWorkspace.empty.description': '把远程 MCP 服务或本地 stdio 服务接进来，让 agent 可以使用外部工具。',
    'mcpWorkspace.empty.filtered': '当前筛选下没有 MCP 数据源。',
    'mcpWorkspace.empty.filteredDescription': '可以切到别的筛选，或者为这个工作区新增一个 MCP。',
    'mcpWorkspace.cardMenu': 'MCP 操作',
    'mcpWorkspace.openDetails': '查看详情',
    'mcpWorkspace.authenticate': '去鉴权',
    'mcpWorkspace.openSettings': '打开设置',
    'mcpWorkspace.inspect': '查看',
    'mcpWorkspace.authBearer': 'Bearer',
    'mcpWorkspace.authNone': '无鉴权',
    'mcpWorkspace.serverUrl': '服务地址',
    'mcpWorkspace.command': '启动命令',
    'mcpWorkspace.localServersDisabled': '本地 MCP 服务已关闭',
    'mcpWorkspace.localServersDisabledDescription': '在工作区设置里打开本地 MCP 服务后，stdio 类型的 MCP 工具才能使用。',

    'skills.empty.none': '还没有配置技能',
    'skills.empty.addFirst': '还没有安装技能',
    'skills.empty.browseStore': '去“发现”标签页里浏览并安装技能。',
    'skills.description': '技能是可复用的指令，用来教会 agent 特定的行为模式。',
    'skills.addSkill': '添加技能',
    'skills.discover': '发现',
    'skills.installed': '已安装',
    'skills.install': '安装',
    'skills.installedState': '已安装',
    'skills.seeAll': '查看全部',
    'skills.showLess': '收起',
    'skills.backToAll': '全部技能',
    'skills.backToStore': '技能商店',
    'skills.cardMenu': '技能操作',
    'skills.openDetails': '查看详情',
    'skills.runSkill': '运行',
    'skills.showInFinder': '在访达中显示',
    'skills.sourceLabel': '来源',
    'skills.source.claudeCode': 'Claude Code',
    'skills.source.cursor': 'Cursor',
    'skills.source.global': '全局',
    'skills.source.workspace': '工作区',
    'skills.source.project': '项目',
    'skills.category.featured': '精选',
    'skills.category.productivity': '效率',
    'skills.category.design': '设计',
    'skills.category.contentCreation': '内容创作',
    'skills.category.development': '开发',
    'annotations.followUp.title': '跟进',
    'annotations.followUp.placeholder': '补充一些希望 agent 在下一轮考虑的说明…',
    'annotations.followUp.moreSubmitActions': '更多提交操作',
    'chat.escapeInterruptPrefix': '按',
    'chat.escapeInterruptSuffix': '再次中断',
    'chat.empty.changePrompt': '你想改点什么？',
    'chat.empty.changeHint': '直接说出来就行，剩下的我来处理。',
    'authRequest.status.connected': '{name} 已连接',
    'authRequest.status.cancelled': '{name} 已取消',
    'authRequest.status.failed': '{name} 失败',
    'authRequest.signedInAs': '已登录：{email}',
    'authRequest.workspace': '工作区：{name}',
    'authRequest.enterValue': '输入 {label}',
    'authRequest.signInWith': '使用 {name} 登录',
    'authRequest.credentialsEncryptedAtRest': '凭证会以加密形式存储',
    'resetApp.title': '重置应用',
    'resetApp.description': '这会永久删除：',
    'resetApp.deleteWorkspaces': '所有工作区及其设置',
    'resetApp.deleteCredentials': '所有凭证和 API Key',
    'resetApp.deletePreferences': '所有偏好和会话数据',
    'resetApp.backupFirst': '请先备份重要数据！',
    'resetApp.cannotUndo': '这个操作无法撤销。',
    'resetApp.solvePrompt': '确认前请先计算：',
    'resetApp.enterAnswer': '输入答案',
    'files.loadingPreview': '正在加载预览...',
    'files.previewUnavailable': '暂不支持预览',
    'files.previewUnavailableDescription': '这种文件类型暂时还不能在应用内预览，你仍然可以用系统应用打开它。',
    'overlay.toolFailed': '工具执行失败',
    'overlay.copyHtml': '复制 HTML',
    'overlay.pdfPreview': 'PDF 预览',
    'overlay.htmlPreview': 'HTML 预览',
    'overlay.loadingHtml': '正在加载 HTML...',
    'overlay.failedReadPdfFile': '读取 PDF 文件失败',
    'overlay.failedReadHtmlFile': '读取 HTML 文件失败',
    'overlay.failedReadDataFile': '读取数据文件失败',
    'overlay.failedParseDataFile': '将数据文件解析为 JSON 失败',
    'overlay.invalidDatatableData': '文件里没有有效的数据表内容',
    'overlay.invalidSpreadsheetData': '文件里没有有效的表格内容',
    'overlay.failedLoadImageCount': '有 {count} 张图片加载失败',
    'overlay.inputParams': '输入参数',
    'overlay.code': '代码',
    'overlay.read': '读取',
    'overlay.write': '写入',
    'overlay.readFailed': '读取失败',
    'overlay.writeFailed': '写入失败',
    'overlay.loadingImage': '正在加载图片...',
    'overlay.loadingPdf': '正在加载 PDF...',
    'overlay.imagePreview': '图片预览',
    'terminal.copyCommand': '复制命令',
    'terminal.copyOutput': '复制输出',
    'turnCard.viewFileChanges': '查看文件变更',
    'turnCard.viewTurnDetails': '查看回合详情',
    'turnCard.acceptPlan': '接受方案',
    'turnCard.accept': '接受',
    'turnCard.acceptAndSendFollowUps': '接受并发送跟进',
    'turnCard.branchOptions': '分支选项',
    'turnCard.branchFromThisMessage': '从这条消息创建分支',
    'turnCard.branchDescription': '在不打断当前对话流程的前提下，探索另一条处理方向。',
    'turnCard.streaming': '输出中...',
    'turnCard.todoList': '任务清单',
    'turnCard.thinking': '思考中...',
    'turnCard.preparingResponse': '准备回复中...',
    'skills.section.description': '描述',
    'skills.section.source': '来源',
    'skills.section.screenshots': '截图',

    'files.home': '主页',
    'files.desktop': '桌面',
    'files.downloads': '下载',
    'files.workspace': '工作区',
    'files.skills': '技能',
    'files.searchPlaceholder': '搜索',
    'files.loading': '正在加载文件...',
    'files.loadError': '加载目录失败',
    'files.empty': '没有找到文件',
    'files.name': '名称',
    'files.modified': '修改时间',
    'files.size': '大小',
    'files.items': '{count} 个项目',
    'files.selected': '已选择 1 项',
    'files.noneSelected': '未选择',
    'files.drop.prompt': '松开后移动到这里',
    'files.drop.description': '拖入的文件会被移动到当前文件夹。',
    'files.drop.importing': '正在移动文件...',
    'files.drop.success': '已将 {count} 个文件移动到当前文件夹。',
    'files.drop.partial': '已移动 {count} 个文件，跳过 {skipped} 个。',
    'files.drop.failed': '将拖入文件移动到当前文件夹失败。',
    'files.drop.noLocalFiles': '这次拖拽里没有读取到可用的本地文件路径。',

    'sourceMenu.showInFileManager': '在 {name} 中显示',
    'sourceMenu.deleteSource': '删除数据源',
    'skillMenu.showInFileManager': '在 {name} 中显示',
    'skillMenu.deleteSkill': '删除技能',

    'automationMenu.enable': '启用',
    'automationMenu.disable': '停用',
    'automationMenu.runTest': '运行测试',
    'automationMenu.editConfiguration': '编辑配置',
    'automations.empty.none': '还没有配置自动规则',
    'automations.empty.create': '创建你的第一条自动规则',
    'automations.empty.hint': '点击上方「新建规则」来设置定时任务、事件触发或提示自动化。',
    'automations.empty.examples': '这里有一些示例，帮你快速开始。',
    'automations.paused': '已暂停',
    'automations.pageDescription': '创建自动规则，让任务自动运行，并为 Pulse 简报提供输入。',
    'automations.pulsePreviewDescription': '简报页展示 Pulse 生成的每日总结，包括卡片、持续关注点和后续建议。',
    'automations.rulesDetailDescription': '自动规则页展示这些工作流的触发条件、运行历史和控制项。',
    'automations.description': '自动规则会在事件发生时执行动作，可以按计划运行命令、响应标签变化，或自动触发提示。',
    'automations.addAutomation': '添加规则',
    'automations.new': '新建动态',
    'automations.newRule': '新建规则',
    'automations.backToAll': '全部规则',
    'automations.optimize': '优化简报',
    'automations.viewMode.pulse': '简报',
    'automations.viewMode.rules': '自动规则',
    'automations.viewRules': '打开规则',
    'automations.viewRulesCount': '查看 {count} 条自动规则',
    'automations.tab.all': '全部',
    'automations.tab.enabled': '已启用',
    'automations.tab.disabled': '未启用',
    'automations.searchPlaceholder': '搜索规则...',
    'automations.noneFound': '未找到规则',
    'automations.noneConfigured': '还没有配置自动规则。',
    'automations.clearSearch': '清除搜索',
    'automations.prompt': '提示',
    'automations.webhook': 'Webhook',
    'automations.lastRan': '上次运行于 {time}',
    'automations.example.emailDigest': '每日邮件摘要',
    'automations.example.emailDigestDesc': '按发件人和优先级汇总过去 24 小时的未读邮件，并标出待处理事项。',
    'automations.example.birthday': '生日与纪念日提醒',
    'automations.example.birthdayDesc': '检查即将到来的生日和纪念日，并根据过往偏好推荐礼物想法。',
    'automations.example.creative': '创意项目追踪',
    'automations.example.creativeDesc': '跟踪个人或副业项目进度，并在卡住时给出下一步建议。',
    'automations.example.sprint': '每周冲刺总结',
    'automations.example.sprintDesc': '每周五汇总已完成和计划中的任务，计算节奏，并预估下个冲刺容量。',
    'automations.example.health': '系统健康监控',
    'automations.example.healthDesc': '每日检查关键业务工具和服务，并在宕机或性能异常时提醒。',
    'automations.example.expense': '费用报表整理',
    'automations.example.expenseDesc': '从邮件和应用中收集票据，自动分类，并起草每月费用报表。',

    'batchAutomation.enabled': '已启用 {count} 个{noun}',
    'batchAutomation.disabled': '已停用 {count} 个{noun}',
    'batchAutomation.deleted': '已删除 {count} 个{noun}',
    'batchAutomation.selected': '已选择 {count} 个{noun}',
    'batchAutomation.automation': '规则',
    'batchAutomation.automations': '规则',
    'batchAutomation.enableAll': '全部启用',
    'batchAutomation.disableAll': '全部停用',

    'workspace.failedCreate': '创建工作区失败',
    'workspace.close': '关闭',
    'workspace.addWorkspace': '添加工作区',
    'workspace.choiceDescription': '让你的想法和落地工具在这里汇合。',
    'workspace.createNew': '新建工作区',
    'workspace.startFresh': '从空白工作区开始。',
    'workspace.openFolder': '打开文件夹',
    'workspace.chooseExistingFolder': '选择一个已有文件夹作为工作区。',
    'workspace.back': '返回',
    'workspace.createWorkspace': '创建工作区',
    'workspace.createDescription': '输入名称并选择工作区的存放位置。',
    'workspace.name': '工作区名称',
    'workspace.namePlaceholder': '我的工作区',
    'workspace.defaultLocation': '默认位置',
    'workspace.defaultLocationSubtitle': '位于 .xclaw 文件夹下',
    'workspace.chooseLocation': '选择位置',
    'workspace.chooseLocationSubtitle': '选择新工作区要创建到哪里。',
    'workspace.creating': '创建中...',
    'workspace.create': '创建',
    'workspace.slugExists': '名为 “{slug}” 的工作区已存在',
    'workspace.chooseExistingTitle': '选择已有文件夹',
    'workspace.chooseExistingDescription': '选择任意文件夹作为工作区。',
    'workspace.noFolderSelected': '尚未选择文件夹',
    'workspace.opening': '打开中...',
    'workspace.open': '打开',

    'headerMenu.openInNewWindow': '在新窗口中打开',
    'headerMenu.learnMore': '了解更多',
    'sessionMenu.unflag': '取消标记',
    'sessionMenu.archive': '归档',
    'sessionMenu.unarchive': '取消归档',
    'sessionMenu.markAsUnread': '标记为未读',
    'sessionMenu.rename': '重命名',
    'sessionMenu.regenerateTitle': '重新生成标题',
    'sessionMenu.openInNewPanel': '在新面板中打开',
    'sessionMenu.copyPath': '复制路径',
    'sessionMenu.share': '分享',
    'sessionMenu.shared': '已分享',
    'sessionMenu.status': '状态',
    'sessionMenu.labels': '标签',
    'sessionMenu.flag': '标记',
    'sessionMenu.linkCopied': '链接已复制到剪贴板',
    'sessionMenu.failedShare': '分享失败',
    'sessionMenu.pathCopied': '路径已复制到剪贴板',
    'sessionMenu.titleRefreshed': '标题已刷新',
    'sessionMenu.failedRefreshTitle': '刷新标题失败',
    'sessionMenu.unknownError': '未知错误',
    'shareMenu.openInBrowser': '在浏览器中打开',
    'shareMenu.copyLink': '复制链接',
    'shareMenu.updateShare': '更新分享',
    'shareMenu.stopSharing': '停止分享',
    'shareMenu.shareUpdated': '分享已更新',
    'shareMenu.failedUpdateShare': '更新分享失败',
    'shareMenu.sharingStopped': '已停止分享',
    'shareMenu.failedStopSharing': '停止分享失败',
    'sessionSearch.placeholder': '搜索标题和内容...',
    'sessionSearch.close': '关闭搜索',
    'sessionSearch.loading': '加载中…',
    'sessionSearch.results': '{count} 条结果',
    'sessionList.noResultsInCurrentFilter': '当前筛选下没有结果',
    'sessionList.noSessionsFound': '未找到会话',
    'sessionList.searchedTitlesAndContent': '已搜索标题和消息内容',
    'sessionList.clearSearch': '清除搜索',
    'sessionList.sessions': '会话',
    'sessionList.group.today': '今天',
    'sessionList.group.yesterday': '昨天',
    'sessionList.group.currentView': '当前视图',
    'sessionList.group.otherConversations': '其他对话',
    'sessionList.archivedEmptyTitle': '暂无已归档对话',
    'sessionList.archivedEmptyDescription': '你归档的对话会显示在这里。归档可以让列表更整洁，同时保留原有对话内容。',
    'sessionList.emptyTitle': '还没有对话',
    'sessionList.emptyDescription': '你与 AI 的对话会显示在这里。新建一个即可开始。',
    'sessionList.renameSession': '重命名会话',
    'sessionList.enterSessionName': '输入会话名称...',
    'sessionInfo.titleLabel': '标题',
    'sessionInfo.untitledPlaceholder': '未命名',
    'sessionFiles.title': '会话文件',
    'sessionFiles.viewInFileManager': '在 {name} 中查看',
    'sessionFiles.showInFileManager': '在 {name} 中显示',
    'sessionFiles.emptyDescription': '这个对话附加或生成的文件会显示在这里。',
    'sessionFiles.directory': '文件夹',
    'sessionFiles.expandAction': '展开',
    'sessionFiles.revealAction': '显示',
    'sessionFiles.itemTooltip': '{path}\n{type}\n\n单击以{action}，双击以打开',
    'chat.sessionFallbackTitle': '会话',
    'chat.sessionMissing': '这个会话已不存在',
    'mentionMenu.header': '提及文件、技能或数据源',
    'mentionMenu.noResults': '没有结果',
    'mentionMenu.skill': '技能',
    'mentionMenu.source': '数据源',
    'skillInfo.notFound': '未找到技能',
    'skillInfo.failedLoad': '加载技能失败',
    'skillInfo.deleted': '已删除技能：{name}',
    'skillInfo.failedDelete': '删除技能失败',
    'skillInfo.installed': '已安装技能：{name}',
    'skillInfo.failedInstall': '安装技能失败',
    'skillInfo.requiredSources': '所需数据源',
    'skillInfo.permissionModes': '权限模式',
    'skillInfo.permissionModesDescription': '“始终允许的工具” 与权限模式的交互方式：',
    'skillInfo.permission.explore': '浏览',
    'skillInfo.permission.exploreDescription': '会被阻止，写入工具始终不可用',
    'skillInfo.permission.ask': '请求后编辑',
    'skillInfo.permission.askDescription': '自动批准，允许的工具不会再弹提示',
    'skillInfo.permission.auto': '自动',
    'skillInfo.permission.autoDescription': '无影响，所有工具本来就会自动批准',
    'skillInfo.noInstructions': '*暂无说明。*',
    'labels.searchPlaceholder': '搜索标签...',
    'labels.emptyConfigured': '还没有配置标签',
    'serverDirectoryBrowser.title': '选择服务端目录',
    'serverDirectoryBrowser.enterPath': '输入路径...',
    'serverDirectoryBrowser.go': '前往',
    'serverDirectoryBrowser.failedListDirectory': '读取目录失败',
    'serverDirectoryBrowser.truncated': '当前只显示前 {shown} 个文件夹，共 {total} 个。若缺少目标文件夹，请进一步缩小路径范围。',
    'serverDirectoryBrowser.noSubdirectories': '没有子目录。可以用上面的路径输入框继续导航。',
    'serverDirectoryBrowser.symlink': '符号链接',
    'serverDirectoryBrowser.manualHint': '输入服务端上的完整路径：',
    'serverDirectoryBrowser.wrongPlatformPath': '这个路径看起来来自另一种操作系统，请输入服务端上实际存在的路径。',
    'sourceInfo.notFound': '未找到数据源',
    'sourceInfo.failedLoad': '加载数据源失败',
    'sourceInfo.failedLoadTools': '加载工具失败',
    'sourceInfo.deleted': '已删除数据源：{name}',
    'sourceInfo.failedDelete': '删除数据源失败',
    'sourceInfo.sourceDisabled': '数据源已禁用',
    'sourceInfo.sourceDisabledDescription': '本地 MCP 服务在“设置 > 高级”中被禁用。启用后才能使用这个数据源。',
    'sourceInfo.connectionDescription.mcpStdio': '用于启动这个 MCP 服务的本地命令。',
    'sourceInfo.connectionDescription.mcpRemote': '服务地址和当前连接状态。',
    'sourceInfo.connectionDescription.api': '发起 API 请求时使用的基础地址。',
    'sourceInfo.connectionDescription.local': '这个数据源对应的文件系统路径。',
    'sourceInfo.connectionDescription.default': '连接详情。',
    'sourceInfo.permissionsDescription.mcp': 'Explore 模式下允许使用的工具模式。',
    'sourceInfo.permissionsDescription.api': 'Explore 模式下允许访问的 API 端点。',
    'sourceInfo.permissionsDescription.default': 'Explore 模式下的访问规则。',
    'sourceInfo.lastTested': '上次测试',
    'sourceInfo.toolsDescription': '这个服务暴露出来的操作。',
    'sourceInfo.documentationDescription': '提供给 agent 的上下文和使用准则。',
    'automationInfo.paused': '已暂停',
    'automationInfo.pausedDescription': '这个动态当前处于关闭状态。启用后会再次开始运行。',
    'automationInfo.when': '何时触发',
    'automationInfo.whenDescription': '这个动态由什么触发',
    'automationInfo.onlyWhenMatching': '仅在匹配时',
    'automationInfo.repeats': '重复方式',
    'automationInfo.scheduleExpression': '调度表达式',
    'automationInfo.nextRuns': '接下来运行',
    'automationInfo.if': '触发条件',
    'automationInfo.ifDescription': '动作执行前必须满足的条件',
    'automationInfo.then': '执行动作',
    'automationInfo.thenDescription': '将执行 {count} 个{noun}',
    'automationInfo.action': '动作',
    'automationInfo.actions': '动作',
    'automationInfo.accessLevel': '访问级别',
    'automationInfo.lastRuns': '最近 {count} 次运行',
    'automationInfo.rawConfig': '原始配置',
    'automationInfo.noActivityYet': '还没有活动记录。',
    'automationInfo.openSession': '打开会话',
    'automationInfo.retry': '重试',
    'automationInfo.copyPayload': '复制负载',
    'automationInfo.noResponse': '无响应',
    'automationInfo.attempts': '尝试次数',
    'automationInfo.emptyPrompt': '空提示词',

    // Automation utility translations (utils.ts, types.ts)
    'automation.justNow': '刚刚',
    'automation.minAgo': '{n}分钟前',
    'automation.hrAgo': '{n}小时前',
    'automation.dAgo': '{n}天前',
    'automation.invalidSchedule': '无效调度',
    'automation.everyMinute': '每分钟',
    'automation.everyNMinutes': '每 {n} 分钟',
    'automation.everyHourAt': '每小时的 :{mm}',
    'automation.dailyAt': '每天 {time}',
    'automation.weekdaysAt': '工作日 {time}',
    'automation.weekendsAt': '周末 {time}',
    'automation.atTimeWeekday': '{time}（星期规则: {dow}）',
    'automation.monthlyOnDay': '每月 {dom} 日 {time}',
    'automation.durationMs': '{n}毫秒',
    'automation.durationS': '{n}秒',
    'automation.matching': '匹配',
    'automation.atLabel': '于',
    'automation.field.permissionMode': '权限模式',
    'automation.field.sessionStatus': '会话状态',
    'automation.field.isFlagged': '标记状态',
    'automation.field.labels': '标签',
    'automation.field.sessionName': '会话名称',
    'automation.afterTime': '{time} 之后',
    'automation.beforeTime': '{time} 之前',
    'automation.anyTime': '任意时间',
    'automation.any': '任意',
    'automation.changedFromTo': '{field} 从 {from} 变为 {to}',
    'automation.hasContaining': '{field} 包含 "{value}"',
    'automation.notFlagged': '未标记',
    'automation.isFlagged': '已标记',
    'automation.fieldIsNot': '{field} 不为 {value}',
    'automation.fieldIs': '{field} 为 {value}',
    'automation.andNot': '且不',
    'automation.and': '且',
    'automation.or': '或',
    'automation.unknownCondition': '未知条件',
    'automation.conditionTime': '时间',
    'automation.conditionState': '状态',
    'automation.condition': '条件',
    'automation.event.LabelAdd': '已添加标签',
    'automation.event.LabelRemove': '已移除标签',
    'automation.event.LabelConfigChange': '标签设置已变更',
    'automation.event.PermissionModeChange': '权限已变更',
    'automation.event.FlagChange': '标记已变更',
    'automation.event.TodoStateChange': '任务已更新',
    'automation.event.SessionStatusChange': '状态已变更',
    'automation.event.SchedulerTick': '定时',
    'automation.event.PreToolUse': '工具运行前',
    'automation.event.PostToolUse': '工具运行后',
    'automation.event.PostToolUseFailure': '工具失败时',
    'automation.event.Notification': '通知',
    'automation.event.UserPromptSubmit': '消息发送后',
    'automation.event.SessionStart': '会话开始',
    'automation.event.SessionEnd': '会话结束',
    'automation.event.Stop': 'Agent 已停止',
    'automation.event.SubagentStart': '子 Agent 开始',
    'automation.event.SubagentStop': '子 Agent 结束',
    'automation.event.PreCompact': '压缩记忆前',
    'automation.event.PermissionRequest': '请求权限时',
    'automation.event.Setup': '初始设置',
    'automation.permission.safe': 'Explore',
    'automation.permission.ask': '请求',
    'automation.permission.allowAll': '执行',
    'automation.nextRun': '下次运行：{time}（{tz}）',
    'automation.matches': '匹配：{pattern}',
    'automation.onEvent': '触发于 {event}',

    // Pulse
    'pulse.notEnabled.title': '每天主动帮你整理值得看的事',
    'pulse.notEnabled.description': '动态会根据你最近的对话和关注方向，每天生成一份个性化简报。你可以展开详情、继续聊聊，或者告诉它明天多看什么。',
    'pulse.notEnabled.enabling': '开启中...',
    'pulse.notEnabled.enable': '开启动态',
    'pulse.generating.title': '正在生成今天的动态',
    'pulse.generating.description': '系统正在分析你的近期上下文并准备今日内容，请稍等片刻。',
    'pulse.emptyToday.noUpdates': '今天暂无新的动态',
    'pulse.emptyToday.startFirst': '先开始第一段对话',
    'pulse.emptyToday.keepChatting': '继续对话、连接更多数据源，动态会越来越懂你。',
    'pulse.emptyToday.noHistory': '你现在还没有任何会话内容，所以动态暂时不会生成。先聊一聊、留下上下文，动态再来整理当天重点。',
    'pulse.failed.title': '今天的动态生成失败了',
    'pulse.failed.retryHint': '你可以立即重试一次。',
    'pulse.failed.refreshing': '刷新中...',
    'pulse.failed.regenerate': '重新生成',
    'pulse.backToToday': '回到今天',
    'pulse.refresh': '刷新',
    'pulse.toast.loadFailed': 'Pulse 加载失败',
    'pulse.toast.enableFailed': 'Pulse 启用失败',
    'pulse.toast.refreshFailed': 'Pulse 刷新失败',
    'pulse.toast.chatFailed': '创建会话失败',
    'pulse.historyAriaLabel': '查看历史',
    'pulse.cardDrawer.summary': '摘要',
    'pulse.cardDrawer.whyThis': '为什么会看到这条',
    'pulse.cardDrawer.sources': '来源',
    'pulse.cardDrawer.imageSource': '配图来源',
    'pulse.cardDrawer.nextSteps': '下一步',
    'pulse.cardDrawer.nextActions': '下一步动作',
    'pulse.cardDrawer.continueChat': '继续聊聊',
    'pulse.cardDrawer.closeDetails': '关闭详情',
    'pulse.cardDrawer.drawerDescription': '{kind}卡片详情，包含摘要、为什么会看到这条、来源和后续动作。',
    'pulse.cardDrawer.pulseDetail': '动态详情',
    'pulse.cardDrawer.authorBy': '作者 {author}',
    'pulse.preview.moreOptions': '更多选项',
    'pulse.preview.manageSettings': '管理设置',
    'pulse.preview.improveUpdate': '优化明日推送',
    'pulse.preview.newFeature': '新功能',
    'pulse.preview.featureTitle': '动态现已汇总你的所有定时更新。',
    'pulse.preview.featureBody': '你可以在动态或任何对话中创建及编辑更新，只需说明更新的内容和时间。你之前设置的规则也会逐步收进这里。',
    'pulse.preview.settingsTitle': '我不会在此处显示任何与你的邮件和日历有关的内容。',
    'pulse.preview.settingsBody': '等设置面板接通后，这里会负责通知时间、来源授权和隐私偏好。当前仍保留 {count} 条底层规则可在"规则"里查看。',

    // Pulse card kinds
    'pulse.kind.followup': '跟进',
    'pulse.kind.research': '研究',
    'pulse.kind.monitor': '监控',
    'pulse.kind.suggestion': '建议',

    // Pulse footer copy
    'pulse.footer.0': '今天先到这里。多聊一点，简报会越来越懂你。',
    'pulse.footer.1': '今天的重点就到这。继续对话，明天的简报会更贴近你。',
    'pulse.footer.2': '先收工吧。你分享得越多，简报越知道该替你关注什么。',
    'pulse.footer.3': '今天没有更多内容了。多给我一些上下文，之后的简报会更有感觉。',
    'pulse.footer.4': '今天先看到这里。继续聊聊你的计划和偏好，简报会越来越准。',

    // Pulse chat context
    'pulse.chatContext': '请围绕这个话题继续聊。帮我进一步了解、回答疑问，或者建议下一步怎么做。',

    // Pulse quick prompts
    'pulse.quickPrompt.0': '每周一，给我看看最值得继续跟进的主题',
    'pulse.quickPrompt.1': '提醒我关注下周规划、试点和上线阻塞',
    'pulse.quickPrompt.2': '每天早上推送 AI 产品和模型动态',
    'pulse.quickPrompt.3': '持续追踪记忆、多模态和 agent 评测',

    // Pulse preference blocks
    'pulse.preference.thinking.title': '最近在想什么？我会记住它以便明天的更新。',
    'pulse.preference.thinking.prompt.0': '我很好奇...',
    'pulse.preference.thinking.prompt.1': '请随时向我通报...',
    'pulse.preference.thinking.prompt.2': '我想继续跟进...',
    'pulse.preference.thinking.prompt.3': '随心分享...',
    'pulse.preference.domain.title': '除了 AI 技术本身，你还想我关注哪些相关领域？',
    'pulse.preference.domain.prompt.0': '半导体与算力',
    'pulse.preference.domain.prompt.1': '教育与学习技术',
    'pulse.preference.domain.prompt.2': '企业级应用',
    'pulse.preference.domain.prompt.3': '文化与创意行业',

    // Skills - article markdown fallbacks
    'skills.article.viewVideo': '查看视频',
    'skills.article.viewOriginal': '查看原帖',

    // Plan dropdown
    'plan.acceptPlan': '接受方案',
    'plan.accept': '接受',
    'plan.executeImmediately': '立即执行方案',
    'plan.acceptCompact': '接受并压缩',
    'plan.compactDescription': '适合较长、较复杂的方案',

    // Inline edit execution
    'inlineEdit.editing': '编辑中...',
    'inlineEdit.cancel': '取消',
    'inlineEdit.done': '完成',
    'inlineEdit.failed': '失败',
    'inlineEdit.dismiss': '忽略',
    'inlineEdit.retry': '重试',

    // Diff viewer controls
    'diff.splitView': '切换为分栏视图',
    'diff.unifiedView': '切换为统一视图',
    'diff.enableHighlight': '启用背景高亮',
    'diff.disableHighlight': '关闭背景高亮',

    // Item navigator
    'nav.previousItem': '上一项',
    'nav.selectItem': '选择项目',
    'nav.nextItem': '下一项',

    // Overlay titles
    'overlay.terminal': '终端',
    'overlay.json': 'JSON',
    'overlay.copyCode': '复制代码',
    'overlay.mermaidDiagram': 'Mermaid 图表',

    // Zoom controls
    'zoom.presets': '缩放预设',
    'zoom.out': '缩小 (\u2318\u2212)',
    'zoom.in': '放大 (\u2318+)',
    'zoom.reset': '重置缩放 (\u23180)',

    // Editor bubble menus
    'editor.bold': '粗体',
    'editor.italic': '斜体',
    'editor.strikethrough': '删除线',
    'editor.code': '代码',
    'editor.math': '公式',
    'editor.done': '完成',

    // Attachments
    'attachment.clickToOpen': '点击打开 {name}',
    'attachment.queued': '排队中',

    // Task actions
    'taskAction.clickForActions': '点击查看任务操作',

    // Chat display
    'chatDisplay.openInNewWindow': '在新窗口中打开',

    // Onboarding
    'onboarding.sessionExpired': '会话已过期',

    // Preferences (standalone page)
    'preferences.title': '偏好设置',
    'preferences.namePlaceholder': '你的名字',
    'preferences.additionalInfoPlaceholder': '你想分享给 AI 助手的其他信息...',

    // Files
    'files.preview': '预览',

    // Session status
    'sessionStatus.changeTodoState': '切换任务状态',
  },
}
